﻿import React, { useEffect, useState, useRef, useMemo, useLayoutEffect } from "react";
import ReactDOM from "react-dom";
import { useParams, useNavigate, useLocation, useSearchParams, useOutletContext } from "react-router-dom";
import { supabase } from "../supabaseClient.js";
import DashboardSidebar from "../components/Dashboard/DashboardSidebar.jsx";
// DashboardNavbar removed
import * as Icons from "lucide-react";
import { motion } from "framer-motion";
import ImageEditorModal from "../components/ImageEditorModal";
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { MEMORY_CACHE, IMAGE_RAM_CACHE, BLOB_CACHE, InputCache } from '../services/GlobalCache.js';
import CacheService from '../services/CacheService.js';

const BLOCKED_EXTENSIONS = ['exe', 'bat', 'cmd', 'sh', 'msi', 'bin', 'php', 'pl', 'py', 'js', 'jsx', 'tsx', 'rb', 'html', 'htm', 'css', 'zip', 'rar', '7z', 'tar', 'gz'];


// Helper for dynamic icons
// Helper for dynamic icons with case-insensitive handling
const DynamicIcon = ({ name, className, size = 24 }) => {
    if (!name) return null;
    const cleanName = name.trim();
    // 1. Try exact match
    let IconComponent = Icons[cleanName];
    // 2. Try PascalCase conversion if direct match fails (e.g. "crown" -> "Crown")
    if (!IconComponent) {
        const pascalName = cleanName.replace(/(^|-)(\w)/g, (match, p1, p2) => p2.toUpperCase());
        IconComponent = Icons[pascalName];
    }
    // 3. Fallback
    if (!IconComponent) IconComponent = Icons.Box;

    return <IconComponent className={className} size={size} />;
};

// --- Helper to extract clean filename ---
// --- Helper to extract clean filename ---
const getCleanFilename = (url) => {
    if (!url) return "Document";
    // Decode URL first to handle %20 etc
    try { url = decodeURIComponent(url); } catch (e) { }

    let filenamePart = url.split("/").pop() || "";

    // Check for NEW format: Name_TIMESTAMP.ext (e.g., headline_173434.. .txt)
    // Regex: look for _(\d{13}) before extension
    const match = filenamePart.match(/^(.*)_(\d{13})\.([a-zA-Z0-9]+)$/);

    if (match) {
        const originalName = match[1];
        const timestamp = parseInt(match[2]);
        const ext = match[3];

        // Format Date: DD-MM-YYYY_HHmm
        const date = new Date(timestamp);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        const hour = String(date.getHours()).padStart(2, '0');
        const minute = String(date.getMinutes()).padStart(2, '0');

        return `${originalName}_${day}-${month}-${year}_${hour}${minute}.${ext}`;
    }

    // Fallback/Legacy cleanup
    if (filenamePart.includes(';')) {
        filenamePart = filenamePart.split(';')[0];
    }
    return filenamePart;
};

// --- NEW Helper: Lazy Video Thumbnail (Performance Optimization) ---
// Only loads video metadata when the item scrolls into view
const LazyVideoThumbnail = ({ url }) => {
    const containerRef = useRef(null);
    const [shouldLoad, setShouldLoad] = useState(false);
    const [videoLoaded, setVideoLoaded] = useState(false);

    useEffect(() => {
        const observer = new IntersectionObserver(([entry]) => {
            if (entry.isIntersecting) {
                setShouldLoad(true);
                observer.disconnect();
            }
        }, { rootMargin: '100px' }); // Starts loading 100px before appearing

        if (containerRef.current) observer.observe(containerRef.current);
        return () => observer.disconnect();
    }, []);

    // Reset videoLoaded when URL changes
    useEffect(() => {
        setVideoLoaded(false);
    }, [url]);

    return (
        <div ref={containerRef} className="w-full h-full bg-black flex items-center justify-center overflow-hidden relative">
            {/* Skeleton / Loading State */}
            {/* Skeleton / Loading State */}
            {(!shouldLoad || !videoLoaded) && (
                <div className="absolute inset-0 bg-slate-200 animate-pulse z-10" />
            )}
            {shouldLoad && (
                <video
                    src={`${url}#t=0.1`}
                    preload="metadata"
                    className={`w-full h-full object-cover ${!videoLoaded ? 'opacity-0' : 'opacity-100'} transition-opacity duration-300`}
                    muted
                    playsInline
                    disablePictureInPicture
                    onLoadedMetadata={() => setVideoLoaded(true)}
                    onError={() => setVideoLoaded(true)} // Remove skeleton on error too
                />
            )}
            {/* Play icon overlay (only show when loaded) */}
            {videoLoaded && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                    <div className="p-1.5 bg-black/50 rounded-full text-white backdrop-blur-sm">
                        <Icons.Play size={10} fill="currentColor" />
                    </div>
                </div>
            )}
        </div>
    );
};

// --- Unified Preview Component (Outside to prevent re-renders) ---
const UnifiedPreview = ({ result, mimeType, isHistory }) => {
    const [textContent, setTextContent] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [unwrappedMedia, setUnwrappedMedia] = useState(null); // { url, type }

    // State for tracking image load (for history skeleton) - MUST be at top level (Rules of Hooks)
    const [imageLoaded, setImageLoaded] = useState(false);

    // Ref to check if image is already complete (cached) - MUST be at top level
    const imgRef = useRef(null);

    useEffect(() => {
        // Helper to unwrap N8N JSON output
        const checkAndUnwrap = async (url) => {
            try {
                const r = await fetch(url);
                const text = await r.text();
                setTextContent(text); // Store raw text anyway

                // Try JSON
                try {
                    const json = JSON.parse(text);
                    const mediaUrl = json.output || json.url || json.video || json.image || json.audio || (Array.isArray(json) && json[0]);
                    if (mediaUrl && typeof mediaUrl === 'string' && mediaUrl.startsWith('http')) {
                        const ext = mediaUrl.split(/[#?]/)[0].split('.').pop().trim().toLowerCase();
                        let type = null;
                        if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) type = 'image';
                        else if (['mp4', 'webm', 'mov', 'mkv', 'avi', 'wmv', 'ts', 'm4v'].includes(ext)) type = 'video';
                        else if (['mp3', 'wav', 'ogg', 'm4a', 'flac', 'aac'].includes(ext)) type = 'audio';

                        if (type) {
                            setUnwrappedMedia({ url: mediaUrl, type });
                            return;
                        }
                    }
                } catch (e) { /* not json */ }
            } catch (e) { console.error("Unwrap failed", e); }
            setIsLoading(false);
        };

        const isJson = mimeType === "application/json" || result.public_url?.endsWith(".json");
        const isText = mimeType === "text/plain" || result.public_url?.endsWith(".txt");

        if ((isJson || isText) && result.public_url) {
            checkAndUnwrap(result.public_url);
        } else {
            setIsLoading(false);
        }
    }, [result.public_url, mimeType]);

    // USE UNWRAPPED MEDIA IF AVAILABLE
    const effectiveUrl = unwrappedMedia ? unwrappedMedia.url : result.public_url;
    const effectiveType = unwrappedMedia ? unwrappedMedia.type : null;

    const isVideo = effectiveType === 'video' || mimeType?.startsWith('video');
    const isImage = effectiveType === 'image' || mimeType?.startsWith('image');
    const isAudio = effectiveType === 'audio' || mimeType?.startsWith('audio');
    const isPdf = mimeType === 'application/pdf' || effectiveUrl?.endsWith('.pdf');

    // Reset imageLoaded when URL changes (for images only)
    useEffect(() => {
        if (isImage) {
            setImageLoaded(false);
        }
    }, [effectiveUrl, isImage]);

    // Check if image is already complete on mount (handles cached images)
    useLayoutEffect(() => {
        if (isImage && imgRef.current && imgRef.current.complete && imgRef.current.naturalWidth > 0) {
            setImageLoaded(true);
        }
    }, [isImage, effectiveUrl]);

    // 1. VIDEO
    if (isVideo) {
        if (isHistory) {
            // Use Lazy Loader to prevent network flood
            return <LazyVideoThumbnail url={effectiveUrl} />;
        }
        // Modal / Main Result: Full Player (Contained)
        // Removed bg-black to allow parent background (background.webp) to show
        return (
            <div className="w-full h-full flex items-center justify-center overflow-hidden">
                <video
                    controls
                    autoPlay
                    src={effectiveUrl}
                    className="w-full h-full object-contain"
                />
            </div>
        );
    }

    // 2. IMAGE
    if (isImage) {
        return (
            <div className="w-full h-full flex items-center justify-center overflow-hidden relative">
                {/* Skeleton / Loading State for History */}
                {/* Skeleton / Loading State for History - Modified to Pulse Only (No Spinner) */}
                {isHistory && !imageLoaded && (
                    <div className="absolute inset-0 bg-slate-200 animate-pulse z-10" />
                )}
                <img
                    ref={imgRef}
                    src={effectiveUrl}
                    alt="Result"
                    className={`w-full h-full ${isHistory ? 'object-cover' : 'object-contain'} ${isHistory && !imageLoaded ? 'opacity-0' : 'opacity-100'} transition-opacity duration-300`}
                    onLoad={() => setImageLoaded(true)}
                    onError={() => setImageLoaded(true)} // Also mark as loaded on error to remove skeleton
                />
            </div>
        );
    }

    // 3. AUDIO
    if (isAudio) {
        if (isHistory) {
            return (
                <div className="w-full h-full bg-gradient-to-br from-indigo-50 to-purple-50 flex flex-col items-center justify-center p-2 text-indigo-400">
                    <Icons.Music size={24} className="mb-1" />
                    <span className="text-[9px] font-bold uppercase tracking-wider">Audio</span>
                </div>
            );
        }
        return (
            <div className="w-full h-full bg-black/90 flex items-center justify-center p-8">
                <audio controls autoPlay src={effectiveUrl} className="w-full max-w-2xl bg-white rounded-full" />
            </div>
        );
    }

    // 4. DOCUMENTS
    const getViewer = () => {
        if (isPdf) {
            return (
                <iframe
                    src={effectiveUrl}
                    className="w-full h-full"
                    title="PDF Preview"
                    onLoad={() => setIsLoading(false)}
                />
            );
        }
        if (mimeType === 'text/plain' || effectiveType === 'json' || mimeType === 'application/json') {
            return (
                <div className="w-full h-full bg-white p-8 overflow-y-auto whitespace-pre-wrap font-mono text-xs text-slate-700 mx-auto">
                    <div className="max-w-4xl mx-auto">
                        {textContent || "Loading..."}
                    </div>
                </div>
            );
        }
        return (
            <iframe
                src={`https://docs.google.com/gview?url=${encodeURIComponent(effectiveUrl)}&embedded=true`}
                className="w-full h-full"
                title="Doc Preview"
                onLoad={() => setIsLoading(false)}
            />
        );
    };

    if (!isHistory) {
        return (
            <div className="w-full h-full bg-slate-200 flex items-center justify-center">
                {getViewer()}
            </div>
        );
    }

    const cleanFilename = getCleanFilename(result.public_url);
    return (
        <div className="w-full h-full bg-slate-50 flex flex-col items-center justify-center text-center p-2">
            <Icons.FileText size={20} className="text-slate-400 mb-1" />
            <span className="text-[10px] text-slate-500 font-medium truncate w-full px-2">
                {cleanFilename}
            </span>
        </div>
    );
};

// --- Chat UI Components ---
// --- Chat UI Components ---
// --- Strip Markdown Helper ---
const stripMarkdown = (text) => {
    if (!text) return "";
    try {
        return text
            .replace(/!\[.*?\]\(.*?\)/g, "") // Remove images
            .replace(/\[([^\]]+)\]\([^\)]+\)/g, "$1") // Links -> text
            .replace(/(\*\*|__)(.*?)\1/g, "$2") // Bold
            .replace(/(\*|_)(.*?)\1/g, "$2") // Italic
            .replace(/`{3}[\s\S]*?`{3}/g, (match) => match.replace(/`{3}/g, "")) // Code blocks (keep content, remove backticks)
            .replace(/`([^`]+)`/g, "$1") // Inline code
            .replace(/^#+\s+/gm, "") // Headers
            .replace(/^>\s+/gm, "") // Blockquotes
            .replace(/^[-*+]\s+/gm, "") // Lists
            .replace(/\|/g, " ") // Tables (pipes to spaces)
            .replace(/-{3,}/g, "") // Horizontal rules / table separators
            .replace(/\n{2,}/g, "\n\n") // Normalize newlines
            .trim();
    } catch (e) {
        return text;
    }
};

const ChatMessage = React.forwardRef(({
    role,
    content,
    modelName,
    credits,
    timestamp,
    inputs, // For User inputs (media)
    status, // Added status prop
    onPreviewMedia,
    onDownloadMedia
}, ref) => {
    const isUser = role === "user" || role === "human";
    const isFailed = status === "failed";
    const [isCopied, setIsCopied] = useState(false);

    const handleCopy = () => {
        if (!content) return;
        // Use the displayed text content (stripping internal raw logic if needed, but textContent var is safer)
        let textToCopy = textContent || (typeof content === 'string' ? content : JSON.stringify(content));

        // Strip markdown before copying
        textToCopy = stripMarkdown(textToCopy);

        navigator.clipboard.writeText(textToCopy);
        setIsCopied(true);
        setTimeout(() => setIsCopied(false), 2000);
    };

    // 1. Parse Logic (v8 - Robust Multi-Content Support)
    let displayContent = content; // Fallback text
    let mediaItems = []; // Array of { url, mime }
    let textContent = "";

    try {
        const rawStr = typeof content === 'string' ? content.trim() : "";
        let parsed = null;

        // A. Try JSON Parse first
        if ((rawStr.startsWith('{') && rawStr.endsWith('}')) || (rawStr.startsWith('[') && rawStr.endsWith(']'))) {
            try {
                parsed = JSON.parse(rawStr);
                // Double encoding check
                if (typeof parsed === 'string') parsed = JSON.parse(parsed);
            } catch (e) { /* ignore */ }
        } else if (typeof content === 'object' && content !== null) {
            parsed = content;
        }

        // Helper to extract media from object
        const extractMedia = (obj) => {
            const url = obj.public_url || obj.url || (typeof obj.output === 'string' && obj.output.startsWith('http') ? obj.output : null);
            const mime = obj.mime_type || obj.mimeType || "";
            if (url && (mime || url.includes('/assets/') || url.includes('/storage/') || /\.(jpg|jpeg|png|gif|webp|mp4|mov|webm|mkv|avi|wmv|ts|m4v|mp3|wav|pdf)$/i.test(url))) {
                return { url, mime, ...obj };
            }
            return null;
        };

        if (Array.isArray(parsed)) {
            // 1. Array Result: iterate and split
            parsed.forEach(item => {
                const media = extractMedia(item);
                if (media) mediaItems.push(media);
                else if (item.text || item.output || item.message) textContent += (item.text || item.output || item.message) + "\n";
            });
        } else if (parsed && typeof parsed === 'object') {
            // 2. Object Result
            const media = extractMedia(parsed);
            if (media) mediaItems.push(media);

            // Also check for text
            const possibleText = parsed.text || parsed.message || (parsed.output && !parsed.output.startsWith('http') ? parsed.output : "");
            if (possibleText) textContent = possibleText;

        } else {
            // 3. Raw String: Regex Scan for URLs
            // This handles "Here is your image: http://... and video: http://..." cases
            const urlRegex = /https?:\/\/[^\s"'{}()]+(?:\.(?:jpg|jpeg|png|gif|webp|mp4|mov|webm|mkv|avi|wmv|ts|m4v|mp3|wav|pdf))?/gi;
            let match;
            let lastIndex = 0;
            let foundMedia = false;

            // We need to be careful not to strip text.
            // Strategy: Extract URLs that look like files.
            const matches = [...rawStr.matchAll(urlRegex)];

            if (matches.length > 0) {
                matches.forEach(m => {
                    const url = m[0];
                    // Strict check for extension or known path to avoid grabbing random links as media
                    if (url.includes('/assets/') || url.includes('/storage/') || /\.(jpg|jpeg|png|gif|webp|mp4|mov|webm|mkv|avi|wmv|ts|m4v|mp3|wav|pdf)$/i.test(url)) {
                        mediaItems.push({ url, mime: "" });
                        foundMedia = true;
                    }
                });

                // If we found media, we treat the rest as text? 
                // Or just keep the whole string as text for context? 
                // Usually better to keep the whole string as textContext so user sees "Here is:"
                textContent = rawStr;
            } else {
                textContent = rawStr || "[No content]";
            }
        }

    } catch (e) {
        textContent = String(content || "[No content]");
    }

    // 1b. Parse INPUTS for User Media (if User Role)
    if (isUser && inputs) {
        // Handle if inputs is string
        let safeInputs = inputs;
        if (typeof inputs === 'string') {
            try { safeInputs = JSON.parse(inputs); } catch (e) { }
        }

        if (typeof safeInputs === 'object' && safeInputs !== null) {
            const processValue = (val) => {
                if (!val) return;

                // Handle String URL
                if (typeof val === 'string') {
                    // Specific check for Supabase Storage URLs or Media Files
                    // User Request: "hanya link yang seperti ini... bikinkan divnya sendiri di atasnya prompt"
                    // UPDATED: Now supports both 'assets' and 'Generate' buckets
                    const isSupabaseStorage = val.includes('/storage/v1/object/public/assets/') || val.includes('/storage/v1/object/public/Generate/');

                    // Also keep robust check for direct media links
                    const cleanUrl = val.split(/[?#]/)[0];
                    const ext = cleanUrl.split('.').pop().toLowerCase();
                    const isMediaExt = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'mp4', 'mov', 'webm', 'mkv', 'avi', 'wmv', 'ts', 'm4v', 'mp3', 'wav', 'pdf', 'doc', 'docx', 'txt'].includes(ext);

                    if ((val.startsWith('http') || val.startsWith('data:')) && (isSupabaseStorage || isMediaExt)) {
                        if (!mediaItems.some(m => m.url === val)) {
                            mediaItems.push({ url: val, mime: '' });
                        }
                    }
                }
                // Handle Object (file metadata)
                else if (typeof val === 'object') {
                    if (val.public_url) {
                        if (!mediaItems.some(m => m.url === val.public_url)) {
                            mediaItems.push({ url: val.public_url, mime: val.mime_type || '' });
                        }
                    }
                }
            };

            Object.entries(safeInputs).forEach(([k, v]) => {
                // Skip non-media keys
                if (['prompt', 'message', 'width', 'height', 'num_inference_steps', 'guidance_scale', 'seed'].includes(k)) return;
                // Also skip internal keys starting with _
                if (k.startsWith('_')) return;

                if (Array.isArray(v)) v.forEach(processValue);
                else processValue(v);
            });
        }
    }

    // Fallback: If no media found and no text extracted but we have displayContent, assume it's text
    if (mediaItems.length === 0 && !textContent && displayContent) {
        textContent = String(displayContent);
    }

    // Auto-detect mime if missing (Crucial for UnifiedPreview)
    mediaItems = mediaItems.map(item => {
        if (!item.mime && typeof item.url === 'string') {
            const ext = item.url.split(/[?#]/)[0].split('.').pop().toLowerCase();
            if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) item.mime = 'image/jpeg';
            else if (['mp4', 'webm', 'mov', 'mkv', 'avi', 'wmv', 'ts', 'm4v'].includes(ext)) item.mime = 'video/mp4';
            else if (ext === 'pdf') item.mime = 'application/pdf';
        }
        return item;
    });

    // Safeguard
    const isMedia = mediaItems.length > 0;

    // Safeguard: Ensure timestamp is valid
    const validTimestamp = timestamp && !isNaN(timestamp) ? timestamp : Date.now();

    return (
        <div ref={ref} className={`flex flex-col w-full ${isUser ? 'items-end' : 'items-start'} mb-4 animate-in fade-in slide-in-from-bottom-2 duration-300 scroll-mt-24`}>

            {/* A. USER MEDIA (SEPARATE BUBBLES START) */}
            {isUser && mediaItems.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-1.5 justify-end max-w-[85%]">
                    {mediaItems.map((item, idx) => {
                        const isImageOrVideo = item.mime?.startsWith('image/') || item.mime?.startsWith('video/') || ['jpg', 'jpeg', 'png', 'gif', 'webp', 'mp4', 'mov', 'webm', 'mkv', 'avi', 'wmv', 'ts', 'm4v'].some(ext => item.url.toLowerCase().endsWith('.' + ext));

                        // Determine styling for Documents
                        let DocIcon = Icons.FileText;
                        let itemColor = "bg-slate-50 text-slate-500";
                        let label = item.mime ? item.mime.split('/')[1] : item.url.split('.').pop();

                        const ext = item.url.split('.').pop().toLowerCase();
                        if (['pdf'].includes(ext) || item.mime?.includes('pdf')) {
                            DocIcon = Icons.FileText;
                            itemColor = "bg-rose-50 text-rose-600";
                            label = "PDF";
                        } else if (['xls', 'xlsx', 'csv'].includes(ext) || item.mime?.includes('spreadsheet') || item.mime?.includes('excel')) {
                            DocIcon = Icons.FileSpreadsheet;
                            itemColor = "bg-emerald-50 text-emerald-600";
                            label = "EXCEL";
                        } else if (['doc', 'docx'].includes(ext) || item.mime?.includes('word') || item.mime?.includes('document')) {
                            DocIcon = Icons.FileType2;
                            itemColor = "bg-blue-50 text-blue-600";
                            label = "WORD";
                        } else if (['mp3', 'wav', 'ogg', 'm4a'].includes(ext) || item.mime?.startsWith('audio/')) {
                            DocIcon = Icons.Mic;
                            itemColor = "bg-violet-50 text-violet-600";
                            label = "AUDIO";
                        } else if (['txt', 'md', 'json'].includes(ext)) {
                            DocIcon = Icons.FileCode;
                            itemColor = "bg-slate-100 text-slate-600";
                            label = ext.toUpperCase();
                        }

                        return (
                            <div
                                key={idx}
                                className="relative w-24 h-24 bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden group hover:scale-[1.02] transition-transform cursor-pointer flex items-center justify-center p-2"
                                style={{
                                    backgroundImage: "url('/assets/dashboard/backgroundchatbox.webp')",
                                    backgroundSize: 'cover',
                                    backgroundPosition: 'center'
                                }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    const ext = item.url.split('.').pop().toLowerCase();
                                    const isOfficeDoc = ['xls', 'xlsx', 'csv', 'doc', 'docx', 'ppt', 'pptx'].includes(ext) || item.mime?.includes('spreadsheet') || item.mime?.includes('excel') || item.mime?.includes('word') || item.mime?.includes('presentation');

                                    if (isOfficeDoc) {
                                        onDownloadMedia ? onDownloadMedia(item.url) : window.open(item.url, '_blank');
                                    } else {
                                        onPreviewMedia && onPreviewMedia(item.url, item.mime);
                                    }
                                }}
                            >
                                {isImageOrVideo ? (
                                    <>
                                        <UnifiedPreview
                                            result={{ public_url: item.url, ...item }}
                                            mimeType={item.mime || 'image/jpeg'}
                                            isHistory={true}
                                        />
                                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
                                    </>
                                ) : (
                                    // Custom View for Documents (Dynamic Colors)
                                    <div className="flex flex-col items-center justify-center h-full w-full text-center">
                                        <div className={`w-10 h-10 rounded-full flex items-center justify-center mb-1 ${itemColor}`}>
                                            <DocIcon size={20} />
                                        </div>
                                        <span className={`text-[10px] font-bold uppercase truncate max-w-full px-1 ${itemColor.split(' ')[1]}`}>
                                            {label}
                                        </span>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )
            }

            {/* B. AI MEDIA (SEPARATE BUBBLE) */}
            {!isUser && mediaItems.length > 0 && (
                <div className={`mb-2 ${mediaItems.length === 1 ? 'w-full max-w-[12rem]' : 'w-full max-w-[18rem]'}`}>
                    <div className={`grid gap-1.5 ${mediaItems.length === 1 ? 'grid-cols-1' : mediaItems.length >= 4 ? 'grid-cols-3' : 'grid-cols-2'}`}>
                        {mediaItems.map((item, idx) => (
                            <div key={idx} className={`relative overflow-hidden group bg-white rounded-xl shadow-sm border border-slate-200 ${mediaItems.length === 1 ? '' : 'aspect-square'}`}>
                                <div
                                    className={`w-full h-full cursor-zoom-in ${mediaItems.length === 1 && !item.url.endsWith('.pdf') ? 'min-h-[10rem]' : ''}`}
                                    onClick={() => onPreviewMedia && onPreviewMedia(item.url, item.mime)}
                                >
                                    <UnifiedPreview
                                        result={{ public_url: item.url, ...item }}
                                        mimeType={item.mime}
                                        isHistory={!isUser}
                                    />
                                </div>
                                <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-all z-10">
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onDownloadMedia && onDownloadMedia(item.url);
                                        }}
                                        className="p-1.5 bg-black/50 hover:bg-black/70 text-white rounded-lg backdrop-blur-sm shadow-sm"
                                        title="Download"
                                    >
                                        <Icons.Download size={14} />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* MAIN TEXT BUBBLE */}
            {(textContent || isFailed) && (
                <div
                    className={`max-w-[85%] md:max-w-[75%] rounded-2xl ${isUser
                        ? 'bg-sky-600 text-white rounded-br-sm px-4 py-3 shadow-sm'
                        : isFailed
                            ? 'bg-rose-50 border border-rose-200 text-rose-600 rounded-bl-sm shadow-sm'
                            : 'border border-gray-200/50 text-slate-900 rounded-bl-sm shadow-sm'
                        }`}
                    style={(!isUser && !isFailed) ? {
                        backgroundImage: "url('/assets/dashboard/backgroundchatbox.webp')",
                        backgroundSize: 'cover',
                        backgroundPosition: 'center'
                    } : {}}
                >

                    {/* C. TEXT CONTENT */}
                    <div className={!isUser ? "relative group px-4 py-2.5 break-words" : ""} >
                        {/* Sticky Copy Button (AI ONLY) - Compact */}
                        {!isUser && !isFailed && (
                            <div className="sticky top-1 right-0 flex justify-end z-20 mb-[-1.25rem] pointer-events-none">
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleCopy();
                                    }}
                                    className="pointer-events-auto flex items-center gap-1 px-2 py-1 bg-white/90 backdrop-blur-md text-slate-500 hover:text-slate-800 rounded-full shadow-sm border border-slate-200/80 hover:bg-white transition-all transform hover:scale-105"
                                    title="Salin Teks"
                                >
                                    {isCopied ? <Icons.Check size={12} className="text-emerald-600" /> : <Icons.Copy size={12} />}
                                    <span className="text-[9px] font-semibold uppercase tracking-wide">{isCopied ? "Tersalin" : "Salin"}</span>
                                </button>
                            </div>
                        )}

                        {isFailed ? (
                            <div className="flex items-center gap-2 -my-1">
                                <Icons.AlertCircle size={16} className="shrink-0 text-rose-600" />
                                <p className="text-sm font-semibold italic text-rose-600 leading-snug">
                                    "Gagal memproses permintaan. Silakan hubungi admin."
                                </p>
                            </div>
                        ) : isUser ? (
                            /* User Content (Simple Plain Text) */
                            <p className="text-[12px] leading-relaxed whitespace-pre-wrap break-words">
                                {textContent}
                            </p>
                        ) : (
                            /* AI Content (Markdown) */
                            <div className="text-[12px] leading-relaxed text-slate-800 markdown-body pt-6">
                                <ReactMarkdown
                                    remarkPlugins={[remarkGfm]}
                                    components={{
                                        // Text & layout - Filter empty paragraphs to prevent #text bugs
                                        p: ({ node, children, ...props }) => {
                                            // Check if paragraph is empty or only contains whitespace
                                            const hasContent = React.Children.toArray(children).some(child => {
                                                if (typeof child === 'string') return child.trim().length > 0;
                                                return true; // Keep non-string children (like <strong>, <em>, etc)
                                            });
                                            if (!hasContent) return null; // Don't render empty paragraphs
                                            return <p className="mb-2 last:mb-0 whitespace-pre-wrap break-words" {...props}>{children}</p>;
                                        },
                                        strong: ({ node, ...props }) => <strong className="font-semibold text-slate-900" {...props} />,
                                        em: ({ node, ...props }) => <em className="italic" {...props} />,

                                        // Headings
                                        h1: ({ node, ...props }) => <h1 className="text-xl font-bold text-slate-900 mt-4 mb-2 pb-1 border-b border-slate-200" {...props} />,
                                        h2: ({ node, ...props }) => <h2 className="text-lg font-bold text-slate-900 mt-3 mb-2" {...props} />,
                                        h3: ({ node, ...props }) => <h3 className="text-base font-semibold text-slate-900 mt-2 mb-1" {...props} />,

                                        // Lists
                                        ul: ({ node, ...props }) => <ul className="list-disc pl-4 mb-2 space-y-0.5" {...props} />,
                                        ol: ({ node, ...props }) => <ol className="list-decimal pl-4 mb-2 space-y-0.5" {...props} />,
                                        li: ({ node, ...props }) => <li className="pl-1" {...props} />,

                                        // Links & Blockquotes
                                        a: ({ node, ...props }) => <a className="text-blue-600 hover:underline font-medium" target="_blank" rel="noopener noreferrer" {...props} />,
                                        blockquote: ({ node, ...props }) => <blockquote className="border-l-4 border-slate-300 pl-3 italic text-slate-600 my-2" {...props} />,

                                        // Code
                                        code: ({ node, inline, className, children, ...props }) => {
                                            const match = /language-(\w+)/.exec(className || '');
                                            return inline ? (
                                                <code className="bg-slate-100 text-pink-600 px-1 py-0.5 rounded text-[0.85em] font-mono border border-slate-200" {...props}>
                                                    {children}
                                                </code>
                                            ) : (
                                                <div className="relative my-3 rounded-lg overflow-hidden bg-slate-900 border border-slate-800">
                                                    <div className="flex items-center justify-between px-3 py-1.5 bg-slate-800 border-b border-slate-700">
                                                        <span className="text-[10px] uppercase font-mono text-slate-400">{match ? match[1] : 'text'}</span>
                                                    </div>
                                                    <div className="overflow-x-auto p-3">
                                                        <code className="block text-xs font-mono text-slate-200 whitespace-pre" {...props}>
                                                            {children}
                                                        </code>
                                                    </div>
                                                </div>
                                            );
                                        },

                                        // Tables
                                        table: ({ node, ...props }) => <div className="overflow-x-auto my-3 rounded-lg border border-slate-200"><table className="w-full text-left text-xs border-collapse" {...props} /></div>,
                                        thead: ({ node, ...props }) => <thead className="bg-slate-50 border-b border-slate-200 text-slate-600 uppercase font-semibold" {...props} />,
                                        tbody: ({ node, ...props }) => <tbody className="divide-y divide-slate-100" {...props} />,
                                        tr: ({ node, ...props }) => <tr className="hover:bg-slate-50/50 transition-colors" {...props} />,
                                        th: ({ node, ...props }) => <th className="px-3 py-2 whitespace-nowrap" {...props} />,
                                        td: ({ node, ...props }) => <td className="px-3 py-2 align-top" {...props} />,

                                        // Misc
                                        hr: ({ node, ...props }) => <hr className="my-4 border-slate-200" {...props} />,
                                    }}
                                >
                                    {textContent}
                                </ReactMarkdown>
                            </div>
                        )}
                    </div>

                    {
                        (!isUser && credits > 0) && (
                            <div className={`flex items-center gap-2 mt-1 ${isMedia ? 'px-3 pb-1.5' : 'px-4 pb-1'}`}>
                                <span className="text-[9px] bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded-full font-bold">
                                    -{credits} Kredit
                                </span>
                            </div>
                        )
                    }
                </div >
            )
            }
        </div >
    );
});

const TypingIndicator = () => (
    <div className="flex justify-start mb-3">
        <div className="bg-white border border-slate-200 rounded-2xl rounded-bl-sm px-4 py-3 shadow-sm">
            <div className="flex gap-1">
                <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" />
                <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }} />
                <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0.4s' }} />
            </div>
        </div>
    </div>
);

const ChatInterface = ({ messages, isSubmitting, onClearChat, chatEndRef, onPreviewMedia, onDownloadMedia }) => {
    const lastUserRef = useRef(null);

    // Find the index of the LAST user message to anchor scroll
    const lastUserIndex = messages ? messages.reduce((lastIdx, msg, idx) => {
        if (msg.role === 'user' || msg.role === 'human') return idx;
        return lastIdx;
    }, -1) : -1;

    // Track if we have performed the initial scroll to the last user message
    const hasInitialScrolled = useRef(false);

    // Reset scroll flag when messages are cleared or changed completely (new session)
    useEffect(() => {
        if (!messages || messages.length === 0) {
            hasInitialScrolled.current = false;
        }
    }, [messages]);

    // Scroll to the last user message ONLY on initial load
    useEffect(() => {
        if (lastUserRef.current && !hasInitialScrolled.current) {
            lastUserRef.current.scrollIntoView({ behavior: 'auto', block: 'start' });
            hasInitialScrolled.current = true;
        }
    }, [messages?.length, lastUserIndex]);

    if (!messages || messages.length === 0) {
        return (
            <div className="flex-1 flex items-center justify-center p-8">
                <div className="text-center">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-sky-50 flex items-center justify-center">
                        <Icons.MessageCircle className="w-8 h-8 text-sky-500" />
                    </div>
                    <h3 className="text-lg font-bold text-slate-800 mb-2">Mulai Percakapan</h3>
                    <p className="text-sm text-slate-500 max-w-xs mx-auto">
                        Ketik pesan di bawah untuk memulai percakapan dengan AI. Chat akan tersimpan otomatis.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-1">
            {messages.map((msg, idx) => (
                <ChatMessage
                    key={`${msg.timestamp}-${idx}`}
                    ref={idx === lastUserIndex ? lastUserRef : null}
                    role={msg.role}
                    content={msg.content}
                    inputs={msg.inputs} // Pass inputs for user media
                    modelName={msg.model_name}
                    timestamp={msg.timestamp}
                    credits={msg.credits}
                    onPreviewMedia={onPreviewMedia}
                    onDownloadMedia={onDownloadMedia}
                />
            ))}
            {isSubmitting && <TypingIndicator />}
            {/* Removed chatEndRef to disable auto-scroll to bottom */}
        </div>
    );
};

// --- Full-Screen Chat Modal (WhatsApp Web Style) ---
const ChatModal = ({
    isOpen,
    onClose,
    modelName,
    messages,
    isSubmitting,
    onNewChat,
    onSend,
    chatEndRef,
    currentInput,
    onInputChange,
    onPreviewMedia,
    onDownloadMedia
}) => {
    if (!isOpen) return null;

    // ESC to close
    React.useEffect(() => {
        const handleEsc = (e) => {
            if (e.key === 'Escape') onClose();
        };
        window.addEventListener('keydown', handleEsc);
        return () => window.removeEventListener('keydown', handleEsc);
    }, [onClose]);

    return (
        <div className="fixed inset-0 z-[9999] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in duration-200">
            <div
                className="w-full max-w-5xl h-[90vh] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden animate-in zoom-in-95 duration-200"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="bg-gradient-to-r from-sky-600 to-blue-600 text-white px-6 py-4 flex items-center justify-between shrink-0">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                            <Icons.MessageCircle className="w-6 h-6" />
                        </div>
                        <div>
                            <h2 className="font-bold text-lg">{modelName}</h2>
                            <p className="text-xs text-sky-100">Chat History</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={onNewChat}
                            className="px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg text-sm font-medium transition-colors flex items-center gap-2"
                        >
                            <Icons.Plus size={16} />
                            New Chat
                        </button>
                        <button
                            onClick={onClose}
                            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                            title="Close (ESC)"
                        >
                            <Icons.X size={24} />
                        </button>
                    </div>
                </div>

                {/* Chat Messages Area */}
                <div className="flex-1 overflow-y-auto bg-slate-50 px-6 py-4">
                    {messages.length === 0 ? (
                        <div className="h-full flex items-center justify-center">
                            <div className="text-center">
                                <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-sky-100 flex items-center justify-center">
                                    <Icons.MessageCircle className="w-10 h-10 text-sky-600" />
                                </div>
                                <h3 className="text-xl font-bold text-slate-800 mb-2">Start Conversation</h3>
                                <p className="text-sm text-slate-500 max-w-md mx-auto">
                                    Type your message below and click Generate to start chatting with AI.
                                </p>
                            </div>
                        </div>
                    ) : (
                        <>
                            {messages.map((msg, idx) => (
                                <ChatMessage
                                    key={`${msg.timestamp}-${idx}`}
                                    role={msg.role}
                                    content={msg.content}
                                    inputs={msg.inputs} // Pass inputs for user media
                                    modelName={msg.model_name}
                                    timestamp={msg.timestamp}
                                    credits={msg.credits}
                                    onPreviewMedia={onPreviewMedia}
                                    onDownloadMedia={onDownloadMedia}
                                />
                            ))}
                            {isSubmitting && <TypingIndicator />}
                            <div ref={chatEndRef} />
                        </>
                    )}
                </div>

                {/* Info Footer */}
                <div className="bg-slate-100 border-t border-slate-200 px-6 py-3 shrink-0">
                    <p className="text-xs text-slate-500 text-center">
                        💡 Use the form on the left to send messages. This window displays your conversation history.
                    </p>
                </div>
            </div>
        </div>
    );
};

// --- Multi-File Gallery Component ---
const MultiFileGallery = ({ results }) => {
    return (
        <div className="w-full h-full bg-slate-100 p-4 overflow-y-auto">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 pb-20">
                {results.map((item, idx) => {
                    const isImage = item.mime_type?.startsWith('image') || ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(item.public_url?.split('.').pop());
                    return (
                        <div
                            key={idx}
                            className="group relative aspect-square bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden hover:shadow-md transition-all cursor-pointer"
                            onClick={() => window.open(item.public_url, '_blank')}
                        >
                            {isImage ? (
                                <img src={item.public_url} className="w-full h-full object-cover" alt={`Result ${idx + 1}`} />
                            ) : (
                                <div className="w-full h-full flex flex-col items-center justify-center text-slate-400 bg-slate-50">
                                    <Icons.FileText size={32} />
                                    <span className="text-xs mt-2 px-2 text-center truncate w-full">{item.original_name || `File ${idx + 1}`}</span>
                                </div>
                            )}

                            {/* Hover Actions */}
                            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                                <button className="p-2 bg-white rounded-full text-slate-900 hover:scale-110 transition-transform" title="Download">
                                    <Icons.Download size={16} />
                                </button>
                                <button className="p-2 bg-white rounded-full text-slate-900 hover:scale-110 transition-transform" title="Open New Tab">
                                    <Icons.ExternalLink size={16} />
                                </button>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

// --- Helper to render result preview ---
const renderResultPreview = (result, isHistory = false) => {
    if (!result) return null;

    // HANDLE MULTI-FILE RESULT (ARRAY)
    if (Array.isArray(result)) {
        if (isHistory) {
            // Sidebar: Text Summary for Multiple Files
            return (
                <div className="w-full h-full bg-slate-50 p-2 overflow-hidden flex flex-col items-center justify-center text-center gap-1">
                    <div className="w-8 h-8 rounded-full bg-green-100 text-green-600 flex items-center justify-center mb-1">
                        <Icons.CheckCircle2 size={16} />
                    </div>
                    <p className="text-[10px] font-bold text-slate-700 leading-tight">
                        Generate ({result.length}) berhasil
                    </p>
                </div>
            );
        } else {
            // Main View: Gallery
            return <MultiFileGallery results={result} />;
        }
    }

    let mimeType = result.mime_type;
    if (!mimeType && !result.public_url) mimeType = 'text/plain';
    if (result.public_url && !mimeType) {
        const ext = result.public_url.split(/[#?]/)[0].split('.').pop().trim().toLowerCase();
        if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) mimeType = 'image/jpeg';
        else if (['mp4', 'webm', 'mov', 'mkv', 'avi', 'wmv', 'ts', 'm4v'].includes(ext)) mimeType = 'video/mp4';
        else if (['wav'].includes(ext)) mimeType = 'audio/wav';
        else if (['mp3', 'ogg', 'm4a', 'flac', 'aac'].includes(ext)) mimeType = 'audio/mpeg';
        else if (['pdf'].includes(ext)) mimeType = 'application/pdf';
        else mimeType = 'text/plain';
    }
    return <UnifiedPreview result={result} mimeType={mimeType} isHistory={isHistory} />;
};

// --- RAM CACHE ONLY (No Persistent Storage) ---


// CacheStorage: RAM-only implementation (no localStorage)
const CacheStorage = {
    get: (key) => {
        try {
            return MEMORY_CACHE[key] || null;
        } catch (e) {
            return null;
        }
    },
    set: (key, data) => {
        try {
            MEMORY_CACHE[key] = data;
        } catch (e) {
            console.error("Cache error", e);
        }
    },
    remove: (key) => {
        try {
            delete MEMORY_CACHE[key];
        } catch (e) { }
    }
};

// Local InputCache Removed - Imported from GlobalCache

const GenericAiPage = () => {
    // Session Cache for instant loading
    const sessionCacheRef = useRef({});
    // Race condition protection
    const latestSessionIdRef = useRef(null);
    const toastDebounceRef = useRef({ message: '', time: 0 });

    const { slug, sessionId: urlSessionId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { setPageTitle, user: ctxUser, profile: ctxProfile, setNavbarOptions } = useOutletContext();





    // --- State ---
    // CACHE-FIRST INIT
    const getCachedData = () => {
        const key = slug;
        const mem = MEMORY_CACHE[key];
        if (mem) return mem;

        const local = CacheService.get(`__TOOL_${key}__`);
        if (local) {
            MEMORY_CACHE[key] = local;
            return local;
        }
        return null;
    };

    const cachedState = useRef(getCachedData()).current;

    const [loading, setLoading] = useState(() => !cachedState);
    const [featureConfig, setFeatureConfig] = useState(() => cachedState?.featureConfig || null);
    const [user, setUser] = useState(ctxUser);
    const [profile, setProfile] = useState(ctxProfile);

    useEffect(() => {
        if (featureConfig?.name) {
            setPageTitle(featureConfig.name);
        }
    }, [setPageTitle, featureConfig?.name]);

    // Models & Fields
    const [models, setModels] = useState(() => cachedState?.models || []);
    const [selectedModelId, setSelectedModelId] = useState(() => {
        if (location.state?.selectedModelId) return location.state.selectedModelId;
        if (cachedState?.models?.length > 0) return cachedState.models[0].id;
        return "";
    });
    const [modelCapabilities, setModelCapabilities] = useState(() => cachedState?.modelCapabilities || {}); // { modelId: { hasStart: bool, hasEnd: bool } }
    const [showModelSelector, setShowModelSelector] = useState(false);
    const [showDebugInfo, setShowDebugInfo] = useState(false);

    // --- Sync State from Navigation (Sidebar) ---
    useEffect(() => {
        if (location.state?.selectedModelId && location.state.selectedModelId !== selectedModelId) {
            setSelectedModelId(location.state.selectedModelId);
        }
    }, [location.state]);

    // --- Navbar Model Sync (DISABLED) ---
    useEffect(() => {
        if (!setNavbarOptions) return;
        // User requested to remove model selector from navbar (only sidebar needed)
        setNavbarOptions(null);
        return () => setNavbarOptions(null);
    }, [setNavbarOptions]);

    // --- Model Change Broadcaster (Keeps Sidebar in sync) ---
    useEffect(() => {
        if (selectedModelId) {
            window.dispatchEvent(new CustomEvent('neo-model-change', { detail: { modelId: selectedModelId } }));
        }
    }, [selectedModelId]);



    // Toast State
    const [toast, setToast] = useState({ message: '', type: 'info', visible: false });

    const showToast = (message, type = 'info') => {
        // Prevent duplicate toasts (Debounce/De-dupe)
        const now = Date.now();
        if (toastDebounceRef.current.message === message && (now - toastDebounceRef.current.time < 1500)) {
            return;
        }
        toastDebounceRef.current = { message, time: now };

        setToast({ message, type, visible: true });
        setTimeout(() => setToast(prev => ({ ...prev, visible: false })), 3000);
    };
    const [showAttachmentMenu, setShowAttachmentMenu] = useState(false);
    const [modelFields, setModelFields] = useState(() => {
        if (cachedState?.models?.length > 0 && cachedState?.allModelFields) {
            return cachedState.allModelFields[cachedState.models[0].id] || [];
        }
        return [];
    });
    const [allModelFields, setAllModelFields] = useState(() => cachedState?.allModelFields || {}); // Cache for instant model switching

    // Config State (Structure: { categoryId: optionId })
    const [selectedConfigs, setSelectedConfigs] = useState({});
    const [totalCredits, setTotalCredits] = useState(0);

    // Inputs & Submission
    // Inputs & Submission
    const [inputs, setInputs] = useState(() => {
        if (cachedState?.models?.length > 0 && cachedState?.allModelFields) {
            const fields = cachedState.allModelFields[cachedState.models[0].id] || [];
            const initial = {};
            fields.forEach(f => {
                if (f.type === 'slider') initial[f.key] = "1";
                else if (f.type === 'file_multi') initial[f.key] = [];
                else {
                    let def = f.default_value || "";
                    if (['file', 'image', 'audio', 'video'].includes(f.type)) def = "";
                    initial[f.key] = def;
                }
            });
            return initial;
        }
        return {};
    });
    // const [searchParams, setSearchParams] = useSearchParams(); // Removed in favor of Path Params
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState("");
    const [submitSuccess, setSubmitSuccess] = useState("");
    const [showHistory, setShowHistory] = useState(false);

    // Magic Prompt State
    const [isMagicLoading, setIsMagicLoading] = useState(false);

    // Click Outside Handling
    const attachmentMenuRef = useRef(null);
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (attachmentMenuRef.current && !attachmentMenuRef.current.contains(event.target)) {
                setShowAttachmentMenu(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    // Upload State
    const [uploadingFields, setUploadingFields] = useState({}); // { [key]: boolean }

    // History / Output
    const [history, setHistory] = useState(() => {
        // 0ms Init Strategy: Try RAM -> Disk -> Empty
        if (!ctxUser?.id) return [];

        // Try to resolve key from cached state or current slug
        const code = cachedState?.featureConfig?.code || cachedState?.featureConfig?.slug || slug;
        const key = `history_${code}_${ctxUser.id}`;

        // 1. RAM Hit
        if (MEMORY_CACHE[key]) return MEMORY_CACHE[key];

        // 2. Disk Hit (LocalStorage)
        const local = CacheService.get(key);
        if (local) {
            MEMORY_CACHE[key] = local; // Hydrate RAM
            return local;
        }

        return [];
    });
    const [generatedOutput, setGeneratedOutput] = useState(null); // For immediate result display
    const [mediaModal, setMediaModal] = useState(null); // Modal state for media preview
    const [mediaModalIndex, setMediaModalIndex] = useState(-1); // Current index in history for navigation
    const [pinSelectionModal, setPinSelectionModal] = useState(null); // Modal for pin target selection
    const [processingJob, setProcessingJob] = useState(null);

    // Image Editor State
    const [imageEditorAsset, setImageEditorAsset] = useState(null);
    const [isSavingEditedImage, setIsSavingEditedImage] = useState(false);

    // --- Chat Mode State (Pin-Based) ---
    const [isChatMode, setIsChatMode] = useState(false);
    const [pinnedConversation, setPinnedConversation] = useState(null); // Pinned .txt content

    // --- Sidebar Collapse State (for responsive grid) ---
    const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
        try {
            return localStorage.getItem('neo_sidebar_collapsed') === 'true';
        } catch {
            return false;
        }
    });

    // Listen for sidebar collapse changes
    useEffect(() => {
        const handleSidebarChange = (e) => {
            setSidebarCollapsed(e.detail?.collapsed ?? false);
        };
        window.addEventListener('sidebar-collapse-change', handleSidebarChange);
        return () => window.removeEventListener('sidebar-collapse-change', handleSidebarChange);
    }, []);

    // --- Mobile Long Press State ---
    const [activeMobileMenuId, setActiveMobileMenuId] = useState(null);
    const [activeAssetMenuUrl, setActiveAssetMenuUrl] = useState(null);
    const [activeDropdown, setActiveDropdown] = useState(null); // Track opened dropdown key
    const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 0 }); // Track dropdown position
    const [customFieldModes, setCustomFieldModes] = useState({}); // Track which fields are in custom text input mode
    const longPressTimer = useRef(null);
    const dropdownTriggerRef = useRef(null); // Store reference to active dropdown trigger element

    // --- Global Scroll Handler for Dropdowns (UPDATE POSITION instead of closing) ---
    useEffect(() => {
        if (activeDropdown && dropdownTriggerRef.current) {
            const updateDropdownPosition = () => {
                const triggerEl = dropdownTriggerRef.current;
                if (!triggerEl) return;

                const rect = triggerEl.getBoundingClientRect();
                const isMobile = window.innerWidth < 768;

                // Skip update for mobile (uses bottom sheet)
                if (isMobile) return;

                // Check if trigger is still visible in viewport
                if (rect.bottom < 0 || rect.top > window.innerHeight) {
                    // Trigger scrolled out of view, close dropdown
                    setActiveDropdown(null);
                    return;
                }

                let finalWidth = rect.width;
                let finalLeft = rect.left;
                const MIN_WIDTH = 240;
                finalWidth = Math.max(rect.width, MIN_WIDTH);

                if (finalLeft + finalWidth > window.innerWidth - 20) {
                    finalLeft = rect.right - finalWidth;
                }

                const spaceBelow = window.innerHeight - rect.bottom;
                const openUpwards = spaceBelow < 250;

                setDropdownPos(prev => ({
                    ...prev,
                    top: rect.bottom + 4,
                    left: finalLeft,
                    width: finalWidth,
                    bottom: window.innerHeight - rect.top + 4,
                    openUpwards,
                    isMobile
                }));
            };

            // Capture on capture phase to catch scroll on any element
            window.addEventListener('scroll', updateDropdownPosition, true);
            window.addEventListener('resize', updateDropdownPosition);
            return () => {
                window.removeEventListener('scroll', updateDropdownPosition, true);
                window.removeEventListener('resize', updateDropdownPosition);
            };
        }
    }, [activeDropdown]);
    const isLongPress = useRef(false);
    const chatScrollRef = useRef(null);
    const lastUserMessageRef = useRef(null); // Ref for "Scroll to Last Prompt"
    const chatEndRef = useRef(null); // Ref for bottom (fallback)

    // --- Config UI State ---
    const [openConfigCatId, setOpenConfigCatId] = useState(null);

    // Layout State (Banner & Button)
    const [currentBannerIndex, setCurrentBannerIndex] = useState(0);
    const [hasGenerated, setHasGenerated] = useState(false);

    // === NEW: Chat Session State (for chatbox features) ===
    const [currentSession, setCurrentSession] = useState(null);
    const [sessionRuns, setSessionRuns] = useState([]);
    const [isLoadingSession, setIsLoadingSession] = useState(false);
    const [chatSessions, setChatSessions] = useState([]);

    // --- Side-by-Side Chat State ---
    const [sideBySideSessionId, setSideBySideSessionId] = useState(() => {
        try {
            return localStorage.getItem('neo_side_by_side_id');
        } catch {
            return null;
        }
    });
    const [sideBySideRuns, setSideBySideRuns] = useState([]);
    const [isLoadingSideBySide, setIsLoadingSideBySide] = useState(false);

    // -- Scroll Logic for Side-by-Side View --
    const lastScrolledSessionIdRef = useRef(null);

    // Attempt to scroll when runs update and DOM is ready
    useEffect(() => {
        if (!sideBySideSessionId || sideBySideRuns.length === 0) return;

        // If we haven't handled this session yet
        if (lastScrolledSessionIdRef.current !== sideBySideSessionId) {
            // In flex-col-reverse with Newest-First array, the newest run is at index 0
            const newestRun = sideBySideRuns[0];

            // If the newest run is still generating (no result), DO NOT scroll.
            // We want to stay at the bottom to follow the generation.
            if (!newestRun.result) {
                lastScrolledSessionIdRef.current = sideBySideSessionId;
                return;
            }

            // If it has a result, we wait a bit for layout to settle, then scroll to the prompt.
            const timer = setTimeout(() => {
                if (lastUserMessageRef.current) {
                    lastUserMessageRef.current.scrollIntoView({ behavior: 'auto', block: 'start' });
                }
                // Mark as handled
                lastScrolledSessionIdRef.current = sideBySideSessionId;
            }, 500); // 500ms delay to force scroll after layout settles

            return () => clearTimeout(timer);
        }
    }, [sideBySideRuns, sideBySideSessionId]);

    // -- Scroll Logic for Main Chat Area (Scroll to User Prompt) --
    const mainChatScrolledRef = useRef(null); // Tracks which session ID we've scrolled for
    const pendingScrollSessionRef = useRef(null); // Tracks pending scroll request
    const prevRunsLenRef = useRef(0); // Tracks checks for NEW messages vs STREAMING updates

    useEffect(() => {
        // Only run for chatbox features
        const isChatbox = featureConfig?.output === 'chatbox' ||
            featureConfig?.output_type === 'chatbox' ||
            featureConfig?.output_type?.includes('Chat Application');

        // Wait until loading is done and we have runs
        if (!isChatbox || isLoadingSession || sessionRuns.length === 0) return;

        const currentSessionId = currentSession?.id || 'new';

        // 1. Detect New Message Added (Length Increased)
        // This handles "Direct Scroll on New Input" request
        const isLenIncreased = sessionRuns.length > prevRunsLenRef.current;
        prevRunsLenRef.current = sessionRuns.length; // Update ref for next render

        // 2. Detect Session Switch / History Load
        const hasPendingScroll = pendingScrollSessionRef.current === currentSessionId;
        const isNewSession = mainChatScrolledRef.current !== currentSessionId;
        const isSessionSwitch = hasPendingScroll || isNewSession;

        // Decision: Should we scroll?
        let shouldScroll = false;
        const newestRun = sessionRuns[sessionRuns.length - 1];

        if (isLenIncreased) {
            // Always scroll if a NEW message is added (User input or AI start)
            // This ensures we jump to input immediately when generated
            shouldScroll = true;
        } else if (isSessionSwitch) {
            // If switching sessions, scroll ONLY if not processing (loading history)
            // UNLESS explicit pending scroll is requested (clicked from sidebar)
            if (newestRun?.status !== 'processing' || hasPendingScroll) {
                shouldScroll = true;
            }
        }

        if (shouldScroll) {
            const scrollToUserPrompt = () => {
                requestAnimationFrame(() => {
                    requestAnimationFrame(() => {
                        setTimeout(() => {
                            if (lastUserMessageRef.current && chatScrollRef.current) {
                                lastUserMessageRef.current.scrollIntoView({
                                    behavior: 'auto',
                                    block: 'start'
                                });
                                // Add small offset padding compensation
                                setTimeout(() => {
                                    if (chatScrollRef.current) {
                                        chatScrollRef.current.scrollTop -= 20;
                                    }
                                }, 50);
                            }
                            // Mark handled
                            mainChatScrolledRef.current = currentSessionId;
                            pendingScrollSessionRef.current = null;
                        }, 150); // Delay for DOM readiness
                    });
                });
            };

            scrollToUserPrompt();
        }
    }, [currentSession?.id, isLoadingSession, sessionRuns, featureConfig?.output, featureConfig?.output_type]);
    // NOTE: sessionRuns is in deps but scroll only happens ONCE per session ID (tracked by mainChatScrolledRef)

    // Note: Side-by-Side session persists in localStorage even when navigating to non-chatbox features
    // The UI is simply hidden for non-chatbox, and automatically restored when returning to chatbox

    // --- UI State for Chat Sidebar ---
    const [editingSessionId, setEditingSessionId] = useState(null);
    const [editTitle, setEditTitle] = useState("");
    const [sessionToDelete, setSessionToDelete] = useState(null); // Session object for modal confirmation
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false); // Delete All Confirmation
    const [openMenuSessionId, setOpenMenuSessionId] = useState(null); // For dropdown menu

    // --- Model Switch Confirmation State ---
    const [showSwitchModelModal, setShowSwitchModelModal] = useState(false);
    const [pendingModelId, setPendingModelId] = useState(null);
    const [dontAskSwitchModel, setDontAskSwitchModel] = useState(false);

    // --- Delete Item Modal State ---
    const [itemToDelete, setItemToDelete] = useState(null); // Item object for custom delete modal
    const [pinnedHistoryItem, setPinnedHistoryItem] = useState(null); // Pinned history item for external actions
    // --- Clear Input Cache on Tool Switch ---
    const prevSlugRef = useRef(null);
    useEffect(() => {
        // Clear cache if slug changed (user switched tools)
        if (prevSlugRef.current && prevSlugRef.current !== slug && user?.id) {
            InputCache.clear(prevSlugRef.current, user.id);
            // RESET Submission states to prevent stale UI on navigation
            setIsSubmitting(false);
            setSubmitError("");
            setSubmitSuccess("");

            // FIX: Reset Chat Mode & Session states to prevent cross-tool pollution
            setIsChatMode(false);
            setCurrentSession(null);
            setSessionRuns([]);
            setChatSessions([]);
            setGeneratedOutput(null);
            setHasGenerated(false);
            setPinnedHistoryItem(null);
        }
        prevSlugRef.current = slug;
    }, [slug, user?.id]);

    // --- Recent Assets (Inline Gallery) ---

    const [recentAssets, setRecentAssets] = useState(() => {
        // 0ms Init: Try RAM -> Disk
        if (!ctxUser?.id) return [];
        const key = `recent_assets_${ctxUser.id}`;
        if (MEMORY_CACHE[key]) return MEMORY_CACHE[key];
        const local = CacheService.get(key);
        if (local) {
            MEMORY_CACHE[key] = local;
            return local;
        }
        return [];
    });
    const [loadingRecent, setLoadingRecent] = useState(false);
    const [assetToDelete, setAssetToDelete] = useState(null);
    const [isDeletingAsset, setIsDeletingAsset] = useState(false);
    const [menuAnchor, setMenuAnchor] = useState(null);
    const hasFetchedRecentRef = useRef(false); // Track if we've already fetched

    // Fetch recent assets function (can be called manually for refresh)
    const fetchRecentAssets = async (forceRefresh = false, silent = false, overrideUserId = null) => {
        const targetUserId = overrideUserId || user?.id;
        if (!targetUserId) return;

        const cacheKey = `recent_assets_${targetUserId}`;

        // 1. Instant Cache Hit (if not forcing refresh)
        if (!forceRefresh && !silent) {
            let cached = MEMORY_CACHE[cacheKey] || CacheService.get(cacheKey);
            if (cached) {
                setRecentAssets(cached);
                // Hydrate RAM
                if (!MEMORY_CACHE[cacheKey]) MEMORY_CACHE[cacheKey] = cached;
                // Continue to silent update
                silent = true;
            }
        }

        // Show loading only on first fetch (not silent background refresh)
        if (!silent) setLoadingRecent(true);

        try {
            const folderPath = `${targetUserId}`;

            // 1. Fetch recent uploads
            const { data: uploads } = await supabase.storage.from("asset-user").list(folderPath, {
                limit: 20,
                sortBy: { column: 'created_at', order: 'desc' }
            });

            const uploadAssets = (uploads || []).map(file => ({
                name: file.name,
                url: supabase.storage.from("asset-user").getPublicUrl(`${folderPath}/${file.name}`).data.publicUrl,
                type: file.metadata?.mimetype || "application/octet-stream",
                source: 'upload',
                size: file.metadata?.size || 0,
                date: new Date(file.created_at).getTime()
            }));

            // 2. Persist Cache
            setRecentAssets(uploadAssets);

            // Prevent excessive writes if data didn't change (Simple optimization)
            const oldCache = MEMORY_CACHE[cacheKey];
            if (JSON.stringify(oldCache) !== JSON.stringify(uploadAssets)) {
                MEMORY_CACHE[cacheKey] = uploadAssets;
                CacheService.set(cacheKey, uploadAssets, 60 * 24); // 24 Hours Cache

                // 3. Preload Images as BLOBS (True 0ms - Stored in RAM, not HTTP cache)
                const blobPromises = uploadAssets.slice(0, 15).map(async asset => {
                    const isImg = asset.type.startsWith('image/') || ['jpg', 'png', 'webp', 'jpeg', 'gif'].some(ext => asset.name.toLowerCase().endsWith(ext));
                    if (isImg) {
                        // Standard HTTP preload (fallback)
                        IMAGE_RAM_CACHE.add(asset.url);
                        // Blob Preload (True 0ms)
                        await BLOB_CACHE.prefetch(asset.url);
                    }
                });
                // Don't await - let it run in background
                Promise.all(blobPromises).catch(e => console.warn('Blob prefetch warning:', e));
            }

            hasFetchedRecentRef.current = true;
        } catch (err) {
            console.error("Error fetching recent assets:", err);
        } finally {
            if (!silent) setLoadingRecent(false);
        }
    };

    useEffect(() => {
        if ((showAttachmentMenu || (activeMobileMenuId && typeof activeMobileMenuId === 'string' && activeMobileMenuId.startsWith('menu-'))) && user?.id && slug) {
            if (hasFetchedRecentRef.current && recentAssets.length > 0) {
                // Already have cached data - show instantly, then refresh in background (silent)
                fetchRecentAssets(true, true); // silent background refresh
            } else {
                // First load - show loading spinner
                fetchRecentAssets(false, false);
            }
        }
    }, [showAttachmentMenu, activeMobileMenuId, user?.id, slug]);

    const handleAttachAsset = (url, fileMetadata = null) => {
        // 1. Determine Asset Type from URL
        const ext = url.split('.').pop().toLowerCase();
        let assetType = 'file';
        if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) assetType = 'image';
        else if (['mp4', 'mov', 'webm', 'mkv', 'avi', 'wmv'].includes(ext)) assetType = 'video';
        else if (['mp3', 'wav', 'ogg', 'm4a'].includes(ext)) assetType = 'audio';
        else if (['pdf'].includes(ext)) assetType = 'pdf';
        else if (['doc', 'docx'].includes(ext)) assetType = 'docx';
        else if (['xls', 'xlsx', 'csv'].includes(ext)) assetType = 'excel';
        else if (['txt'].includes(ext)) assetType = 'txt';


        // 2. Find Compatible Field
        // Priority: file_multi (if it accepts the type) -> Type-Specific Field

        // A. Check for file_multi field first
        const fileMultiField = modelFields.find(f => f.type === 'file_multi' && !f.is_hidden);
        let targetField = null;

        if (fileMultiField) {
            let options = fileMultiField.options;
            if (typeof options === 'string') {
                try { options = JSON.parse(options); } catch { options = {}; }
            }

            // Check allowed_types (if defined, must include assetType; if not defined, accept all)
            const allowedTypes = options?.allowed_types;
            const isAllowed = !allowedTypes || !Array.isArray(allowedTypes) || allowedTypes.length === 0 || allowedTypes.includes(assetType);

            if (isAllowed) {
                targetField = fileMultiField;
            }
        }

        // B. Fallback: Find ALL type-specific fields (e.g. Video -> Video Inputs)
        if (!targetField) {
            const specificFields = modelFields.filter(f => f.type === assetType && !f.is_hidden);

            if (specificFields.length === 1) {
                targetField = specificFields[0];
            } else if (specificFields.length > 1) {
                // Multiple fields of same type -> Show Selection Modal

                setPinSelectionModal({
                    url: url,
                    candidates: specificFields,
                    assetType: assetType,
                    item: { fileMetadata } // Pass metadata for validation
                });
                setShowAttachmentMenu(false);
                return; // Exit, modal will handle the rest
            }
        }


        // 3. Execute Attachment or Show Error
        if (targetField) {
            // --- VALIDATION START ---

            // 3.1 Size Check (if metadata provided)
            if (fileMetadata && fileMetadata.size) {
                let maxMB = 10; // System Default

                // Get specifics from either target field (if single) or the Type-Specific field (if multi)
                // If target is file_multi, we must find the 'image' field (etc) to get the max size config for images.
                // If target is specific (e.g. image input), use that.

                let limitField = targetField;
                if (targetField.type === 'file_multi') {
                    const specificField = modelFields.find(f => f.type === assetType && !f.is_hidden);
                    if (specificField) limitField = specificField;
                }

                let opts = limitField.options || {};
                if (typeof opts === 'string') {
                    try { opts = JSON.parse(opts); } catch { opts = {}; }
                }
                if (opts.max_size_mb) maxMB = parseInt(opts.max_size_mb);

                // Extra check: if we somehow got 'file_multi' options and it has no max_size_mb, check if model has 'file' type?
                // But generally the logic above covers 'image', 'video' fields.

                if (fileMetadata.size > maxMB * 1024 * 1024) {
                    showToast(`File ${fileMetadata.name || url.split('/').pop()} terlalu besar (Max ${maxMB}MB untuk ${assetType}).`, 'error');
                    return;
                }
            }

            if (targetField.type === 'file_multi') {
                const current = inputs[targetField.key] || [];
                // Check double add
                if (Array.isArray(current) && current.includes(url)) {
                    showToast("File sudah ditambahkan.", "info");
                    return;
                }
                const currentArray = Array.isArray(current) ? current : [];

                // 3.2 Total Count Check
                const maxTotalFiles = targetField.default_value ? parseInt(targetField.default_value) : 5;
                if (currentArray.length >= maxTotalFiles) {
                    showToast(`Batas maksimal ${maxTotalFiles} file sudah tercapai.`, 'error');
                    return;
                }

                handleInputChange(targetField.key, [...currentArray, url]);
            } else {
                handleInputChange(targetField.key, url);
            }
            setShowAttachmentMenu(false);
            setActiveMobileMenuId(null); // Close mobile menu if open
        } else {

            showToast("Tipe file ini tidak didukung oleh model ini.", "error");
            setShowAttachmentMenu(false);
            setActiveMobileMenuId(null);
        }
    };

    const confirmDeleteAsset = async () => {
        if (!assetToDelete || !user?.id) return;
        setIsDeletingAsset(true);
        try {
            // Path construction must match upload logic: {userId}/{filename}
            const filePath = `${user.id}/${assetToDelete.name}`;

            const { error } = await supabase.storage.from("asset-user").remove([filePath]);

            if (error) throw error;

            // Update local state
            setRecentAssets(prev => prev.filter(a => a.name !== assetToDelete.name));
            showToast("File berhasil dihapus.", "success");
            setAssetToDelete(null);
        } catch (e) {
            console.error("Delete failed", e);
            showToast("Gagal menghapus file.", "error");
        } finally {
            setIsDeletingAsset(false);
        }
    };

    // --- Button Config Parsing ---
    const actionButtons = React.useMemo(() => {
        if (featureConfig?.description?.includes("|||CONFIG|||")) {
            const parts = featureConfig.description.split("|||CONFIG|||");
            try {
                const parsed = JSON.parse(parts[1]);
                return Array.isArray(parsed) ? parsed : [];
            } catch (e) {
                console.error("Failed to parse buttons", e);
            }
        }
        return [];
    }, [featureConfig]);

    // Auto-rotate banner carousel
    useEffect(() => {
        const timer = setInterval(() => {
            setCurrentBannerIndex(prev => prev + 1);
        }, 5000); // 5 seconds per slide
        return () => clearInterval(timer);
    }, []);

    // --- Refs for Realtime Listener (Avoid Stale State) ---
    const isClientSideNavigationRef = useRef(false); // Flag to prevent spinner on click
    const currentSessionRef = useRef(currentSession);
    const sessionRunsRef = useRef(sessionRuns);
    const processingJobRef = useRef(processingJob);

    useEffect(() => { currentSessionRef.current = currentSession; }, [currentSession]);
    useEffect(() => { sessionRunsRef.current = sessionRuns; }, [sessionRuns]);
    useEffect(() => { processingJobRef.current = processingJob; }, [processingJob]);

    // --- Realtime Subscription (Auto-Refresh) ---
    useEffect(() => {
        if (!user?.id) return;

        // console.log("Subscribing to realtime updates for user:", user.id);
        const channel = supabase
            .channel(`ai_runs_${user.id}_${slug}`)
            .on(
                'postgres_changes',
                {
                    event: '*', // Listen for INSERT and UPDATE
                    schema: 'public',
                    table: 'ai_model_runs',
                    filter: `user_id=eq.${user.id}`
                },
                (payload) => {
                    const runId = payload.new.id || (payload.old && payload.old.id);
                    if (!runId) return;

                    // Use Refs to get latest state
                    const curSession = currentSessionRef.current;
                    const curRuns = sessionRunsRef.current;
                    const curJob = processingJobRef.current;

                    // A run is relevant if:
                    // 1. Its feature_code matches our current slug
                    // 2. OR we already have this run ID in our local state
                    const relatedCode = payload.new.feature_code;
                    const isFeatureMatch = relatedCode
                        ? (relatedCode === slug || relatedCode === featureConfig?.slug || relatedCode === featureConfig?.code)
                        : false;

                    const isAlreadyTracked =
                        (curJob && curJob.id === runId) ||
                        (curRuns.some(r => r.id === runId)) ||
                        (history.some(r => r.id === runId));

                    const relevant = isFeatureMatch || isAlreadyTracked;

                    if (relevant) {
                        // console.log("Realtime update received:", payload);
                        checkProcessingStatus(user.id);

                        if (payload.new.status === 'success' || payload.new.status === 'failed') {
                            fetchHistory(user.id, relatedCode || featureConfig?.code || slug);
                        }

                        // === Update session runs for chatbox features ===
                        const isChatboxForRealtime =
                            featureConfig?.output === 'chatbox' ||
                            featureConfig?.output_type === 'chatbox' ||
                            featureConfig?.output_type?.includes('Chat Application');

                        if (isChatboxForRealtime) {
                            const runSessionId = payload.new.session_id;
                            const targetSessionId = curSession?.id;

                            // STRICTER CHECK using fresh Ref data
                            const isRunForCurrentSession =
                                (runSessionId && runSessionId === targetSessionId) || // Exact Session Match
                                (curRuns.some(r => r.id === runId)) || // Already in list (e.g. optimistic)
                                ((!curSession || curSession.isTemp) && payload.new.feature_code === slug); // New Chat / Temp Session

                            if (isRunForCurrentSession) {
                                if (payload.eventType === 'INSERT') {
                                    setSessionRuns(prev => {
                                        const exists = prev.some(r => r.id === runId);
                                        // CRITICAL FIX: If exists, update it! Do NOT return early.
                                        // This handles the transition from Optimistic "Processing" -> Real "Success"
                                        if (exists) {
                                            return prev.map(run =>
                                                run.id === runId ? { ...run, ...payload.new } : run
                                            );
                                        }
                                        const cleanPrev = prev.filter(r => !r.id.toString().startsWith('opt_'));
                                        return [...cleanPrev, payload.new];
                                    });
                                } else if (payload.eventType === 'UPDATE') {
                                    setSessionRuns(prev => {
                                        const exists = prev.some(r => r.id === runId);
                                        if (exists) {
                                            return prev.map(run =>
                                                run.id === runId ? { ...run, ...payload.new } : run
                                            );
                                        } else {
                                            const cleanPrev = prev.filter(r => !r.id.toString().startsWith('opt_'));
                                            return [...cleanPrev, payload.new];
                                        }
                                    });
                                }
                            }
                        }
                    }
                }
            )
            .subscribe();

        return () => {
            supabase.removeChannel(channel);
        };
    }, [user?.id, featureConfig, slug]);


    // --- Auto-scroll to bottom of chat ---
    // --- Auto-scroll to bottom of chat ---
    // Removed explicit scroll logic as we are switching to flex-col-reverse generic layout
    // for natural bottom anchoring.
    /*
    useLayoutEffect(() => {
        if (chatScrollRef.current) {
            const behavior = isSubmitting ? 'smooth' : 'auto';
             chatScrollRef.current.scrollTo({
                top: chatScrollRef.current.scrollHeight,
                behavior: behavior
            });
        }
    }, [sessionRuns, isSubmitting, isLoadingSession]);
    */

    // Helper to guess icon based on category name
    const getCategoryIcon = (name) => {
        const n = name.toLowerCase();
        if (n.includes('ratio') || n.includes('dimensi') || n.includes('aspect') || n.includes('ukuran')) return Icons.LayoutTemplate;
        if (n.includes('durasi') || n.includes('waktu') || n.includes('time')) return Icons.Clock;
        if (n.includes('quality') || n.includes('kualitas') || n.includes('resolusi')) return Icons.Sparkles;
        if (n.includes('style') || n.includes('gaya')) return Icons.Palette;
        if (n.includes('suara') || n.includes('voice') || n.includes('audio') || n.includes('tone')) return Icons.Mic;
        return Icons.SlidersHorizontal;
    };

    // Helper to render dynamic option icon (e.g. Aspect Ratio shapes)
    const renderOptionIcon = (categoryName, label, isSelected = false) => {
        const cat = categoryName.toLowerCase();
        if (cat.includes('ratio') || cat.includes('dimensi') || cat.includes('aspect') || cat.includes('ukuran')) {
            // Try to find "W:H" pattern, supporting integers (16:9) and decimals (2.35:1)
            const match = label.match(/([\d\.]+):([\d\.]+)/);
            if (match) {
                const [_, w, h] = match;
                return (
                    <div
                        className={`border rounded-sm transition-colors ${isSelected ? "border-sky-600" : "border-slate-400 group-hover:border-slate-500"}`}
                        style={{
                            width: '14px',
                            height: '14px',
                            aspectRatio: `${w}/${h}`,
                            // Adjust behavior to contain within the box while keeping aspect ratio
                            // Actually, simpler is: if w>h --> width=14, height=auto. If h>w --> height=14, width=auto.
                            ...(parseInt(w) >= parseInt(h) ? { height: 'auto' } : { width: 'auto' })
                        }}
                    />
                );
            }
            // Fallback for "Square" without 1:1 text, etc? Usually they have configs. 
            return <Icons.Square size={14} className={isSelected ? "text-sky-600" : "text-slate-400"} />;
        }
        return null;
    };

    // Close dropdowns when clicking outside
    const CloseBackdrop = () => (
        <div className="fixed inset-0 z-40 bg-transparent" onClick={() => setOpenConfigCatId(null)} />
    );

    // --- Check Global Processing Status ---
    const checkProcessingStatus = async (userId) => {
        if (!userId) return;
        // Use limit(1) instead of maybeSingle() to avoid errors if multiple jobs exist
        const { data } = await supabase
            .from("ai_model_runs")
            .select("*")
            .eq("user_id", userId)
            .eq("status", "processing")
            .order("created_at", { ascending: false })
            .limit(1);

        const job = data && data.length > 0 ? data[0] : null;

        if (job) {
            try {
                const { data: fData } = await supabase
                    .from('app_features')
                    .select('name')
                    .or(`slug.eq.${job.feature_code},code.eq.${job.feature_code}`)
                    .limit(1)
                    .single();
                if (fData?.name) job.feature_name = fData.name;
            } catch (e) {
                console.error("Failed to resolve feature name", e);
            }
        }

        setProcessingJob(job);
        return job;
    };

    // --- Force Check Status on Load/Nav ---
    useEffect(() => {
        checkProcessingStatus(user?.id);
    }, [user?.id, slug]);

    // --- Fetch History (With Image Preloading) ---
    const fetchHistory = async (userId, featureCode, forceRefresh = false) => {
        if (!userId) return;

        // 1. CACHE-FIRST STRATEGY: Check Memory -> Disk
        // If data is found and NOT forcing refresh, use it and STOP.
        // New updates are handled by the Realtime Subscription.
        const cacheKey = `history_${featureCode}_${userId}`;

        // Skip cache if forcing refresh
        if (!forceRefresh) {
            // Check RAM
            let cachedData = MEMORY_CACHE[cacheKey];

            // If not in RAM, check Disk (localStorage) via CacheService
            if (!cachedData) {
                cachedData = CacheService.get(cacheKey);
                if (cachedData) {
                    // Hydrate RAM for next time
                    MEMORY_CACHE[cacheKey] = cachedData;
                }
            }

            // If we have data, use it and abort fetch
            if (cachedData) {
                setHistory(cachedData);

                // Re-run image preload logic on cached data to ensure 0ms images
                const imagesToPreload = cachedData
                    .slice(0, 12)
                    .map(item => {
                        const res = item.parsedResult;
                        if (res?.public_url) {
                            const ext = res.public_url.split(/[#?]/)[0].split('.').pop().trim().toLowerCase();
                            if (['jpg', 'jpeg', 'png', 'webp', 'gif', 'svg'].includes(ext)) {
                                return res.public_url;
                            }
                        }
                        return null;
                    })
                    .filter(url => url);
                if (imagesToPreload.length > 0) {
                    imagesToPreload.forEach(url => IMAGE_RAM_CACHE.add(url));
                }

                return; // <--- Stop network request
            }
        } else {
            // Force refresh: invalidate cache first
            delete MEMORY_CACHE[cacheKey];
            CacheService.remove(cacheKey);
        }

        try {
            const { data } = await supabase
                .from("ai_model_runs")
                .select("id, result, created_at, model_name, inputs, model_key")
                .eq("user_id", userId)
                .eq("feature_code", featureCode)
                .in("status", ["success", "failed"])
                .not("result", "cs", '{"is_cleaned": true}') // Filter out expired/cleaned files
                .order("created_at", { ascending: false })
                .limit(20);

            // POST-PROCESSING: Flatten & Limit to exactly 20 ITEMS
            const MAX_TOTAL_ITEMS = 20;
            let flattenedItems = [];

            if (data) {
                for (const row of data) {
                    // Safety break if we already have enough
                    if (flattenedItems.length >= MAX_TOTAL_ITEMS) break;

                    let parsed;
                    try {
                        parsed = typeof row.result === 'string' ? JSON.parse(row.result) : row.result;
                    } catch (e) { continue; }

                    if (!parsed || parsed.is_cleaned) continue;

                    let rowItems = [];
                    if (Array.isArray(parsed)) {
                        rowItems = parsed.map((res, idx) => ({
                            ...row,
                            uniqueId: `${row.id}_${idx}`,
                            parsedResult: res,
                            resultIndex: idx
                        }));
                    } else {
                        rowItems = [{
                            ...row,
                            uniqueId: row.id,
                            parsedResult: parsed
                        }];
                    }

                    // Push items until we hit the limit
                    const remainingSpace = MAX_TOTAL_ITEMS - flattenedItems.length;
                    if (remainingSpace > 0) {
                        flattenedItems.push(...rowItems.slice(0, remainingSpace));
                    }
                }
            }

            // 2. UPDATE CACHE & STATE
            // Persist to RAM & Disk
            MEMORY_CACHE[cacheKey] = flattenedItems;
            CacheService.set(cacheKey, flattenedItems, 60); // 1 Hour Disk Cache
            setHistory(flattenedItems);

            // --- PRELOAD IMAGES (Optimization for "Pop-in") ---
            // Extract the first 12 images to preload (don't block for all 20 if slow)
            const imagesToPreload = flattenedItems
                .slice(0, 12)
                .map(item => {
                    const res = item.parsedResult;
                    if (res?.public_url) {
                        const ext = res.public_url.split(/[#?]/)[0].split('.').pop().trim().toLowerCase();
                        // Preload standard images and also webp/gifs
                        if (['jpg', 'jpeg', 'png', 'webp', 'gif', 'svg'].includes(ext)) {
                            return res.public_url;
                        }
                    }
                    return null;
                })
                .filter(url => url);

            if (imagesToPreload.length > 0) {
                // Use IMAGE_RAM_CACHE to force Memory Cache (0ms latency)
                imagesToPreload.forEach(url => {
                    IMAGE_RAM_CACHE.add(url);
                });
            }
        } catch (err) {
            console.error("History fetch error", err);
        }
    };

    // --- Manual Refresh for Models (Realtime) ---
    const refreshModelsOnly = async () => {
        if (!featureConfig?.id || !user?.id) return;
        try {
            console.log("🔄 Realtime: Refreshing Models...");
            const { data: userRole } = await supabase.from('profiles').select('role').eq('id', user.id).single();
            const userIsAdmin = userRole?.role === 'admin';

            // Fetch Models for THIS feature
            const { data: rawModels } = await supabase.from("ai_models")
                .select("id,name,key,price_credits,description,created_at,configuration_options,status,is_active,video_url,label,ai_model_providers(id,name,icon_url), feature_code, feature_id")
                .eq("feature_id", featureConfig.id);

            if (!rawModels) return;

            let validModels = [];
            if (userIsAdmin) {
                validModels = rawModels.filter(m => ['active', 'development'].includes(m.status));
            } else {
                validModels = rawModels.filter(m => m.status === 'active');
            }

            validModels.sort((a, b) => {
                const orderA = a.configuration_options?.sort_order ?? 9999;
                const orderB = b.configuration_options?.sort_order ?? 9999;
                if (orderA !== orderB) return orderA - orderB;
                return new Date(b.created_at) - new Date(a.created_at);
            });

            // Fetch Fields for these models
            const modelIds = validModels.map(m => m.id);
            const { data: allFields } = await supabase.from("ai_model_fields")
                .select("*")
                .in("model_id", modelIds)
                .order("sort_order", { ascending: true })
                .order("id", { ascending: true });

            const finalAllModelFields = {};
            if (allFields) {
                allFields.forEach(f => {
                    if (!finalAllModelFields[f.model_id]) finalAllModelFields[f.model_id] = [];
                    finalAllModelFields[f.model_id].push(f);
                });
            }

            // Calculate Caps
            const finalCaps = {};
            validModels.forEach(m => {
                const mFields = finalAllModelFields[m.id] || [];
                finalCaps[m.id] = {
                    hasStart: mFields.some(f => f.key === 'image_url' && !f.is_hidden),
                    hasEnd: mFields.some(f => f.key === 'image_url_2' && !f.is_hidden)
                };
            });

            // Update States
            setModels(validModels);
            setAllModelFields(finalAllModelFields);
            setModelCapabilities(finalCaps);

            // Update Model Selector UI immediately if selected model is present
            if (selectedModelId && finalAllModelFields[selectedModelId]) {
                setModelFields(finalAllModelFields[selectedModelId]);
            }

            // Update Cache
            if (MEMORY_CACHE[slug]) {
                const newCache = {
                    ...MEMORY_CACHE[slug],
                    models: validModels,
                    allModelFields: finalAllModelFields,
                    modelCapabilities: finalCaps
                };
                MEMORY_CACHE[slug] = newCache;
                CacheService.set(`__TOOL_${slug}__`, newCache);
            }

        } catch (e) {
            console.error("Realtime Refresh Error", e);
        }
    };

    // --- Load Data Function (Reusable) ---
    const loadPageData = async (forceRefresh = false) => {
        try {
            // Reset states when switching tools (only if not forcing refresh)
            if (!forceRefresh && slug !== prevSlugRef.current && !isSubmitting) {
                setGeneratedOutput(null);
                setSubmitSuccess("");
                setSubmitError("");
                setIsSubmitting(false);
                setHasGenerated(false);
                setCurrentBannerIndex(0);
                setSessionRuns([]);
                setCurrentSession(null);
                setChatSessions([]);
                setPinnedHistoryItem(null);
                setInputs({});
                setModels([]);
                setModelFields([]);
                setAllModelFields({});
            }

            // CACHE HIT CHECK: RAM first, then Disk
            const cacheKey = `neo_tool_${slug}`;
            let CACHED = forceRefresh ? null : (MEMORY_CACHE[slug] || CacheService.get(cacheKey)); // Try RAM, then Disk

            if (CACHED) {
                // If found in Disk but not RAM, hydrate RAM
                if (!MEMORY_CACHE[slug] && !forceRefresh) {
                    MEMORY_CACHE[slug] = CACHED;
                }

                setLoading(false);
                setFeatureConfig(CACHED.featureConfig);
                setModels(CACHED.models);
                setAllModelFields(CACHED.allModelFields);
                setModelCapabilities(CACHED.modelCapabilities);

                if (CACHED.models.length > 0) {
                    const first = CACHED.models[0];
                    const currentValid = selectedModelId && CACHED.models.find(m => m.id === selectedModelId);
                    const targetId = currentValid ? selectedModelId : first.id;

                    setSelectedModelId(targetId);
                    const fields = CACHED.allModelFields[targetId] || [];
                    setModelFields(fields);

                    setInputs(prev => {
                        const initial = {};
                        fields.forEach(f => {
                            // Audio Choice Group: Start empty (User Request)
                            if (f.type === 'choice_audio') {
                                initial[f.key] = "";
                                return;
                            }

                            if (f.type === 'slider') initial[f.key] = "1";
                            else if (f.type === 'file_multi') initial[f.key] = [];
                            else {
                                if (f.key === 'image_url' && location.state?.outputImage) {
                                    initial[f.key] = location.state.outputImage;
                                } else {
                                    let def = f.default_value || "";
                                    const isNumeric = /^\d+$/.test(String(def));
                                    if ((f.type === 'textarea' || f.type === 'text') && isNumeric) def = "";
                                    if (['file', 'image', 'audio', 'video'].includes(f.type)) def = "";
                                    initial[f.key] = def;
                                }
                            }
                        });
                        return initial;
                    });
                }

                // Background Update for Cached Data
                const { data: { user: authUser } } = await supabase.auth.getUser();
                if (authUser) {
                    setUser(authUser);
                    // Parallel updates
                    Promise.all([
                        supabase.from("profiles").select("*").eq("id", authUser.id).single().then(({ data }) => setProfile(data)),
                        checkProcessingStatus(authUser.id),
                        fetchHistory(authUser.id, CACHED.featureConfig.code || CACHED.featureConfig.slug)
                    ]);

                    // --- NEW: Stale-While-Revalidate Strategy ---
                    // Explicitly fetch fresh config to ensure "Real Time" updates even if cache exists.
                    // ENABLED: We run this in background to sync Admin changes (e.g. new models).
                    // The 'hasChanged' check (down below) prevents UI flicker.
                    if (true) {
                        (async () => {
                            try {
                                const { data: freshFeature } = await supabase.from("app_features").select("*").eq("slug", slug).single();
                                if (!freshFeature) return;

                                // Check models
                                const { data: userRole } = await supabase.from('profiles').select('role').eq('id', authUser.id).single();
                                const userIsAdmin = userRole?.role === 'admin';

                                const { data: freshModels } = await supabase.from("ai_models")
                                    .select("id,name,key,price_credits,description,created_at,configuration_options,status,is_active,video_url,label,ai_model_providers(id,name,icon_url)")
                                    .eq("feature_id", freshFeature.id);

                                let validModels = [];
                                if (freshModels) {
                                    if (userIsAdmin) validModels = freshModels.filter(m => ['active', 'development'].includes(m.status));
                                    else validModels = freshModels.filter(m => m.status === 'active');

                                    validModels.sort((a, b) => {
                                        const orderA = a.configuration_options?.sort_order ?? 9999;
                                        const orderB = b.configuration_options?.sort_order ?? 9999;
                                        if (orderA !== orderB) return orderA - orderB;
                                        return new Date(b.created_at) - new Date(a.created_at);
                                    });
                                }

                                const modelIds = validModels.map(m => m.id);
                                const { data: freshFields } = await supabase.from("ai_model_fields")
                                    .select("*")
                                    .in("model_id", modelIds)
                                    .order("sort_order", { ascending: true })
                                    .order("id", { ascending: true });

                                let freshAllFields = {};
                                if (freshFields) {
                                    freshFields.forEach(f => {
                                        if (!freshAllFields[f.model_id]) freshAllFields[f.model_id] = [];
                                        freshAllFields[f.model_id].push(f);
                                    });
                                }

                                // Calculate Caps
                                const finalCaps = {};
                                validModels.forEach(m => {
                                    const mFields = freshAllFields[m.id] || [];
                                    finalCaps[m.id] = {
                                        hasStart: mFields.some(f => f.key === 'image_url' && !f.is_hidden),
                                        hasEnd: mFields.some(f => f.key === 'image_url_2' && !f.is_hidden)
                                    };
                                });

                                const hasChanged =
                                    JSON.stringify(freshFeature) !== JSON.stringify(CACHED.featureConfig) ||
                                    JSON.stringify(validModels) !== JSON.stringify(CACHED.models) ||
                                    JSON.stringify(freshAllFields) !== JSON.stringify(CACHED.allModelFields);

                                if (hasChanged) {
                                    // console.log("Fresh data found, updating state and cache...");
                                    setFeatureConfig(freshFeature);
                                    setModels(validModels);
                                    setAllModelFields(freshAllFields);
                                    setModelCapabilities(finalCaps);

                                    const freshCacheData = {
                                        featureConfig: freshFeature,
                                        models: validModels,
                                        allModelFields: freshAllFields,
                                        modelCapabilities: finalCaps
                                    };
                                    MEMORY_CACHE[slug] = freshCacheData;
                                    CacheService.set(`__TOOL_${slug}__`, freshCacheData);
                                }

                            } catch (e) {
                                console.error("Revalidation Error", e);
                            }
                        })();
                    }

                    const isChatbox =
                        CACHED.featureConfig?.output === 'chatbox' ||
                        CACHED.featureConfig?.output_type === 'chatbox' ||
                        CACHED.featureConfig?.output_type?.includes('Chat Application');

                    if (isChatbox) {
                        if (urlSessionId) await initSession(authUser.id, false, urlSessionId);
                        else await initSession(authUser.id, true);
                        await fetchChatSessions(authUser.id, CACHED.featureConfig.code || CACHED.featureConfig.slug);
                    }
                }
                return;
            }

            // COLD LOAD
            if (!forceRefresh) setLoading(true);

            // 1. Auth & Core Data
            const { data: { user: authUser }, error: authError } = await supabase.auth.getUser();
            if (authError || !authUser) { navigate("/login"); return; }
            setUser(authUser);

            // 2. Parallel Core Data (Profile, Feature, ProcessJob)
            const [profileRes, featureRes, processing] = await Promise.all([
                supabase.from("profiles").select("*").eq("id", authUser.id).single(),
                supabase.from("app_features").select("*").eq("slug", slug).single(),
                checkProcessingStatus(authUser.id)
            ]);

            setProfile(profileRes.data);
            const featureData = featureRes.data;

            if (!featureData) {
                console.error("Feature not found:", slug);
                setFeatureConfig(null);
                setLoading(false);
                return;
            }
            setFeatureConfig(featureData);

            // 3. Parallel Access Check & Models
            // Note: We check access AND fetch models in parallel. If access denied, we discard/redirect.
            const accessPromise = (async () => {
                const { data: userRole } = await supabase.from('profiles').select('role').eq('id', authUser.id).single();
                const userIsAdmin = userRole?.role === 'admin';
                if (!userIsAdmin) {
                    const { data: access } = await supabase.from('user_features').select('feature_code')
                        .eq('user_id', authUser.id).eq('feature_code', slug).maybeSingle();
                    if (!access) throw new Error("ACCESS_DENIED");
                }
                return userIsAdmin;
            })();

            const modelsPromise = supabase.from("ai_models")
                .select("id,name,key,price_credits,description,created_at,configuration_options,status,is_active,video_url,label,ai_model_providers(id,name,icon_url)")
                .eq("feature_id", featureData.id);

            let userIsAdmin = false;
            let modelsData = [];

            try {
                const [isAdmin, mRes] = await Promise.all([accessPromise, modelsPromise]);
                userIsAdmin = isAdmin;
                let rawModels = mRes.data || [];

                // Filter Models
                if (userIsAdmin) {
                    modelsData = rawModels.filter(m => ['active', 'development'].includes(m.status));
                } else {
                    modelsData = rawModels.filter(m => m.status === 'active');
                }

                modelsData.sort((a, b) => {
                    const orderA = a.configuration_options?.sort_order ?? 9999;
                    const orderB = b.configuration_options?.sort_order ?? 9999;
                    if (orderA !== orderB) return orderA - orderB;
                    return new Date(b.created_at) - new Date(a.created_at);
                });

                setModels(modelsData);

            } catch (err) {
                if (err.message === "ACCESS_DENIED") {
                    navigate('/dashboard'); return;
                }
                throw err;
            }

            // 4. Model Fields (Batch Fetch)
            let finalAllModelFields = {};
            let finalCaps = {};

            if (modelsData.length > 0) {
                // Check if we have a request to restore a specific model
                let targetModelId = modelsData[0].id; // Default to first
                const state = location.state || {}; // Robust state access
                const restoreModelName = state.restoreModelName;
                const restoreModelKey = state.restoreModelKey;
                const restoreModelId = state.restoreModelId || state.selectedModelId; // Support Sidebar & Riwayat

                let found = null;

                // 0. Exact ID Match (Absolute Best)
                if (restoreModelId) {
                    found = modelsData.find(m => m.id === restoreModelId);
                }

                // 1. Precise Match by Key 
                if (!found && restoreModelKey) {
                    found = modelsData.find(m => m.key === restoreModelKey);
                }

                // 2. Fallback: Match by Name (Fuzzy & Partial)
                if (!found && restoreModelName) {
                    const normName = restoreModelName.trim().toLowerCase();
                    found = modelsData.find(m => {
                        const mName = (m.name || "").trim().toLowerCase();
                        const mKey = (m.key || "").trim().toLowerCase();
                        const mLabel = (m.label || "").trim().toLowerCase();
                        return mName === normName || mKey === normName || mLabel === normName
                            || (mName.includes(normName) && normName.length > 4)
                            || (mLabel.includes(normName) && normName.length > 4);
                    });
                }

                if (found) {
                    targetModelId = found.id;
                }

                setSelectedModelId(targetModelId);

                const modelIds = modelsData.map(m => m.id);
                const { data: allFields } = await supabase.from("ai_model_fields")
                    .select("*")
                    .in("model_id", modelIds)
                    .order("sort_order", { ascending: true })
                    .order("id", { ascending: true });

                if (allFields) {
                    allFields.forEach(f => {
                        if (!finalAllModelFields[f.model_id]) finalAllModelFields[f.model_id] = [];
                        finalAllModelFields[f.model_id].push(f);
                    });
                }
                setAllModelFields(finalAllModelFields);

                modelsData.forEach(m => {
                    const mFields = finalAllModelFields[m.id] || [];
                    finalCaps[m.id] = {
                        hasStart: mFields.some(f => f.key === 'image_url' && !f.is_hidden),
                        hasEnd: mFields.some(f => f.key === 'image_url_2' && !f.is_hidden)
                    };
                });
                setModelCapabilities(finalCaps);

                // Configure Initial Inputs
                // FIXED: Use targetModelId, not undefined 'first.id'
                const configuredFields = finalAllModelFields[targetModelId] || [];
                setModelFields(configuredFields);

                // NEW: Restore Cache Once on Load (Init Only) to initialize state correctly
                const cachedInputs = user?.id ? InputCache.restore(slug, user.id) : null;

                const initialInputs = {};
                (configuredFields || []).forEach(f => {
                    let val = null;

                    // 1. Try Cache First (Recreate / Previous Session)
                    if (cachedInputs && f.key in cachedInputs) {
                        const cVal = cachedInputs[f.key];
                        // Basic strict check
                        if (cVal !== null && cVal !== undefined) val = cVal;
                    }

                    // 2. Fallback to Defaults
                    if (val === null) {
                        if (f.type === 'choice_audio') val = "";
                        else if (f.type === 'slider') val = "1";
                        else if (f.type === 'file_multi') val = [];
                        else if (f.key === 'image_url' && location.state?.outputImage) val = location.state.outputImage;
                        else {
                            let def = f.default_value || "";
                            const isNumeric = /^\d+$/.test(String(def));
                            if ((f.type === 'textarea' || f.type === 'text') && isNumeric) def = "";
                            if (['file', 'image', 'audio', 'video'].includes(f.type)) def = "";
                            val = def;
                        }
                    }
                    initialInputs[f.key] = val;
                });
                setInputs(initialInputs);
            }

            // CACHE SAVE
            const cacheData = {
                featureConfig: featureData,
                models: modelsData,
                allModelFields: finalAllModelFields,
                modelCapabilities: finalCaps
            };
            MEMORY_CACHE[slug] = cacheData;
            CacheService.set(`__TOOL_${slug}__`, cacheData);

            // 5. Parallel Secondary Data (History & Chat Stats)
            const historyPromise = fetchHistory(authUser.id, featureData.code || featureData.slug);

            const isChatboxFeature =
                featureData?.output === 'chatbox' ||
                featureData?.output_type === 'chatbox' ||
                featureData?.output_type?.includes('Chat Application');

            const chatPromise = isChatboxFeature ? (async () => {
                if (urlSessionId) await initSession(authUser.id, false, urlSessionId);
                else await initSession(authUser.id, true);
                await fetchChatSessions(authUser.id, featureData.code || featureData.slug);
            })() : Promise.resolve();

            // 6. Pre-fetch Recent Assets (Background Silence Mode)
            // This ensures assets are already in cache when user opens the menu
            const assetsPromise = fetchRecentAssets(false, true, authUser.id);

            await Promise.all([historyPromise, chatPromise, assetsPromise]);

        } catch (e) {
            console.error("Init Error", e);
        } finally {
            setLoading(false);
        }
    };

    // Initial Load
    // Initial Load (Data Fetching)
    useEffect(() => {
        loadPageData(false);
    }, [slug, urlSessionId, navigate]);

    // Handle Model Switching via Navigation State (Sidebar/Recreate)
    useEffect(() => {
        const state = location.state || {};
        // Skip if no relevant state
        if (!state.selectedModelId && !state.restoreModelId && !state.restoreModelKey && !state.restoreModelName) return;

        if (models && models.length > 0) {
            let found = null;
            const targetId = state.selectedModelId || state.restoreModelId;

            // 1. ID Match
            if (targetId) found = models.find(m => m.id == targetId);

            // 2. Key Match
            if (!found && state.restoreModelKey) found = models.find(m => m.key === state.restoreModelKey);

            // 3. Name Match (Fuzzy)
            if (!found && state.restoreModelName) {
                const normName = state.restoreModelName.trim().toLowerCase();
                found = models.find(m => {
                    const mName = (m.name || "").trim().toLowerCase();
                    const mLabel = (m.label || "").trim().toLowerCase();
                    return mName === normName || mLabel === normName
                        || (mName.includes(normName) && normName.length > 4);
                });
            }

            // Only update if different to prevent loops
            if (found && found.id !== selectedModelId) {
                setSelectedModelId(found.id);
            }
        }
    }, [location.state, models]);

    useEffect(() => {
        // Consistent Chat Mode detection matching loadPageData logic
        const isChat =
            featureConfig?.output === 'chatbox' ||
            featureConfig?.output_type === 'chatbox' ||
            featureConfig?.output_type?.includes('Chat Application') ||
            featureConfig?.output_type === 'chat';

        setIsChatMode(!!isChat);
    }, [featureConfig]);

    // Auto-scroll logic for chatbox



    // (Chat history useEffect removed - using pin-based approach now)



    // --- Realtime Cache Invalidation & Revalidate on Focus ---
    useEffect(() => {
        // 1. Realtime Listener for Config Changes
        const channel = supabase.channel(`generic_ai_config_updates_${slug}`)
            .on(
                'postgres_changes',
                { event: '*', schema: 'public', table: 'ai_models' },
                async (payload) => {
                    const changed = payload.new || payload.old;
                    // Check if relevant to current feature
                    // We check feature_code (slug) OR feature_id
                    const isRelevant =
                        (changed.feature_code === slug) ||
                        (featureConfig?.id && changed.feature_id === featureConfig.id);

                    if (isRelevant) {
                        await refreshModelsOnly();
                    }
                }
            )
            .on(
                'postgres_changes',
                { event: '*', schema: 'public', table: 'ai_model_fields' },
                async (payload) => {
                    // For fields, we don't easily know feature_id without a join.
                    // But we can just refresh current feature. It's safe.
                    await refreshModelsOnly();
                }
            )
            .on(
                // 2. Realtime Listener for NEW AI_MODEL_RUNS (History Updates)
                'postgres_changes',
                { event: 'INSERT', schema: 'public', table: 'ai_model_runs', filter: `user_id=eq.${user?.id}` },
                (payload) => {
                    // Check if this run is for the current feature
                    const newRun = payload.new;
                    if (newRun && (newRun.feature_code === slug || newRun.feature_code === featureConfig?.code)) {
                        // Invalidate history cache and refresh
                        const cacheKey = `history_${slug}_${user?.id}`;
                        delete MEMORY_CACHE[cacheKey];
                        CacheService.remove(cacheKey);

                        // Refresh history with force
                        fetchHistory(user?.id, featureConfig?.code || slug, true);
                    }
                }
            )
            .on(
                // 3. Realtime Listener for UPDATED AI_MODEL_RUNS (Status changes from processing -> success)
                'postgres_changes',
                { event: 'UPDATE', schema: 'public', table: 'ai_model_runs', filter: `user_id=eq.${user?.id}` },
                (payload) => {
                    const updatedRun = payload.new;
                    // If status changed to success/failed, refresh history
                    if (updatedRun && ['success', 'failed'].includes(updatedRun.status)) {
                        if (updatedRun.feature_code === slug || updatedRun.feature_code === featureConfig?.code) {
                            const cacheKey = `history_${slug}_${user?.id}`;
                            delete MEMORY_CACHE[cacheKey];
                            CacheService.remove(cacheKey);
                            fetchHistory(user?.id, featureConfig?.code || slug, true);
                        }
                        // Also clear processing job if it's done
                        checkProcessingStatus(user?.id);
                    }
                }
            )
            .subscribe();

        return () => {
            supabase.removeChannel(channel);
        };
    }, [slug, user?.id, featureConfig?.code]);

    // --- Fetch Fields (now only for when selectedModelId changes) ---
    useEffect(() => {
        if (!selectedModelId) {
            setModelFields([]);
            setInputs({});
            return;
        }
        const fetchFields = async () => {
            let data = allModelFields[selectedModelId];

            // ALWAYS FETCH FRESH DATA
            const { data: fetchedData } = await supabase
                .from("ai_model_fields")
                .select("*")
                .eq("model_id", selectedModelId)
                .order("sort_order", { ascending: true })
                .order("id", { ascending: true });

            if (fetchedData) {
                data = fetchedData;
                // Update cache
                setAllModelFields(prev => ({ ...prev, [selectedModelId]: data }));
            }

            setModelFields(data);

            // ✅ CONDITIONAL INPUT RESTORATION
            // 1. If Recreate: Restore from Cache (RAM)
            // 2. If Normal Switch: Preserve Current State (prevInputs)
            const isRecreate = location.state?.restoreModelKey || location.state?.restoreModelName;

            setInputs(prevInputs => {
                const initial = {};
                const cachedInputs = (isRecreate && user?.id) ? InputCache.restore(slug, user.id) : null;

                (data || []).forEach(f => {
                    // Helper to validate restored value
                    const isValidValue = (keyValue, type) => {
                        if (keyValue === null || keyValue === undefined) return false;
                        if (['textarea', 'file', 'image', 'audio', 'video', 'pdf', 'docx', 'excel', 'txt', 'file_multi'].includes(type)) {
                            const strVal = String(keyValue);
                            // Avoid numeric strings for non-numeric fields
                            if (strVal.length < 5 && /^\d+$/.test(strVal)) return false;

                            // Check arrays
                            if (Array.isArray(keyValue)) {
                                const hasNum = keyValue.some(v => String(v).length < 5 && /^\d+$/.test(String(v)));
                                if (hasNum) return false;
                            }
                            if (strVal === "undefined" || strVal === "null") return false;
                        }
                        return true;
                    };

                    if (f.type === 'choice_audio') {
                        initial[f.key] = "";
                        return;
                    }

                    let val = null;

                    // CASE A: RECREATE (Use Cache)
                    if (isRecreate) {
                        if (cachedInputs && f.key in cachedInputs) {
                            const cVal = cachedInputs[f.key];
                            if (isValidValue(cVal, f.type)) val = cVal;
                        }
                    }
                    // CASE B: NORMAL SWITCH (Preserve Previous)
                    else {
                        if (f.key in prevInputs) {
                            const prevVal = prevInputs[f.key];
                            if (isValidValue(prevVal, f.type)) val = prevVal;
                        }
                    }

                    // Fallback Defaults
                    if (val === null) {
                        let def = f.default_value || "";
                        if (f.type === 'slider') def = "1";
                        else if (f.type === 'file_multi') def = [];
                        else {
                            const isNumeric = /^\d+$/.test(String(def));
                            if (['textarea', 'text'].includes(f.type) && isNumeric) def = "";
                            else if (['file', 'image', 'audio', 'video', 'pdf', 'docx', 'excel', 'txt'].includes(f.type)) def = "";
                        }
                        val = def;
                    }

                    initial[f.key] = val;
                });
                return initial;
            });
        };
        fetchFields();
    }, [selectedModelId, location.state]);

    // --- Config Handlers ---
    const selectedModel = useMemo(() => models.find(m => m.id === selectedModelId), [models, selectedModelId]);

    useEffect(() => {
        if (!selectedModel) {
            setSelectedConfigs({});
            setTotalCredits(0);
            return;
        }

        // Initialize Defaults
        let configs = selectedModel.configuration_options;
        // Normalize: supports Array or { categories: [] }
        if (!Array.isArray(configs)) {
            if (configs && Array.isArray(configs.categories)) {
                configs = configs.categories;
            } else {
                configs = [];
            }
        }

        const initialSelections = {};

        configs.forEach(cat => {
            const defaultOpt = cat.options.find(o => o.is_default);
            if (defaultOpt) initialSelections[cat.id] = defaultOpt.id;
            else if (cat.options.length > 0) initialSelections[cat.id] = cat.options[0].id;
        });

        setSelectedConfigs(initialSelections);
    }, [selectedModel]);

    // Recalculate Credits whenever Model or Config changes
    useEffect(() => {
        if (!selectedModel) return;

        let total = parseInt(selectedModel.price_credits) || 0;
        let configs = selectedModel.configuration_options;
        // Normalize
        if (!Array.isArray(configs)) {
            if (configs && Array.isArray(configs.categories)) {
                configs = configs.categories;
            } else {
                configs = [];
            }
        }

        Object.entries(selectedConfigs).forEach(([catId, optId]) => {
            const cat = configs.find(c => c.id == catId);
            if (cat) {
                const opt = cat.options.find(o => o.id == optId);
                if (opt) {
                    total += (parseInt(opt.credit_cost) || 0);
                }
            }
        });

        // Apply Multiplier for "jumlah_gambar"
        if (inputs['jumlah_gambar']) {
            const multiplier = parseInt(inputs['jumlah_gambar']) || 1;
            total = total * multiplier;
        }

        setTotalCredits(total);
    }, [selectedModel, selectedConfigs, inputs['jumlah_gambar']]);


    // --- Handlers ---
    const handleInputChange = (key, value) => {
        setInputs(prev => {
            const updated = { ...prev, [key]: value };
            // Save to localStorage for persistence
            if (user?.id) {
                InputCache.save(slug, user.id, updated);
            }
            return updated;
        });
    };

    const handleConfigChange = (catId, optId) => {
        setSelectedConfigs(prev => ({ ...prev, [catId]: optId }));
    };

    const checkExt = (name) => {
        const ext = name.split('.').pop().toLowerCase();
        if (BLOCKED_EXTENSIONS.includes(ext)) {
            showToast(`File type .${ext} is not allowed for security reasons.`, 'error');
            return false;
        }
        return true;
    };

    const uploadFileToSupabase = async (file) => {
        // console.log("🚀 START Uploading file:", file.name);

        // 1. Determine Folder Name (User ID)
        // Using User ID for privacy/obfuscation
        const safeFolderName = user?.id || "anonymous";

        // Sanitize Filename
        const sanitizedFileName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');

        // Define Paths
        const folderPath = `${safeFolderName}`;
        const filePath = `${folderPath}/${sanitizedFileName}`;

        // console.log("📂 Checking Target Path:", filePath);

        try {
            // 2. Check for existence (Smart Skip)
            const { data: existingFiles } = await supabase.storage.from("asset-user").list(folderPath, {
                search: sanitizedFileName,
                limit: 1
            });

            const fileExists = existingFiles && existingFiles.length > 0 && existingFiles.find(f => f.name === sanitizedFileName);

            let publicUrl = null;

            if (fileExists) {
                // console.log("ℹ️ File already exists. Skipping upload. Using existing.");
                const { data } = supabase.storage.from("asset-user").getPublicUrl(filePath);
                publicUrl = data.publicUrl;
            } else {
                // 3. Upload if new
                // console.log("⬆️ Uploading new file...");
                const { data: uploadData, error } = await supabase.storage.from("asset-user").upload(filePath, file, {
                    upsert: false // Don't overwrite if we have logic to skip, but false is safer here
                });

                if (error) {
                    // Handle race condition: if uploaded between list() and upload()
                    if (error.message.includes('Duplicate') || error.statusCode === '409') {
                        // console.log("ℹ️ File uploaded concurrently. Using existing.");
                        const { data } = supabase.storage.from("asset-user").getPublicUrl(filePath);
                        publicUrl = data.publicUrl;
                    } else {
                        console.error("❌ Upload Error:", error);
                        throw error;
                    }
                } else {
                    // console.log("✅ Upload Success:", uploadData);
                    const { data } = supabase.storage.from("asset-user").getPublicUrl(filePath);
                    publicUrl = data.publicUrl;
                }
            }

            // console.log("🔗 Public URL:", publicUrl);
            return publicUrl;
        } catch (err) {
            console.error("❌ Exception during upload:", err);
            throw err;
        }
    };

    const handleLocalFileSelect = async (file, key) => {
        if (!file) return;
        if (!checkExt(file.name)) return;

        // Validate File Size
        const field = modelFields.find(f => f.key === key);
        // Prioritize: Field Options Limit -> Global Limit? -> Default 10MB
        let maxMB = 10;

        // Safely parse options if string
        let fieldOptions = field?.options;
        if (typeof fieldOptions === 'string') {
            try { fieldOptions = JSON.parse(fieldOptions); } catch { fieldOptions = {}; }
        }

        if (fieldOptions?.max_size_mb) maxMB = parseInt(fieldOptions.max_size_mb);
        else if (field?.extra_metadata?.max_size_mb) maxMB = parseInt(field.extra_metadata.max_size_mb); // Legacy support

        if (file.size > maxMB * 1024 * 1024) {
            showToast(`File terlalu besar (Maks ${maxMB}MB).`, 'error');
            return;
        }

        // Set placeholder first (optional, but good for UI response) or just go to upload
        // showToast("Mengupload...", "loading"); // Need appropriate toast library support
        const tempToast = document.createElement('div'); // Naive visual indicator if needed, or rely on console for now.

        // Set loading state
        setUploadingFields(prev => ({ ...prev, [key]: true }));

        try {
            // IMMEDIATE UPLOAD
            // Add artificial delay for UX (so user sees the loading state clearly)
            await new Promise(resolve => setTimeout(resolve, 1500));

            const publicUrl = await uploadFileToSupabase(file);
            handleInputChange(key, publicUrl);
        } catch (e) {
            showToast("Gagal upload file.", "error");
        } finally {
            setUploadingFields(prev => ({ ...prev, [key]: false }));
        }
    };

    const handleFileUpload = async (e, key) => {
        const file = e.target.files?.[0];
        await handleLocalFileSelect(file, key); // Await to ensure processing starts
        // FIX: Reset input value so same file can be selected again immediately if needed
        e.target.value = '';
    };

    const getFileIconAndColor = (name) => {
        const ext = name.split('.').pop().toLowerCase();
        if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) return { Icon: Icons.Image, color: 'bg-sky-50 text-sky-700 border-sky-200' };
        if (['pdf'].includes(ext)) return { Icon: Icons.FileText, color: 'bg-rose-50 text-rose-700 border-rose-200' };
        if (['xls', 'xlsx', 'csv'].includes(ext)) return { Icon: Icons.FileSpreadsheet, color: 'bg-emerald-50 text-emerald-700 border-emerald-200' };
        if (['doc', 'docx'].includes(ext)) return { Icon: Icons.FileType2, color: 'bg-indigo-50 text-indigo-700 border-indigo-200' };
        if (['mp3', 'wav', 'ogg', 'm4a'].includes(ext)) return { Icon: Icons.Mic, color: 'bg-violet-50 text-violet-700 border-violet-200' };
        if (['mp4', 'mov', 'webm', 'mkv', 'avi', 'wmv', 'ts', 'm4v'].includes(ext)) return { Icon: Icons.Video, color: 'bg-orange-50 text-orange-700 border-orange-200' };
        return { Icon: Icons.File, color: 'bg-slate-100 text-slate-700 border-slate-200' };
    };

    const handleGlobalDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setInputs(prev => ({ ...prev, dragActive: false }));

        const files = Array.from(e.dataTransfer.files || []);
        if (files.length === 0) return;

        // 1. Identify Target Fields
        const multiField = modelFields.find(f => f.type === 'file_multi' && !f.is_hidden);

        // 2. Multi-file Logic
        if (files.length > 1) {
            if (multiField) {
                handleMultiFileUpload(multiField.key, files);
            } else {
                showToast("Fitur ini hanya mendukung 1 file upload.", "error");
                const firstFile = files[0];
                handleLocalFileSelect(firstFile, modelFields.find(f => f.type === 'file' && !f.is_hidden)?.key);
            }
            return;
        }

        // 3. Single-file Logic (Smart Match)

        const file = files[0];
        const ext = file.name.split('.').pop().toLowerCase();
        let targetType = 'file';
        if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) targetType = 'image';
        else if (['mp3', 'wav', 'ogg', 'm4a'].includes(ext)) targetType = 'audio';
        else if (['mp4', 'mov', 'webm', 'mkv', 'avi', 'wmv'].includes(ext)) targetType = 'video';
        else if (['pdf'].includes(ext)) targetType = 'pdf';
        else if (['doc', 'docx'].includes(ext)) targetType = 'docx';
        else if (['xls', 'xlsx', 'csv'].includes(ext)) targetType = 'excel';
        else if (['txt'].includes(ext)) targetType = 'txt';

        // Priority: Multi Field (Accumulator) -> Exact Type -> Generic File Field
        let targetField = multiField;
        if (!targetField) targetField = modelFields.find(f => f.type === targetType && !f.is_hidden);
        if (!targetField) targetField = modelFields.find(f => f.type === 'file' && !f.is_hidden);

        if (targetField) {
            if (targetField.type === 'file_multi') handleMultiFileUpload(targetField.key, files);
            else handleLocalFileSelect(file, targetField.key);
        } else {
            showToast("Tidak ada field input yang cocok.", "error");
        }
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDrop = (e, key) => {
        e.preventDefault();
        e.stopPropagation();
        const files = Array.from(e.dataTransfer.files || []);
        if (files.length === 0) return;

        const field = modelFields.find(f => f.key === key);
        if (field?.type === 'file_multi') {
            handleMultiFileUpload(key, files);
        } else {
            handleLocalFileSelect(files[0], key);
        }
    };

    // --- Image Editor Handlers ---
    const handleEditAsset = (asset) => {
        setImageEditorAsset(asset);
        setShowAttachmentMenu(false);
    };

    const handleSaveEditedImage = async (blob) => {
        setIsSavingEditedImage(true);
        try {
            const fileName = `edited_${Date.now()}.png`;
            const file = new File([blob], fileName, { type: 'image/png' });

            const publicUrl = await uploadFileToSupabase(file);

            // Determine target field
            const multiField = modelFields.find(f => f.type === 'file_multi' && !f.is_hidden);
            let targetKey = multiField ? multiField.key : null;

            if (!targetKey) {
                const imageField = modelFields.find(f => f.type === 'image' && !f.is_hidden);
                if (imageField) targetKey = imageField.key;
            }

            if (!targetKey) {
                const fileField = modelFields.find(f => f.type === 'file' && !f.is_hidden);
                if (fileField) targetKey = fileField.key;
            }

            if (targetKey) {
                if (multiField && targetKey === multiField.key) {
                    setInputs(prev => {
                        const current = prev[targetKey] || [];
                        const currentArray = Array.isArray(current) ? current : (current ? [current] : []);
                        return { ...prev, [targetKey]: [...currentArray, publicUrl] };
                    });
                } else {
                    handleInputChange(targetKey, publicUrl);
                }
                showToast("Gambar berhasil disimpan!", "success");
            } else {
                showToast("Tidak ada field input yang cocok.", "warning");
            }
            setImageEditorAsset(null);
        } catch (error) {
            console.error("Failed to save edited image:", error);
            showToast("Gagal menyimpan gambar edit.", "error");
        } finally {
            setIsSavingEditedImage(false);
        }
    };

    // --- Modified Multi File Upload (Async) ---
    const handleMultiFileUpload = async (key, rawFiles) => {
        const files = Array.from(rawFiles || []);
        if (files.length === 0) return;

        // 1. Get Config for "file_multi" Field (The Driver)
        const multiField = modelFields.find(f => f.key === key);
        const maxTotalFiles = multiField?.default_value ? parseInt(multiField.default_value) : 5;

        // 2. Build Type Permissions & Limits Map from OTHER fields
        // Map: { 'image': 10, 'pdf': 5, ... } -> means Image is allowed (limit 10MB), PDF allowed (limit 5MB)
        const typeConfig = {};
        modelFields.forEach(f => {
            if (['image', 'video', 'audio', 'pdf', 'docx', 'excel', 'txt'].includes(f.type)) {
                let maxMB = 10; // System default if not set
                try {
                    const opts = typeof f.options === 'string' ? JSON.parse(f.options) : (f.options || {});
                    if (opts.max_size_mb) maxMB = parseInt(opts.max_size_mb);
                } catch (e) { }
                typeConfig[f.type] = maxMB;
            }
        });

        // 3. Current Count Check
        const currentFiles = inputs[key] || [];
        const currentCount = currentFiles.length;

        if (currentCount >= maxTotalFiles) {
            showToast(`Batas maksimal ${maxTotalFiles} file sudah tercapai.`, 'error');
            return;
        }

        const slotsAvailable = maxTotalFiles - currentCount;
        const filesToProcess = files.slice(0, slotsAvailable);

        if (files.length > slotsAvailable) {
            showToast(`Hanya ${slotsAvailable} file pertama yang akan diproses (Batas ${maxTotalFiles}).`, 'info');
        }

        const validFiles = [];

        // 4. Validate Each File
        for (const f of filesToProcess) {
            if (!checkExt(f.name)) continue; // Blocked extension check (global)

            // Determine Abstract Type
            const ext = f.name.split('.').pop().toLowerCase();
            let fileType = null;
            if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) fileType = 'image';
            else if (['mp4', 'mov', 'webm', 'mkv', 'avi', 'wmv'].includes(ext)) fileType = 'video';
            else if (['mp3', 'wav', 'ogg', 'm4a'].includes(ext)) fileType = 'audio';
            else if (['pdf'].includes(ext)) fileType = 'pdf';
            else if (['doc', 'docx'].includes(ext)) fileType = 'docx';
            else if (['xls', 'xlsx', 'csv'].includes(ext)) fileType = 'excel';
            else if (['txt'].includes(ext)) fileType = 'txt';

            // STRICT Check: Is this type allowed?
            if (!fileType || typeConfig[fileType] === undefined) {
                showToast(`Tipe file ${fileType || ext} tidak diizinkan oleh model ini.`, 'error');
                continue;
            }

            // Size Check
            const maxMB = typeConfig[fileType];
            if (f.size > maxMB * 1024 * 1024) {
                showToast(`File ${f.name} terlalu besar (Max ${maxMB}MB untuk ${fileType}).`, 'error');
                continue;
            }

            validFiles.push(f);
        }

        if (validFiles.length === 0) return;

        // ASYNC UPLOAD ALL
        setUploadingFields(prev => ({ ...prev, [key]: true }));
        try {
            // Add artificial delay for UX
            await new Promise(resolve => setTimeout(resolve, 1500));

            const uploadedUrls = await Promise.all(
                validFiles.map(async (file) => {
                    return await uploadFileToSupabase(file);
                })
            );

            // Append URLs to state
            setInputs(prev => ({ ...prev, [key]: [...(prev[key] || []), ...uploadedUrls] }));
        } catch (e) {
            showToast("Gagal mengupload beberapa file.", "error");
        } finally {
            setUploadingFields(prev => ({ ...prev, [key]: false }));
        }
    };

    const removeFile = (key, index) => {
        setInputs(prev => {
            const newFiles = [...(prev[key] || [])];
            newFiles.splice(index, 1);
            return { ...prev, [key]: newFiles };
        });
    };

    // === NEW: Chat Session Management Functions ===

    /**
     * Fetch list of chat sessions for the sidebar
     */
    const fetchChatSessions = async (userId, featureCode) => {
        if (!userId) return;
        // Only fetch if we are in chat mode? Or just fetch always when requested?
        try {
            const { data } = await supabase
                .from('chat_sessions')
                .select('*')
                .eq('user_id', userId)
                .eq('feature_code', featureCode || slug)
                .order('is_pinned', { ascending: false, nullsFirst: false }) // Sort pinned first
                .order('updated_at', { ascending: false });

            if (data) setChatSessions(data);
        } catch (error) {
            console.error('Error fetching chat sessions:', error);
        }
    };


    /**
     * Initialize chat session(Chatbox features only)
     * - Checks if session exists for this feature
     * - Creates new session if not found OR if forceNew is true
     * - Loads conversation history
     */
    const initSession = async (userId, forceNew = false, targetSessionId = null) => {
        // console.log('🎯 initSession() called, userId:', userId, 'slug:', slug, 'forceNew:', forceNew);

        if (!userId) {
            // console.log('  ❌ No user ID, aborting');
            return;
        }

        // OPTIMIZATION: If we are already on this session (via client-side switch), don't show loading spinner
        // This prevents the "Data -> Loading -> Data" flicker when URL changes
        const isSameSession = targetSessionId && currentSession?.public_id === targetSessionId;
        const hasLoadedRuns = sessionRuns.length > 0;

        // OPTIMIZATION: Check if this was a client-side click navigation
        // If so, we already showing cached data, so DO NOT show spinner/clear state
        if (location.state?.outputImage) {
            // console.log("  ✅ Client-side nav detected, skipping spinner.");
        }

        if (location.state?.currentSession && location.state.currentSession.feature_code === featureConfig.code) {
            // console.log("  ✅ Session already loaded (Client Switch), skipping spinner.");
            // We can still do a background refresh if needed, but for now just return or let it flow without loading state
            // Actually, we might want to ensure 'session' object is fully correct, so we can let it proceed but WITHOUT setting isLoadingSession(true)
            // But simpler is to likely just return? 
            // Let's let it run but conditionally set loading.
        } else {
            setIsLoadingSession(true);
        }

        try {
            let session;

            // 1. If NOT forcing new, try to find existing session
            if (!forceNew) {
                // console.log('  📊 Querying chat_sessions, user_id:', userId, 'feature_code:', slug);
                let query = supabase
                    .from('chat_sessions')
                    .select('*')
                    .eq('user_id', userId)
                    .eq('feature_code', slug);

                if (targetSessionId) {
                    // Assume targetSessionId from URL is 'public_id' (UUID)
                    query = query.eq('public_id', targetSessionId);
                } else {
                    query = query.order('updated_at', { ascending: false }).limit(1);
                }

                const { data: existingSessions, error: queryError } = await query;
                if (existingSessions && existingSessions.length > 0) {
                    session = existingSessions[0];
                    // console.log('Loaded existing session:', session.id);
                }
            }

            // 2. If forceNew OR no existing session found, Create 'Virtual' Session (Client only)
            if (!session) {
                // LAZY CREATION: Do NOT insert into DB yet. Just create a temporary object.
                session = {
                    id: `temp_${Date.now()}`,
                    user_id: userId,
                    feature_code: slug,
                    title: 'New Chat',
                    isTemp: true, // Marker for handleSubmit
                    created_at: new Date().toISOString()
                };
                // console.log('Created VIRTUAL session:', session.id);
            }

            // console.log('  🔄 setCurrentSession:', session);
            setCurrentSession(session);

            // 3. Load conversation history
            // If it's a real session, load runs. If temp, runs are empty.
            if (session.isTemp) {
                setSessionRuns([]);
            } else {
                await loadSessionRuns(session.id);
            }

            // 4. Update the sidebar list (only existing ones)
            await fetchChatSessions(userId, slug);

        } catch (error) {
            console.error('Error initializing session:', error);
            if (error.message?.includes('public_id') || error.message?.includes('does not exist')) {
                alert("DATABASE UPDATE REQUIRED: Please run the provided SQL migration to add 'public_id' column.");
            }
            showToast('Failed to load chat history', 'error');
        } finally {
            setIsLoadingSession(false);
        }
    };

    /**
     * Load all runs for a specific session
     * Uses both in-memory cache (sessionCacheRef) and localStorage cache (CacheService)
     */
    const loadSessionRuns = async (sessionId) => {
        if (!sessionId) return;

        const cacheKey = `session_runs_${sessionId}`;

        try {
            // 1. Check in-memory cache first (fastest)
            if (sessionCacheRef.current && sessionCacheRef.current[sessionId]) {
                setSessionRuns(sessionCacheRef.current[sessionId]);
                // Still fetch in background for freshness
            }
            // 2. Check localStorage cache (persistent across refreshes)
            else {
                const cachedRuns = CacheService.get(cacheKey);
                if (cachedRuns && cachedRuns.length > 0) {
                    setSessionRuns(cachedRuns);
                    // Also populate in-memory cache
                    if (sessionCacheRef.current) {
                        sessionCacheRef.current[sessionId] = cachedRuns;
                    }
                    // Still fetch fresh data in background
                }
            }

            // 3. Fetch fresh data from database
            const { data: runs, error } = await supabase
                .from('ai_model_runs')
                .select('*')
                .eq('session_id', sessionId)
                .order('created_at', { ascending: false }) // Latest first to get meaningful data
                .limit(50); // Limit to 50 items

            if (error) throw error;

            // Restore Chronological Order for State (Oldest -> Newest)
            if (runs) runs.reverse();

            const finalRuns = runs || [];

            // Merge Optimistic Runs from Navigation State (Preserves "Thinking..." state on new session redirect)
            if (location.state?.optimisticRuns) {
                location.state.optimisticRuns.forEach(opt => {
                    const idExists = finalRuns.some(r => r.id === opt.id);
                    // Check if a real run with same prompt already exists (Avoiding stuck "Processing" bubble)
                    const contentExists = finalRuns.some(r =>
                        !String(r.id).startsWith('opt_') &&
                        r.inputs?.prompt === opt.inputs?.prompt
                    );

                    if (!idExists && !contentExists) {
                        finalRuns.push(opt);
                    }
                });
            }

            // Save to both caches for instant switching next time
            if (sessionCacheRef.current) {
                sessionCacheRef.current[sessionId] = finalRuns;
            }
            // Save to localStorage with 30 minute expiry
            CacheService.set(cacheKey, finalRuns, 30);

            setSessionRuns(finalRuns);
            // console.log(`Loaded ${finalRuns.length} messages in session`);

            // --- Restore Model from History ---
            if (runs && runs.length > 0) {
                const lastRun = runs[runs.length - 1];
                // Assuming 'model' column stores the model key (e.g., 'gpt-4o')
                // We need to find the model in our 'models' list that matches this key
                if (lastRun.model) {
                    // Check if we already have the full model object to avoid partial state
                    // We only switch if it's currently available in the list
                    const restoredModel = models.find(m => m.key === lastRun.model);
                    if (restoredModel && restoredModel.id !== selectedModelId) {
                        // console.log(`Restoring model from history: ${restoredModel.name}`);
                        setSelectedModelId(restoredModel.id);
                        // Also broadcast change
                        window.dispatchEvent(new CustomEvent('neo-model-change', { detail: { modelId: restoredModel.id } }));
                    }
                }
            }

        } catch (error) {
            console.error('Error loading session runs:', error);
        }
    };

    /**
     * Load runs for Side-by-Side view
     */
    const loadSideBySideRuns = async (sessionId) => {
        if (!sessionId) {
            setSideBySideRuns([]);
            return;
        }

        setIsLoadingSideBySide(true);
        try {
            const { data: runs, error } = await supabase
                .from('ai_model_runs')
                .select('*')
                .eq('session_id', sessionId)
                .order('created_at', { ascending: false })
                .limit(50);

            if (error) throw error;

            if (runs) runs.reverse();
            setSideBySideRuns(runs || []);
        } catch (error) {
            console.error('Error loading side-by-side runs:', error);
        } finally {
            setIsLoadingSideBySide(false);
        }
    };

    // Effect to handle Side-by-Side loading changes
    useEffect(() => {
        if (sideBySideSessionId) {
            loadSideBySideRuns(sideBySideSessionId);
            localStorage.setItem('neo_side_by_side_id', sideBySideSessionId);
        } else {
            setSideBySideRuns([]);
            localStorage.removeItem('neo_side_by_side_id');
        }
    }, [sideBySideSessionId]);

    // --- FIX: Force refresh chat history when global processing job finishes ---
    const prevProcessingJobRef = useRef(null);
    useEffect(() => {
        // Detect transition from Job to No Job (Processing -> Done/Null)
        if (!processingJob && prevProcessingJobRef.current) {
            // console.log("Processing Job finished. Triggering refresh for chat...");

            // Check if feature is chatbox (isChatLayout is not defined yet here)
            const isChatFeature = featureConfig?.output_type === 'chatbox' || featureConfig?.output === 'chatbox' || featureConfig?.output_type?.includes('Chat Application');

            if (isChatFeature) {
                // If we have an active session, reload its messages to clear 'processing' state
                // Fallback to job's session_id if currentSession is null or TEMP (e.g. New Chat)
                const targetSessionId = (currentSession?.id && !currentSession.isTemp)
                    ? currentSession.id
                    : prevProcessingJobRef.current?.session_id;

                if (targetSessionId) {
                    loadSessionRuns(targetSessionId);
                }
                // Always refresh the list of sessions (to show new session on sidebar if needed)
                if (user?.id) fetchChatSessions(user.id, slug);
            }
        }
        prevProcessingJobRef.current = processingJob;
    }, [processingJob, featureConfig, currentSession, user?.id, slug]);

    // --- FIX: Aggressive Polling for Processing Items (Safety Net) ---
    // --- FIX: Aggressive Polling for Processing Items (Safety Net) ---
    useEffect(() => {
        let interval;
        const hasProcessing = sessionRuns.some(r => r.status === 'processing');
        // Check if feature is chatbox
        const isChatBoxFeature = featureConfig?.output_type === 'chatbox' || featureConfig?.output === 'chatbox';

        // Only pool if we have a valid, real session ID
        const targetSessionId = (currentSession?.id && !currentSession.isTemp) ? currentSession.id : null;

        if (isChatBoxFeature && hasProcessing && targetSessionId) {
            // Poll every 3 seconds
            interval = setInterval(() => {
                // console.log("Polling session for updates...", targetSessionId);
                loadSessionRuns(targetSessionId);
            }, 3000);
        }

        return () => clearInterval(interval);
    }, [sessionRuns, currentSession, featureConfig]);

    // --- Chat Session Actions ---
    const handlePinSession = async (e, session) => {
        e.stopPropagation();
        try {
            const newPinned = !session.is_pinned;
            const { error } = await supabase
                .from('chat_sessions')
                .update({ is_pinned: newPinned })
                .eq('id', session.id);

            if (error) throw error;
            showToast(newPinned ? "Chat disematkan" : "Sematan dilepas", "success");
            fetchChatSessions(user?.id, slug);
        } catch (err) {
            console.error("Pin failed", err);
            // If column doesn't exist, we might see error here.
            // alert("Gagal menyematkan chat. (Hubungi admin jika fitur ini belum aktif)");
        }
    };

    // Initial click on "Hapus" from menu -> Opens Modal
    const handleDeleteSession = (e, session) => {
        e.stopPropagation(); // Menu click
        setSessionToDelete(session);
        setOpenMenuSessionId(null); // Close menu
    };

    // Confirmed action from Modal
    const confirmDeleteSession = async () => {
        if (!sessionToDelete) return;
        const sessionId = sessionToDelete.id;

        try {
            const { error } = await supabase
                .from('chat_sessions')
                .delete()
                .eq('id', sessionId);

            if (error) throw error;

            showToast("Chat dihapus", "success");

            // If deleting current, reset
            if (currentSession?.id === sessionId) {
                setSessionRuns([]);
                setCurrentSession(null);
                setGeneratedOutput(null);
            }
            fetchChatSessions(user?.id, slug);
        } catch (err) {
            console.error("Delete failed", err);
            showToast("Gagal menghapus chat", "error");
        } finally {
            setSessionToDelete(null); // Close Modal
        }
    };

    const handleStartRename = (e, session) => {
        e.stopPropagation();
        setEditingSessionId(session.id);
        setEditTitle(session.title || "");
    };

    const handleSaveTitle = async (sessionId) => {
        if (!editTitle.trim()) return handleCancelEdit();

        try {
            const { error } = await supabase
                .from('chat_sessions')
                .update({ title: editTitle })
                .eq('id', sessionId);

            if (error) throw error;
            fetchChatSessions(user?.id, slug);

            if (currentSession?.id === sessionId) {
                setCurrentSession(prev => ({ ...prev, title: editTitle }));
            }
            setEditingSessionId(null);
        } catch (err) {
            console.error("Rename failed", err);
            showToast("Gagal mengganti judul", "error");
        }
    };

    const handleCancelEdit = () => {
        setEditingSessionId(null);
        setEditTitle("");
    };

    // Placeholder to keeping code structure if needed, or we just rely on handleStartRename
    const handleRenameSession = async (e, session) => {
        handleStartRename(e, session);
    };

    /**
     * Update session title from first prompt (Auto)
     */
    const updateSessionTitle = async (sessionId, firstPrompt) => {
        if (!sessionId || !firstPrompt) return;

        try {
            // Logic: Take first sentence or up to 60 chars
            let title = firstPrompt.trim();
            // Split by sentence delimiters (. ! ?)
            const sentences = title.split(/[\.!\?\n]/);
            if (sentences.length > 0 && sentences[0].trim().length > 0) {
                title = sentences[0].trim();
            }

            // Hard limit to 60 chars
            if (title.length > 60) {
                title = title.substring(0, 60) + '...';
            }

            // Update Database
            await supabase
                .from('chat_sessions')
                .update({ title })
                .eq('id', sessionId);

            // Update State (Current)
            setCurrentSession(prev => prev ? { ...prev, title } : null);

            // OPTIMISTIC UPDATE: Sidebar
            setChatSessions(prev => prev.map(s => s.id === sessionId ? { ...s, title } : s));

        } catch (error) {
            console.error('Error updating session title:', error);
        } finally {
            // Refresh list in sidebar (to ensure sync)
            fetchChatSessions(user?.id, slug);
        }
    };


    const handleEnhancePrompt = async (fieldKey) => {
        const currentPrompt = inputs[fieldKey] || "";
        const img1 = inputs.image_url;
        const img2 = inputs.image_url_2;

        // if (!img1 && !img2) return; // Allow text-only enhancement

        setIsMagicLoading(true);
        try {
            // WEBHOOK URL for Prompt Enhancer
            const response = await fetch("https://n8n-teoxahs8xdqn.blueberry.sumopod.my.id/webhook/magic-promt", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    current_prompt: currentPrompt,
                    image_url: img1,
                    image_url_2: img2
                })
            });

            if (!response.ok) throw new Error("Enhancement failed");

            const text = await response.text();
            handleInputChange(fieldKey, text);

        } catch (err) {
            console.error(err);
            alert("Failed to enhance prompt. Please try again.");
        } finally {
            setIsMagicLoading(false);
        }
    };

    // --- Delete All Chats Handler ---
    // --- Delete All Chats Handler ---
    const openDeleteModal = () => {
        // console.log("Opening Delete Modal...");
        setShowDeleteConfirm(true);
    };

    const confirmDeleteAllChats = async () => {
        setShowDeleteConfirm(false);

        try {
            // Delete from DB
            const { error } = await supabase
                .from('chat_sessions')
                .delete()
                .eq('user_id', user.id) // Ensure secure delete
                .eq('feature_code', slug);

            if (error) throw error;

            // Clear State
            setChatSessions([]);
            setSessionRuns([]);
            setCurrentSession(null);

            // Clear Cache
            if (sessionCacheRef.current) sessionCacheRef.current = {};

            // Navigate to root (new chat)
            navigate(`/dashboard/feature/${slug}`);

            showToast("Semua riwayat berhasil dihapus.", "success");
        } catch (err) {
            console.error("Delete all error:", err);
            showToast("Gagal menghapus riwayat.", "error");
        }
    };

    // --- New Chat Handler (Smart Reuse) ---
    const handleNewChat = async () => {
        // Clear URL - Navigate to base slug
        navigate(`/dashboard/feature/${slug}`);

        // 1. If currently in an empty session, do nothing (stay here)
        if (currentSession && sessionRuns.length === 0) {
            // console.log("Current session is already empty. Staying.");
            return;
        }

        setIsLoadingSession(true);
        try {
            // 2. Check if the MOST RECENT session in history is empty
            // We fetch the latest session for this feature
            const { data: latestSessions } = await supabase
                .from('chat_sessions')
                .select('id, title, created_at')
                .eq('user_id', user.id)
                .eq('feature_code', slug)
                .order('created_at', { ascending: false })
                .limit(1);

            if (latestSessions && latestSessions.length > 0) {
                const latest = latestSessions[0];

                // Check run count for this latest session
                const { count } = await supabase
                    .from('ai_model_runs')
                    .select('*', { count: 'exact', head: true })
                    .eq('session_id', latest.id);

                if (count === 0) {
                    // Reuse this session!
                    // console.log("Reusing recent empty session:", latest.id);
                    setCurrentSession(latest);
                    setSessionRuns([]); // It's empty

                    // Touch updated_at to move it to top if sorted by updated
                    await supabase
                        .from('chat_sessions')
                        .update({ updated_at: new Date().toISOString() })
                        .eq('id', latest.id);

                    await fetchChatSessions(user.id, slug);
                    setIsLoadingSession(false);
                    return;
                }
            }

            // 3. Create NEW session
            await initSession(user.id, true);
        } catch (e) {
            console.error("Error in handleNewChat", e);
            setIsLoadingSession(false);
        }
    };

    // --- Model Selection Handler (Intercept for Chat Mode) ---
    const handleModelSelect = (id) => {
        // Determine if this is a chat feature based on config
        const isChatFeature = featureConfig?.output_type === 'chatbox';

        // If in Chat Mode AND we have an active session with messages
        // AND the user is selecting a DIFFERENT model
        if (isChatFeature && sessionRuns.length > 0 && id !== selectedModelId) {
            // Check preference
            const savedPref = localStorage.getItem('neo_skip_model_confirm');

            if (savedPref === 'true') {
                // Skip modal, just switch and new chat
                setSelectedModelId(id);
                navigate(location.pathname, { state: { ...location.state, selectedModelId: id }, replace: true }); // Sync Sidebar
                window.dispatchEvent(new CustomEvent('neo-model-change', { detail: { modelId: id } }));
                handleNewChat();
            } else {
                setPendingModelId(id);
                setShowSwitchModelModal(true);
            }
            setShowModelSelector(false); // Close selector
        } else {
            // Normal update
            setSelectedModelId(id);
            navigate(location.pathname, { state: { ...location.state, selectedModelId: id }, replace: true }); // Sync Sidebar
            window.dispatchEvent(new CustomEvent('neo-model-change', { detail: { modelId: id } }));

            // Clear inputs that are not valid for the new model (e.g. video in file_multi when new model doesn't support video)
            // We can't do this synchronously here because 'modelFields' won't update until next render.
            // But we can trigger a cleanup.
            setShowModelSelector(false);
        }
    };

    // --- Model Switch Cleanup Effect ---
    // REMOVED: User requested full clear of inputs on switch, so we don't need partial cleanup.
    /*
    useEffect(() => {
        // ... (logic removed)
    }, [selectedModel, modelFields]); 
    */

    const confirmSwitchModel = () => {
        if (pendingModelId) {
            setSelectedModelId(pendingModelId);
            navigate(location.pathname, { state: { ...location.state, selectedModelId: pendingModelId }, replace: true }); // Sync Sidebar
            window.dispatchEvent(new CustomEvent('neo-model-change', { detail: { modelId: pendingModelId } }));
            handleNewChat(); // Start new chat

            if (dontAskSwitchModel) {
                localStorage.setItem('neo_skip_model_confirm', 'true');
            }
        }
        setShowSwitchModelModal(false);
        setPendingModelId(null);
    };

    // --- Chat Send Handler (from Modal) ---
    const handleChatSend = () => {
        if (!chatInput.trim() || isSubmitting) return;

        // Get current inputs (which might contain images) and merge with prompt
        const currentInputs = { ...inputs, prompt: chatInput };

        // Temporarily set inputs to this merged state so handleSubmit can read it (if it reads from state)
        // But better yet, if we could pass it directly... but handleSubmit reads 'inputs'.
        setInputs(currentInputs);

        // Clear chat input UI immediately
        setChatInput("");

        // Trigger form submit (will use chat mode logic in handleSubmit)
        const fakeEvent = { preventDefault: () => { }, stopPropagation: () => { } };

        // We need to wait for setInputs to propagate, or modify handleSubmit to accept overrides.
        // For now, let's assume standard React batching. 
        // But to be safe, we should clear inputs AFTER submit starts.

        handleSubmit(fakeEvent);

        // CLEAR INPUTS (Images, Files) DELAYED slightly to ensure handleSubmit picked them up
        // Or better, let handleSubmit clear them if it's chat mode.
        // For now, we'll confirm clear here but inside a timeout or just let handleSubmit do it?
        // Actually, handleSubmit logic below doesn't clear inputs.

        setTimeout(() => {
            setInputs({}); // Wipe everything clean for next chat
        }, 100);
    };

    // --- Refs ---
    const slugRef = useRef(slug);
    useEffect(() => { slugRef.current = slug; }, [slug]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!selectedModel || isSubmitting) return;

        // --- Validation ---
        for (const field of modelFields) {
            if (field.is_required) {
                const value = inputs[field.key];
                // Check for empty array (multi-file) or falsy value (others)
                // Note: For numbers, 0 might be valid, but here inputs are usually strings.
                // If value is null/undefined or empty string or empty array.
                const isEmpty = Array.isArray(value) ? value.length === 0 : (!value && value !== 0);

                if (isEmpty) {
                    alert(`Mohon isi kolom "${field.label}" terlebih dahulu.`);
                    return;
                }
            }
        }

        const initiatingSlug = slug;
        const initiatingModelName = selectedModel.name;

        setIsSubmitting(true);
        setSubmitError("");
        setPinnedHistoryItem(null); // Unpin any selected item on new generation
        setSubmitSuccess("");

        // --- Auto-Upload Pending Files ---
        // Clone inputs to avoid mutating state directly/prematurely if upload fails?
        // We will pass `finalInputs` to the API.
        const finalInputs = { ...inputs };
        try {
            for (const key of Object.keys(finalInputs)) {
                const val = finalInputs[key];
                if (val instanceof File) {
                    finalInputs[key] = await uploadFileToSupabase(val);
                } else if (Array.isArray(val)) {
                    const newArr = [];
                    for (const item of val) {
                        if (item instanceof File) newArr.push(await uploadFileToSupabase(item));
                        else newArr.push(item);
                    }
                    finalInputs[key] = newArr;
                }
            }
        } catch (e) {
            console.error("Upload error:", e);
            alert("Gagal mengupload file: " + e.message);
            setIsSubmitting(false);
            return;
        }

        // Update state to show URLs (optional but good for UI consistency if generation fails later)
        setInputs(finalInputs);

        // --- CLEANUP: Remove default values for File/Media fields if they look like limits ---
        // This prevents sending "1" or "5" (limits) as values when no file is uploaded.
        modelFields.forEach(field => {
            const val = finalInputs[field.key];
            const type = (field.type || "").toLowerCase();

            // 1. Button Group Cleanup: Remove if empty
            if (type === 'button_group') {
                if (!val || val === "") {
                    delete finalInputs[field.key];
                }
            }

            if (!val) return;

            // Broad check for file/media types
            const isFileType = ['file', 'image', 'audio', 'video', 'file_multi', 'pdf', 'document'].includes(type) || type.includes('file');

            if (isFileType) {
                // Check if value is a "limit number" (digits only) or matches default_value
                const isLimitValue = typeof val === 'string' && /^\d+$/.test(val);
                const matchesDefault = field.default_value && String(val) === String(field.default_value);

                if (isLimitValue || matchesDefault) {
                    // Ensure it's not a valid URL
                    const isUrl = typeof val === 'string' && (val.includes('http') || val.includes('data:'));
                    if (!isUrl) {
                        delete finalInputs[field.key];
                    }
                }
            }
        });

        // === NEW: Prepare session metadata for chatbox ===
        let sessionMetadata = {};
        let activeSessionId = null; // Defined in outer scope

        // Check if this is a chatbox feature
        const isChatboxForSubmit = featureConfig?.output_type === 'chatbox';

        if (isChatboxForSubmit && currentSession) {

            // --- LAZY SESSION CREATION START ---
            activeSessionId = currentSession.id;

            if (currentSession.isTemp) {
                // console.log("Lazy Creation: Creating REAL session now...");
                try {
                    const { data: realSession, error: createError } = await supabase
                        .from('chat_sessions')
                        .insert({
                            user_id: user.id,
                            feature_code: slug,
                            title: 'New Chat',
                            // Add pinned logic if relevant? Default is false.
                        })
                        .select()
                        .single();

                    if (createError) throw createError;

                    // Update State
                    activeSessionId = realSession.id;
                    const updatedSession = { ...realSession, isTemp: false };
                    setCurrentSession(updatedSession);

                    // Add to sidebar
                    // Add to sidebar
                    setChatSessions(prev => [updatedSession, ...prev]);

                    // Construct Optimistic Run for Navigation (Safety for transition)
                    const navOptimisticRun = {
                        id: `opt_${Date.now()}`,
                        status: 'processing',
                        inputs: finalInputs,
                        created_at: new Date().toISOString(),
                        session_id: activeSessionId,
                        user_id: user.id,
                        feature_code: slug
                    };

                    // PERSIST URL with UUID
                    if (realSession.public_id) {
                        navigate(`/dashboard/feature/${slug}/${realSession.public_id}`, {
                            replace: true,
                            state: { optimisticRuns: [navOptimisticRun] }
                        });
                    }

                } catch (err) {
                    console.error("Failed to create real session:", err);
                    alert("Gagal memulai sesi baru. Silakan coba lagi.");
                    setIsSubmitting(false);
                    return;
                }
            }
            // --- LAZY SESSION CREATION END ---

            // Get last run in this session for context chaining
            const lastRun = sessionRuns.length > 0
                ? sessionRuns[sessionRuns.length - 1]
                : null;

            sessionMetadata = {
                _session_id: activeSessionId,
                _parent_run_id: lastRun?.id || null,
                _context: lastRun ? {
                    previous_result: lastRun.result,
                    previous_prompt: lastRun.inputs?.prompt || ''
                } : null
            };

            // --- OPTIMISTIC UI UPDATE ---
            const optimisticRun = {
                id: `opt_${Date.now()}`,
                status: 'processing',
                inputs: finalInputs,
                created_at: new Date().toISOString(),
                session_id: activeSessionId,
                user_id: user.id,
                feature_code: slug
            };
            setSessionRuns(prev => [...prev, optimisticRun]);

            // IMMEDIATE UI CLEAR (Optimistic)
            setInputs(prev => {
                const nextIds = { ...prev, prompt: "", message: "" };
                modelFields.forEach(f => {
                    if (['file', 'image', 'audio', 'video', 'file_multi', 'pdf'].includes(f.type) || f.key.includes('image') || f.key.includes('file')) {
                        if (f.type === 'file_multi') nextIds[f.key] = [];
                        else nextIds[f.key] = "";
                    }
                });
                return nextIds;
            });

            // console.log('DEBUG SESSION METADATA:', sessionMetadata);
            // console.log('DEBUG currentSession:', currentSession);
            // console.log('DEBUG sessionRuns:', sessionRuns);

            // Auto-generate session title from first prompt
            if (activeSessionId && sessionRuns.length === 0 && finalInputs.prompt) {
                updateSessionTitle(activeSessionId, finalInputs.prompt);
            } else if (activeSessionId) {
                // Manually touch updated_at to bring to top
                supabase.from('chat_sessions').update({ updated_at: new Date().toISOString() }).eq('id', activeSessionId).then(() => {
                    fetchChatSessions(user.id, slug);
                });
            }
        } else {
            // console.log('DEBUG: NOT creating session metadata');
            // console.log('isChatboxForSubmit:', isChatboxForSubmit);
            // console.log('currentSession:', currentSession);
        }

        try {
            const { data, error } = await supabase.functions.invoke("run-universal-model", {
                body: {
                    featureSlug: slug,
                    modelKey: selectedModel.key,
                    session_id: isChatboxForSubmit ? activeSessionId : undefined, // Use updated ID
                    inputs: {
                        ...finalInputs,
                        _user_name: profile?.full_name || user?.user_metadata?.full_name || user?.email || "User",
                        ...sessionMetadata, // ← NEW: Session metadata for chatbox
                        // Add pinned conversation context if available in Chat Mode
                        ...(isChatMode && pinnedConversation && {
                            conversation_context: pinnedConversation
                        }),
                        ...(() => {
                            // Merge Config Values
                            let configValues = {};
                            // Normalize configs
                            let configs = selectedModel.configuration_options;
                            if (!Array.isArray(configs)) {
                                if (configs && Array.isArray(configs.categories)) {
                                    configs = configs.categories;
                                } else {
                                    configs = [];
                                }
                            }

                            Object.entries(selectedConfigs).forEach(([catId, optId]) => {
                                const cat = configs.find(c => c.id == catId);
                                if (cat) {
                                    const opt = cat.options.find(o => o.id == optId);
                                    if (opt && opt.values) {
                                        configValues = { ...configValues, ...opt.values };
                                    }
                                }
                            });
                            return configValues;
                        })()
                    },
                    configSelections: selectedConfigs
                }
            });

            if (error) throw error;
            if (data.error) throw new Error(data.error);

            if (slugRef.current === initiatingSlug) {
                // --- Chat Mode: Add AI response to history ---
                if (isChatMode) {
                    // Clear input field in chat mode
                    setInputs(prev => {
                        const nextIds = { ...prev, prompt: "", message: "" };

                        // Clear all file/media fields to prevent "ghost" attachments
                        modelFields.forEach(f => {
                            if (['file', 'image', 'audio', 'video', 'file_multi', 'pdf'].includes(f.type) || f.key.includes('image') || f.key.includes('file')) {
                                if (f.type === 'file_multi') nextIds[f.key] = [];
                                else nextIds[f.key] = "";
                            }
                        });
                        return nextIds;
                    });
                    setSubmitSuccess("Berhasil generate! Silakan pin hasilnya jika ingin digunakan sebagai context.");
                } else if (isChatboxForSubmit && data.run_id) {
                    // Start Polling for this job (Backup for Realtime)
                    checkProcessingStatus();

                    // Update the Optimistic run with the Real ID from server
                    const processingRun = {
                        id: data.run_id,
                        status: 'processing',
                        inputs: finalInputs,
                        created_at: new Date().toISOString(),
                        session_id: activeSessionId || currentSession?.id
                    };

                    setSessionRuns(prev => {
                        // Remove previous optimistic run (opt_*) and replace with Real ID
                        const cleanPrev = prev.filter(r => !r.id.toString().startsWith('opt_'));
                        if (cleanPrev.some(r => r.id === data.run_id)) return cleanPrev;
                        return [...cleanPrev, processingRun];
                    });

                    // Clear inputs for next message
                    setInputs(prev => {
                        const nextIds = { ...prev, prompt: "", message: "" };

                        // Clear all file/media fields
                        modelFields.forEach(f => {
                            if (['file', 'image', 'audio', 'video', 'file_multi', 'pdf'].includes(f.type) || f.key.includes('image') || f.key.includes('file')) {
                                if (f.type === 'file_multi') nextIds[f.key] = [];
                                else nextIds[f.key] = "";
                            }
                        });
                        return nextIds;
                    });
                } else {
                    // Normal mode: show result
                    if (data.status === 'processing') {
                        // IMMEDIATE UI LOCK: Set processingJob directly from response
                        setProcessingJob({
                            id: data.run_id,
                            feature_code: slug,
                            feature_name: featureConfig?.name,
                            status: 'processing'
                        });

                        // Clear success message (User doesn't want the green box)
                        setSubmitSuccess("");
                        setGeneratedOutput(null); // Ensure result area shows processing overlay
                    } else {
                        setGeneratedOutput(data.result);
                        // Note: Success message will be shown AFTER fetchHistory completes
                    }
                }

                setHasGenerated(true);
                // Note: setIsSubmitting will be called AFTER fetchHistory completes
            }
            // else: silently fail

            if (data.new_credit_balance !== undefined) {
                setProfile(prev => {
                    const newProfile = { ...prev, credit_balance: data.new_credit_balance };
                    CacheService.set('user_profile', newProfile, 60); // Update cache
                    window.dispatchEvent(new CustomEvent('profile-updated', { detail: newProfile })); // Notify Layout
                    return newProfile;
                });
            }

            // Await fetchHistory before hiding loading indicator
            // This ensures new items are in the history before optimistic card disappears
            if (slugRef.current === initiatingSlug) {
                await fetchHistory(user.id, featureConfig.code || slug);
            }

            // NOW show success message and hide loading indicator (after history is updated)
            // Only show success if we got immediate result (not processing status)
            if (data.status !== 'processing' && data.ok) {
                setSubmitSuccess("Generate berhasil, kredit sudah berkurang!");
            }
            setIsSubmitting(false);

        } catch (err) {
            // Restore inputs on error
            setInputs(finalInputs);

            // Check for Concurrency/Processing Error (429 or specific message)
            // If matches, we intentionally LEAVE isSubmitting(true) to simulate queuing/joining
            const isConcurrency = err.message?.includes("sedang di proses") || err.status === 429 || (err.context && err.context.response && err.context.response.status === 429);

            if (isConcurrency) {
                // console.log("Concurrency limit hit, UI will remain in processing state.");
                // Optionally refresh history to catch the result when it appears
                fetchHistory(user.id, featureConfig.code || slug);
                // DO NOT setIsSubmitting(false)
            } else {
                // Normal Error Handling
                if (slugRef.current === initiatingSlug) {
                    const isTimeout = err.message.includes("<!DOCTYPE") || err.message.includes("<html");
                    setSubmitError(isTimeout
                        ? "Generate gagal (Request Timeout), kredit tidak berkurang."
                        : (err.message || "Generate gagal, kredit tidak berkurang."));
                } else {
                    console.error("Background Generation Failed:", err);
                }
                setIsSubmitting(false); // Failure: Stop loading
            }

        }
        // Removed finally block to allow conditional loading state
    };

    // --- Background Notification State ---
    const [backgroundNotification, setBackgroundNotification] = useState(null);

    // --- Polling for Processing Job ---
    useEffect(() => {
        if (!processingJob) return;

        const intervalId = setInterval(async () => {
            try {
                const { data: runData, error } = await supabase
                    .from("ai_model_runs")
                    .select("id, status, result, credits_charged, feature_code")
                    .eq("id", processingJob.id)
                    .single();

                if (error || !runData) return;

                if (runData.status === 'success' || runData.status === 'failed') {
                    clearInterval(intervalId);

                    // CASE 1: On the same page
                    if (slugRef.current === runData.feature_code) {
                        if (runData.status === 'success') {
                            setGeneratedOutput(runData.result);
                            const { data: profileData } = await supabase.from("profiles").select("*").eq("id", user?.id).single();
                            if (profileData) setProfile(profileData);
                        } else {
                            setSubmitError(runData.result?.error || "Generate gagal, kredit tidak berkurang.");
                        }
                        // Await fetchHistory before showing success message
                        await fetchHistory(user?.id, runData.feature_code);

                        // NOW show success message (after history is updated)
                        if (runData.status === 'success') {
                            setSubmitSuccess("Generate berhasil, kredit sudah berkurang!");
                        }
                    }
                    // CASE 2: On a different page (Show Button Notification)
                    else {
                        // Fetch friendly name for the feature
                        let featureName = runData.feature_code;
                        try {
                            const { data: featureData } = await supabase
                                .from('app_features')
                                .select('name')
                                .eq('slug', runData.feature_code)
                                .single();
                            if (featureData?.name) featureName = featureData.name;
                        } catch (e) {
                            // ignore, fallback to slug
                        }

                        const statusMsg = runData.status === 'success' ? "Berhasil!" : "Gagal!";
                        setBackgroundNotification({
                            message: `Generate in ${featureName} ${statusMsg}`,
                            type: runData.status,
                            countdown: 3
                        });

                        // Countdown logic handled by effect below

                        // Update credit balance in background
                        if (runData.status === 'success') {
                            const { data: p } = await supabase.from("profiles").select("*").eq("id", user?.id).single();
                            if (p) setProfile(p);
                        }
                    }

                    // Clear lock AFTER history is updated (ensures smooth transition)
                    setProcessingJob(null);
                }
            } catch (err) {
                console.error("Polling Error", err);
            }
        }, 3000);

        return () => clearInterval(intervalId);
    }, [processingJob, user]);

    // --- Background Notification Countdown ---
    useEffect(() => {
        if (backgroundNotification && backgroundNotification.countdown > 0) {
            const timer = setTimeout(() => {
                setBackgroundNotification(prev => ({ ...prev, countdown: prev.countdown - 1 }));
            }, 1000);
            return () => clearTimeout(timer);
        } else if (backgroundNotification && backgroundNotification.countdown <= 0) {
            setBackgroundNotification(null);
        }
    }, [backgroundNotification]);

    // --- Hydrate Feature Name for Processing Job ---
    useEffect(() => {
        const hydrateName = async () => {
            if (processingJob && !processingJob.feature_name && processingJob.feature_code) {
                try {
                    const { data } = await supabase
                        .from('app_features')
                        .select('name')
                        .eq('slug', processingJob.feature_code)
                        .single();

                    if (data?.name) {
                        setProcessingJob(prev => ({ ...prev, feature_name: data.name }));
                    }
                } catch (e) {
                    console.error("Failed to fetch feature name for job", e);
                }
            }
        };
        hydrateName();
    }, [processingJob?.feature_code]);




    if (loading) {
        return (
            <div className="h-screen w-full bg-slate-50 flex flex-col overflow-hidden animate-in fade-in duration-300">
                <div className="flex-1 overflow-hidden p-3 md:p-4 lg:p-6 relative z-10">
                    <div className="h-full max-w-[1600px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-6">

                        {/* LEFT: Hero Info */}
                        <div className="hidden lg:flex lg:col-span-4 xl:col-span-3 flex-col bg-white rounded-2xl border border-slate-200/80 p-6 h-full relative overflow-hidden shadow-sm">
                            <div className="space-y-6 animate-pulse z-10 relative">
                                <div className="space-y-3">
                                    <div className="h-3 w-16 bg-indigo-50 rounded-full" />
                                    <div className="h-8 w-48 bg-slate-800/5 rounded-lg" />
                                </div>
                                <div className="space-y-2 pt-2">
                                    <div className="h-3 w-full bg-slate-100 rounded" />
                                    <div className="h-3 w-11/12 bg-slate-100 rounded" />
                                    <div className="h-3 w-4/6 bg-slate-100 rounded" />
                                    <div className="h-3 w-5/6 bg-slate-100 rounded" />
                                </div>
                            </div>

                            {/* Decorative Stack Placeholder */}
                            <div className="absolute bottom-[-2rem] left-0 right-0 h-[45%] flex items-end justify-center opacity-70 pointer-events-none">
                                <div className="w-40 h-52 bg-slate-50/80 rounded-xl shadow-md transform -rotate-12 translate-x-4 border border-slate-100" />
                                <div className="w-40 h-52 bg-slate-100 rounded-xl shadow-lg transform rotate-6 -translate-x-4 absolute bottom-6 border border-slate-200" />
                            </div>
                        </div>

                        {/* CENTER: Form Area */}
                        <div className="col-span-1 lg:col-span-4 xl:col-span-5 h-full flex flex-col bg-white rounded-2xl border border-slate-200/80 overflow-hidden shadow-sm">
                            <div className="flex-1 overflow-y-auto p-5 md:p-6 space-y-8 scrollbar-none [&::-webkit-scrollbar]:hidden animate-pulse">
                                {/* Form Elements */}
                                <div className="space-y-2">
                                    <div className="h-3 w-20 bg-slate-200 rounded" />
                                    <div className="h-11 w-full bg-slate-50 border border-slate-100 rounded-lg" />
                                </div>

                                <div className="space-y-2">
                                    <div className="h-3 w-24 bg-slate-200 rounded" />
                                    <div className="h-40 w-full bg-slate-50 border-2 border-dashed border-slate-100 rounded-xl flex items-center justify-center">
                                        <div className="w-12 h-12 rounded-full bg-slate-100" />
                                    </div>
                                </div>

                                <div className="space-y-3">
                                    <div className="h-3 w-28 bg-slate-200 rounded" />
                                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                                        {[...Array(4)].map((_, i) => (
                                            <div key={i} className="h-9 bg-slate-50 rounded-lg border border-slate-100" />
                                        ))}
                                    </div>
                                </div>

                                <div className="space-y-3">
                                    <div className="h-3 w-28 bg-slate-200 rounded" />
                                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                                        {[...Array(6)].map((_, i) => (
                                            <div key={i} className="h-9 bg-slate-50 rounded-lg border border-slate-100" />
                                        ))}
                                    </div>
                                </div>
                            </div>

                            <div className="p-4 border-t border-slate-100 bg-white z-10">
                                <div className="h-12 w-full bg-sky-50 rounded-xl animate-pulse" />
                            </div>
                        </div>

                        {/* RIGHT: History Grid */}
                        <div className="lg:col-span-4 xl:col-span-4 flex flex-col bg-white rounded-2xl border border-slate-200/80 h-full overflow-hidden shadow-sm">
                            <div className="p-5 border-b border-slate-100 flex justify-between items-center bg-slate-50/30">
                                <div className="h-4 w-32 bg-slate-200 rounded animate-pulse" />
                                <div className="h-3 w-16 bg-slate-100 rounded animate-pulse" />
                            </div>
                            <div className="flex-1 p-5 overflow-y-auto scrollbar-none [&::-webkit-scrollbar]:hidden">
                                <div className="grid grid-cols-2 gap-3">
                                    {[...Array(6)].map((_, i) => (
                                        <div key={i} className="aspect-square bg-slate-100 rounded-xl animate-pulse mb-1" />
                                    ))}
                                </div>
                            </div>
                        </div>

                    </div>
                </div>
            </div>
        );
    }
    if (!featureConfig) return <div className="h-screen flex items-center justify-center text-slate-400">Fitur tidak ditemukan.</div>;

    const displayName = profile?.username || user?.email || "User";

    // --- Download Handler ---
    const handleDownload = async (url, filename) => {
        try {
            const response = await fetch(url);
            const blob = await response.blob();
            const blobUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = filename || 'download';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(blobUrl);
        } catch (error) {
            console.error("Download failed:", error);
            alert("Gagal mengunduh file.");
        }
    };

    // --- Pin Conversation (for Chat Mode) ---
    const handlePinConversation = async (e, item) => {
        e.stopPropagation();

        try {
            // Fetch .txt file content
            // Handle both raw item (from Modal) and history item (from sidebar) which has parsedResult
            let fileUrl = item.public_url || item.result;

            if (item.parsedResult) {
                fileUrl = item.parsedResult.public_url || item.parsedResult.result;
            }

            // If fileUrl is still an object (e.g. JSON), try to parse it
            if (typeof fileUrl === 'string' && fileUrl.startsWith('{')) {
                try {
                    const parsed = JSON.parse(fileUrl);
                    fileUrl = parsed.public_url || parsed.result;
                } catch (e) { /* ignore */ }
            }
            const response = await fetch(fileUrl);

            if (!response.ok) throw new Error('Failed to fetch file');

            const textContent = await response.text();
            setPinnedConversation(textContent);

            // Highlight this item (Persistent Hover Effect)
            setActiveMobileMenuId(item.uniqueId);

            // Success feedback - REMOVED ALERT as per user request
            // alert('Conversation pinned! Anda bisa edit atau gunakan sebagai context untuk generate selanjutnya.');
        } catch (error) {
            console.error('Pin conversation failed:', error);
            // alert('Gagal memuat conversation. File mungkin sudah tidak tersedia.');
        }
    };

    // --- Delete History Item ---
    // --- Delete History Item (Granular) ---
    // --- Delete History Item (Granular) ---
    const handleDeleteItem = async (e, item) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        setItemToDelete(item);
    };

    const confirmDeleteItem = async () => {
        if (!itemToDelete) return;
        const item = itemToDelete;
        setItemToDelete(null); // Close modal

        try {
            // 1. Fetch latest "result" for this Row
            const { data: rowData, error: fetchError } = await supabase
                .from('ai_model_runs')
                .select('result')
                .eq('id', item.id)
                .single();

            if (fetchError || !rowData) throw new Error("Item not found");

            let currentResult = rowData.result;
            // Parse if string
            if (typeof currentResult === 'string') {
                try { currentResult = JSON.parse(currentResult); } catch (e) { }
            }

            // 2. Modify content
            let newResult = null;
            let success = false;

            // CASE A: Array (Multi-item) - Remove specific index
            if (Array.isArray(currentResult) && typeof item.resultIndex === 'number') {
                if (item.resultIndex >= 0 && item.resultIndex < currentResult.length) {
                    currentResult.splice(item.resultIndex, 1);
                    newResult = currentResult.length === 0 ? null : currentResult; // clean empty
                    success = true;
                }
            }
            // CASE B: Single Item or Fallback
            else {
                newResult = null; // Clear all
                success = true;
            }

            if (!success) return; // Nothing changed

            // 3. Update DB
            const { error: updateError } = await supabase
                .from('ai_model_runs')
                .update({ result: newResult })
                .eq('id', item.id);

            if (updateError) throw updateError;

            // 4. Optimistic Update (Local State)
            // FIXED: Filter by BOTH id AND resultIndex to avoid removing sibling items
            setHistory(prev => prev.filter(h => {
                // Keep if different row ID
                if (h.id !== item.id) return true;
                // Same row: Keep if different index (or if item has no index - single result case)
                if (typeof item.resultIndex === 'number' && typeof h.resultIndex === 'number') {
                    return h.resultIndex !== item.resultIndex;
                }
                // Single item case: Remove if same ID (no resultIndex means it's the whole row)
                return false;
            }));

            // Background refresh to sync indices for next time
            fetchHistory(user?.id, featureConfig?.feature_code || slug);

            showToast("Item berhasil dihapus.", "success");

        } catch (err) {
            console.error("Failed to delete item:", err);
            showToast("Gagal menghapus item.", "error");
        }
    };

    const handlePinHistoryItem = async (e, item) => {
        e.stopPropagation();
        if (pinnedHistoryItem?.uniqueId === item.uniqueId) {
            setPinnedHistoryItem(null); // Unpin
            showToast("Pin dilepas.", "info");
        } else {
            setPinnedHistoryItem(item); // Pin
            showToast("Item disematkan.", "success");

            // --- Auto-Upload to 'image_url' or 'file_multi' with Modal Selection ---
            const result = item.parsedResult || item;

            if (result) {
                const sourceUrl = result.public_url || result.result;

                // Only proceed if it looks like a URL
                // Only proceed if it looks like a URL
                if (typeof sourceUrl === 'string' && sourceUrl.startsWith('http')) {

                    // 1. Determine Asset Type
                    let assetType = 'file';
                    const mime = result.mime_type || item.mime_type; // Use DB mime_type if available

                    if (mime) {
                        if (mime.startsWith('image/')) assetType = 'image';
                        else if (mime.startsWith('video/')) assetType = 'video';
                        else if (mime.startsWith('audio/')) assetType = 'audio';
                        else if (mime === 'application/pdf') assetType = 'pdf';
                    } else {
                        // Fallback to extension
                        const ext = sourceUrl.split('?')[0].split('.').pop().toLowerCase();
                        if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) assetType = 'image';
                        else if (['mp4', 'mov', 'webm', 'mkv', 'avi', 'wmv'].includes(ext)) assetType = 'video';
                        else if (['mp3', 'wav', 'ogg', 'm4a'].includes(ext)) assetType = 'audio';
                        else if (['pdf'].includes(ext)) assetType = 'pdf';
                        else if (['doc', 'docx'].includes(ext)) assetType = 'docx';
                        else if (['xls', 'xlsx', 'csv'].includes(ext)) assetType = 'excel';
                        else if (['txt'].includes(ext)) assetType = 'txt';
                    }



                    // Check for file_multi field (PRIORITY: file_multi always wins)
                    const fileMultiField = modelFields.find(f => !f.is_hidden && f.type === 'file_multi');

                    // If file_multi exists, check if it accepts this asset type
                    if (fileMultiField) {
                        let options = fileMultiField.options;
                        if (typeof options === 'string') { try { options = JSON.parse(options); } catch { options = {}; } }

                        // Check allowed_types (if defined, must include assetType; if not defined, accept all)
                        const allowedTypes = options?.allowed_types;
                        const isAllowed = !allowedTypes || !Array.isArray(allowedTypes) || allowedTypes.length === 0 || allowedTypes.includes(assetType);

                        if (isAllowed) {

                            handlePinToField(fileMultiField, sourceUrl);
                            return; // Exit early, no modal needed
                        }
                    }

                    // Fallback: Find type-specific fields if file_multi is not available or doesn't accept this type
                    const specificFields = modelFields.filter(f => !f.is_hidden && f.type === assetType);



                    if (specificFields.length === 1) {
                        // Single matching field -> Direct Assignment
                        handlePinToField(specificFields[0], sourceUrl);
                    } else if (specificFields.length > 1) {
                        // Multiple matching fields -> Show Selection Modal

                        setPinSelectionModal({
                            url: sourceUrl,
                            candidates: specificFields,
                            assetType: assetType,
                            item: item
                        });
                    } else {
                        // No compatible field
                        // console.warn("No compatible field found for", assetType);
                        showToast(`Tidak ada input field yang cocok untuk ${assetType}.`, "warning");
                    }
                }
            }
        }
    };

    // --- Helper: Pin Execution ---
    const handlePinToField = async (field, sourceUrl) => {
        try {
            showToast(`Menyiapkan asset untuk ${field.label || field.key}...`, "loading");

            const response = await fetch(sourceUrl);
            const blob = await response.blob();
            const ext = blob.type.split('/')[1] || 'png';

            let originalName = sourceUrl.split('/').pop().split('?')[0];
            if (!originalName || originalName.length < 3) originalName = `pinned_item.${ext}`;
            try { originalName = decodeURIComponent(originalName); } catch (e) { }

            const file = new File([blob], originalName, { type: blob.type });
            const publicUrl = await uploadFileToSupabase(file);

            // Update State
            setInputs(prev => {
                const newState = { ...prev };
                let valToSet = publicUrl;

                if (field.type === 'file_multi') {
                    const current = newState[field.key] || [];
                    const currentArray = Array.isArray(current) ? current : (current ? [current] : []);
                    if (!currentArray.includes(publicUrl)) {
                        valToSet = [...currentArray, publicUrl];
                    } else {
                        valToSet = currentArray;
                    }
                }

                newState[field.key] = valToSet;

                if (user?.id) {
                    InputCache.save(slug, user.id, newState);
                }
                return newState;
            });

            showToast(`Asset pinned to ${field.label}!`, "success");
            setPinSelectionModal(null); // Close if open
        } catch (err) {
            console.error("Auto-upload pinned item failed:", err);
            showToast("Gagal memproses asset.", "error");
        }
    };


    // --- Recreate / Reuse Prompt ---
    const handleRecreate = (e, item) => {
        e.stopPropagation();

        // 1. Check if model exists
        // If item.model_key is missing, maybe try to match verify against current? 
        // Ideally we used model_key.
        let targetModelId = null;

        if (item.model_key) {
            const found = models.find(m => m.key === item.model_key);
            if (found) targetModelId = found.id;
        }

        // Fallback: if no model_key saved (old data), or model not found by key
        if (!targetModelId && item.model_name) {
            const found = models.find(m => m.name === item.model_name);
            if (found) targetModelId = found.id;
        }

        // If still not found, we can't recreate accurately (different model might have different fields)
        if (!targetModelId) {
            alert("Model AI yang digunakan untuk generate ini tidak lagi tersedia.");
            return;
        }

        // 2. Restore Inputs
        // Use saved inputs or empty
        const savedInputs = item.inputs || {};

        // Force InputCache Update BEFORE switching model (Race Condition Fix)
        // When selectedModelId changes, the useEffect triggers fetchFields -> setInputs(defaults).
        // By priming the cache now, the effect will 'restore' these values instead of wiping them.
        if (user?.id) {
            InputCache.save(slug, user.id, savedInputs);
        }

        // Restore Model
        setSelectedModelId(targetModelId);

        // Restore Fields
        // We need to wait for fields to update? No, setSelectedModelId triggers useEffect but we might need to verify fields match.
        // For now, simple restore:
        setInputs(prev => ({
            ...savedInputs
        }));

        // Highlight this item (Persistent Hover Effect)
        setActiveMobileMenuId(null);

        // 3. UX
        // Scroll to top
        window.scrollTo({ top: 0, behavior: 'smooth' });

        // Optional: Close modal if open
        // setMediaModal(null); // Actually, maybe user wants to keep looking? No, recreate implies action.

        // Toast/Alert
        // alert("Settings restored! Ready to generate."); // Too intrusive
        // Ideally a toast. For now, maybe just focus.
    };


    // --- CHAT + SIDEBAR LAYOUT (For 'chatbox') ---
    const isChatLayout = featureConfig?.output_type === 'chatbox';

    if (isChatLayout) {
        const activeHistoryId = history.find(h => JSON.stringify(h.parsedResult) === JSON.stringify(generatedOutput))?.id;

        return (
            <>
                {/* Toast Notification */}
                {toast.visible && (
                    <div className={`fixed top-4 left-1/2 -translate-x-1/2 z-[100020] px-4 py-2 rounded-full shadow-lg text-xs animate-in slide-in-from-top-2 fade-in duration-300 flex items-center gap-2 ${toast.type === 'error'
                        ? 'bg-rose-50 text-rose-600 border border-rose-200'
                        : toast.type === 'success'
                            ? 'bg-emerald-50 text-emerald-600 border border-emerald-200'
                            : 'bg-slate-800 text-white'
                        }`}>
                        {toast.type === 'error' && <Icons.AlertCircle size={14} />}
                        {toast.type === 'success' && <Icons.Check size={14} />}
                        {toast.type === 'loading' && <Icons.Loader2 size={14} className="animate-spin" />}
                        {toast.message}
                    </div>
                )}

                {/* Chat Layout Container */}
                <div className="flex-1 flex overflow-hidden relative h-full">

                    {/* SIDE BY SIDE PANEL (NEW LEFT COLUMN) - Only for Chatbox features */}
                    {!!sideBySideSessionId && (featureConfig?.output === 'chatbox' || featureConfig?.output_type === 'chatbox' || featureConfig?.output_type?.includes?.('Chat Application')) && (
                        <div className="hidden md:flex w-[400px] border-r border-slate-200 bg-slate-50 flex-col h-full shrink-0 animate-in slide-in-from-left duration-300 relative z-30 shadow-xl md:shadow-none">
                            {/* Header */}
                            <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between bg-white">
                                <div className="flex items-center gap-2">
                                    <div className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center border border-indigo-200 shadow-sm">
                                        <Icons.Columns size={16} />
                                    </div>
                                    <div>
                                        <h3 className="text-xs font-bold text-slate-800">Side-by-Side View</h3>
                                        <p className="text-[10px] text-slate-500 truncate max-w-[200px]">{chatSessions.find(s => s.id === sideBySideSessionId)?.title || 'Percakapan'}</p>
                                    </div>
                                </div>
                                <button
                                    onClick={() => setSideBySideSessionId(null)}
                                    className="p-1.5 hover:bg-rose-100 rounded-md text-slate-400 hover:text-rose-500 transition-colors"
                                    title="Tutup Side-by-Side"
                                >
                                    <Icons.X size={16} />
                                </button>
                            </div>

                            {/* Content */}
                            <div className="flex-1 overflow-y-auto p-4 space-y-6 scrollbar-thin">
                                {isLoadingSideBySide ? (
                                    <div className="h-full flex flex-col items-center justify-center text-slate-400 gap-2">
                                        <Icons.Loader2 size={24} className="animate-spin text-indigo-400" />
                                        <p className="text-xs">Memuat percakapan...</p>
                                    </div>
                                ) : sideBySideRuns.length === 0 ? (
                                    <div className="h-full flex flex-col items-center justify-center text-slate-400 text-center p-4">
                                        <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center mb-3">
                                            <Icons.MessageSquare size={20} className="opacity-30" />
                                        </div>
                                        <p className="text-xs font-medium text-slate-500">Percakapan Kosong</p>
                                        <p className="text-[10px]">Belum ada pesan dalam sesi ini.</p>
                                    </div>
                                ) : (
                                    sideBySideRuns.map((run, idx) => (
                                        <div key={run.id || idx} className="contents">
                                            {/* 1. User Message */}
                                            {(run.inputs?.prompt || run.inputs?.message) && (
                                                <ChatMessage
                                                    role="user"
                                                    content={run.inputs.prompt || run.inputs.message}
                                                    inputs={run.inputs}
                                                    timestamp={run.created_at}
                                                    onPreviewMedia={setMediaModal}
                                                />
                                            )}

                                            {/* 2. AI Message */}
                                            {run.result && (
                                                <ChatMessage
                                                    role="assistant"
                                                    content={run.result}
                                                    modelName={run.model || run.model_name}
                                                    credits={run.total_cost || 0}
                                                    timestamp={run.created_at} // Same timestamp roughly
                                                    onPreviewMedia={setMediaModal}
                                                    onDownloadMedia={(url, name) => handleDownload(url, name)}
                                                />
                                            )}
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>
                    )}

                    {/* LEFT: Main Chat Area */}
                    <div className="flex-1 flex flex-col min-w-0 bg-white relative">
                        {/* Chat Header / Top Bar - Minimalist (Hidden if no output or on desktop to save space) */}


                        <div className="flex-1 flex flex-col min-h-0 relative group/scroll-area">
                            {/* Mobile History Toggle (Visible only on mobile) */}
                            <button
                                onClick={() => setShowHistory(!showHistory)}
                                className={`absolute right-4 top-4 z-20 p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100/80 rounded-full transition-colors backdrop-blur-sm ${showHistory ? 'hidden' : ''}`}
                            >
                                <Icons.History size={20} />
                            </button>

                            {/* Main Chat Content Area */}
                            <div
                                ref={chatScrollRef}
                                className="flex-1 overflow-y-auto px-4 md:px-10 flex flex-col-reverse pb-2 space-y-reverse space-y-6 scrollbar-thin scrollbar-thumb-slate-200 scrollbar-track-transparent"
                            >

                                {/* === NEW: Chat History for Chatbox Features === */}
                                {((featureConfig.output === 'chatbox' ||
                                    featureConfig.output_type === 'chatbox' ||
                                    featureConfig.output_type?.includes('Chat Application')) &&
                                    (currentSession || sessionRuns.length > 0)) ? (
                                    isLoadingSession ? (
                                        <div className="flex flex-col items-center justify-center p-8 min-h-[40vh] w-full max-w-3xl mx-auto space-y-6">
                                            {/* Pulse Skeleton Loading */}
                                            <div className="w-full flex justify-end">
                                                <div className="w-3/4 bg-slate-100 rounded-2xl rounded-tr-sm p-4 animate-pulse">
                                                    <div className="h-4 bg-slate-200 rounded w-full mb-2"></div>
                                                    <div className="h-4 bg-slate-200 rounded w-2/3"></div>
                                                </div>
                                            </div>
                                            <div className="w-full flex justify-start">
                                                <div className="w-3/4 bg-blue-50/50 rounded-2xl rounded-tl-sm p-4 animate-pulse">
                                                    <div className="h-4 bg-blue-100/50 rounded w-full mb-2"></div>
                                                    <div className="h-4 bg-blue-100/50 rounded w-5/6 mb-2"></div>
                                                    <div className="h-4 bg-blue-100/50 rounded w-4/6"></div>
                                                </div>
                                            </div>
                                            <div className="w-full flex justify-end">
                                                <div className="w-3/4 bg-slate-100 rounded-2xl rounded-tr-sm p-4 animate-pulse">
                                                    <div className="h-4 bg-slate-200 rounded w-11/12"></div>
                                                </div>
                                            </div>
                                        </div>
                                    ) : (sessionRuns.length === 0 && !isSubmitting) ? (
                                        // Empty state - no messages yet
                                        <div className="flex-1 w-full h-full flex flex-col items-center justify-center p-4">
                                            <div className="text-center space-y-3 max-w-2xl animate-in fade-in slide-in-from-bottom-4 duration-700">
                                                <div className="mx-auto flex items-center justify-center mb-6">
                                                    {selectedModel?.configuration_options?.cover_image ? (
                                                        <div className="w-24 h-24 sm:w-28 sm:h-28 lg:w-32 lg:h-32 flex items-center justify-center">
                                                            <img
                                                                src={selectedModel.configuration_options.cover_image.startsWith('http') ? selectedModel.configuration_options.cover_image : supabase.storage.from('assets').getPublicUrl(selectedModel.configuration_options.cover_image).data.publicUrl}
                                                                alt={selectedModel.name}
                                                                className="w-full h-full object-contain drop-shadow-2xl"
                                                                onError={(e) => { e.currentTarget.style.display = 'none'; }}
                                                            />
                                                        </div>
                                                    ) : (
                                                        <div className="w-24 h-24 sm:w-28 sm:h-28 lg:w-32 lg:h-32 flex items-center justify-center">
                                                            {(featureConfig.icon && (featureConfig.icon.includes('/') || featureConfig.icon.includes('.'))) ? (
                                                                <img src={featureConfig.icon.startsWith('http') ? featureConfig.icon : supabase.storage.from('assets').getPublicUrl(featureConfig.icon).data.publicUrl} alt={featureConfig.name} className="w-full h-full object-contain" />
                                                            ) : (
                                                                <DynamicIcon name={featureConfig.icon} size={60} className="text-blue-500" />
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                                <h3 className="text-4xl md:text-5xl font-['Special_Gothic_Expanded_One'] bg-gradient-to-r from-sky-400 to-blue-500 bg-clip-text text-transparent tracking-wide leading-tight pb-2">Halo, {displayName.split(' ')[0]}</h3>
                                                <h2 className="text-sm font-['Special_Gothic_Expanded_One'] text-slate-400 mb-3 tracking-widest uppercase opacity-80">{featureConfig.name}</h2>
                                                <p className="text-sm text-slate-500 max-w-lg mx-auto mt-2 leading-relaxed px-2 md:px-0">
                                                    {featureConfig.description}
                                                </p>
                                            </div>
                                        </div>
                                    ) : (
                                        // Chat History - Display all messages
                                        <div className="w-full max-w-3xl mx-auto flex flex-col-reverse space-y-reverse space-y-6 pt-6">
                                            {/* Optimistic Loading State (Rendered FIRST so it appears at the BOTTOM) */}
                                            {(isSubmitting && !sessionRuns.some(r => r.status === 'processing')) && (
                                                <div className="flex justify-start animate-in fade-in slide-in-from-left-4 duration-300">
                                                    <div className="w-full sm:max-w-[70%] space-y-2 px-4">
                                                        <div className="w-full bg-blue-50/50 rounded-2xl rounded-tl-sm p-4 animate-pulse">
                                                            <div className="flex items-center gap-2 mb-2">
                                                                <div className="w-2 h-2 bg-blue-300 rounded-full"></div>
                                                                <span className="text-xs font-semibold text-blue-300">Generating Response...</span>
                                                            </div>
                                                            <div className="h-3 bg-blue-100/50 rounded w-full mb-2"></div>
                                                            <div className="h-3 bg-blue-100/50 rounded w-2/3"></div>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            {[...sessionRuns].reverse().map((run, idx, reversedArray) => (
                                                <div key={run.id} className="space-y-3">
                                                    {/* User Message - First item (idx 0) is the NEWEST after reverse */}
                                                    <ChatMessage
                                                        ref={idx === 0 ? lastUserMessageRef : null}
                                                        role="user"
                                                        content={run.inputs?.prompt || 'No prompt'}
                                                        inputs={run.inputs}
                                                        modelName={run.model_name}
                                                        timestamp={run.created_at}
                                                        onPreviewMedia={(url, mime) => setMediaModal({ public_url: url, mime_type: mime })}
                                                        onDownloadMedia={(url) => handleDownload(url, getCleanFilename(url))}
                                                    />

                                                    {/* AI Response */}
                                                    {run.status === 'success' && run.result ? (
                                                        <ChatMessage
                                                            role="assistant"
                                                            content={run.result}
                                                            timestamp={run.updated_at}
                                                            credits={run.credits_used}
                                                            status={run.status}
                                                            onPreviewMedia={(url, mime) => setMediaModal({ public_url: url, mime_type: mime })}
                                                            onDownloadMedia={(url) => handleDownload(url, getCleanFilename(url))}
                                                        />
                                                    ) : run.status === 'processing' ? (
                                                        <div className="flex justify-start animate-in fade-in slide-in-from-left-4 duration-300">
                                                            <div className="max-w-[80%] space-y-2 pl-4">
                                                                <div className="w-64 bg-slate-100 rounded-2xl rounded-tl-sm p-3 animate-pulse">
                                                                    <div className="h-3 bg-slate-200 rounded w-full mb-2"></div>
                                                                    <div className="h-3 bg-slate-200 rounded w-4/5"></div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ) : run.status === 'failed' ? (
                                                        <ChatMessage
                                                            role="assistant"
                                                            content={'Gagal memproses permintaan. Silakan hubungi admin.'}
                                                            timestamp={run.updated_at}
                                                            status="failed"
                                                            onPreviewMedia={(url, mime) => setMediaModal({ public_url: url, mime_type: mime })}
                                                            onDownloadMedia={(url) => handleDownload(url, getCleanFilename(url))}
                                                        />
                                                    ) : null}
                                                </div>
                                            ))}

                                        </div>
                                    )
                                ) : generatedOutput ? (
                                    // Show Current Result (Non-chatbox features)
                                    <div className="w-full max-w-3xl mx-auto space-y-6 pt-6 pb-6">
                                        <div className="flex justify-start animate-in fade-in slide-in-from-bottom-4 duration-500">
                                            {/* AI Response Card - Frameless feel */}
                                            <div className="w-full space-y-4">
                                                <div className="flex items-center gap-3 mb-2">
                                                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white shadow-md shadow-blue-200">
                                                        <Icons.Bot size={16} />
                                                    </div>
                                                    <span className="text-sm font-bold text-slate-800">AI Response</span>
                                                </div>

                                                <div className="pl-11">
                                                    {/* If output is array (history style) or string */}
                                                    {Array.isArray(generatedOutput) ? (
                                                        <div className="grid grid-cols-2 gap-3">
                                                            {generatedOutput.map((item, idx) => (
                                                                <div key={idx} className="aspect-square rounded-2xl overflow-hidden border border-slate-100 bg-slate-50 shadow-sm hover:shadow-md transition-shadow">
                                                                    <UnifiedPreview result={item} mimeType={featureConfig.output_type} isHistory={true} />
                                                                </div>
                                                            ))}
                                                        </div>
                                                    ) : (
                                                        <div className="prose prose-sm prose-slate max-w-none bg-slate-50/50 p-6 rounded-2xl border border-slate-100 prosemirror-content">
                                                            {(() => {
                                                                const isObj = generatedOutput && typeof generatedOutput === 'object' && !React.isValidElement(generatedOutput);
                                                                const res = isObj ? generatedOutput : { public_url: generatedOutput };
                                                                const txt = isObj ? (res.output || res.text_content || res.text || res.message) : null;
                                                                const url = res.public_url;
                                                                const hasMedia = url && typeof url === 'string' && (url.startsWith('http') || url.startsWith('data:'));
                                                                const mime = (isObj && res.mime_type) ? res.mime_type : (featureConfig.output_type || "text/markdown");

                                                                return (
                                                                    <div className="flex flex-col gap-6">
                                                                        {hasMedia && (
                                                                            <div className="w-full">
                                                                                <UnifiedPreview result={res} mimeType={mime} />
                                                                            </div>
                                                                        )}
                                                                        {txt && (
                                                                            <div className="whitespace-pre-wrap font-medium text-slate-800 leading-relaxed">
                                                                                {txt}
                                                                            </div>
                                                                        )}
                                                                        {!hasMedia && !txt && (
                                                                            <UnifiedPreview result={{ public_url: generatedOutput }} mimeType={featureConfig.output_type || "text/markdown"} />
                                                                        )}
                                                                    </div>
                                                                );
                                                            })()}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    // Empty State
                                    <div className="h-full flex flex-col items-center justify-center text-center opacity-100 pb-6">
                                        <div className={`w-20 h-20 flex items-center justify-center mb-6 ${(featureConfig.icon && (featureConfig.icon.includes('/') || featureConfig.icon.includes('.')))
                                            ? ''
                                            : 'bg-gradient-to-br from-blue-50 to-indigo-50 rounded-3xl shadow-inner border border-white'
                                            }`}>
                                            {(featureConfig.icon && (featureConfig.icon.includes('/') || featureConfig.icon.includes('.'))) ? (
                                                <img src={featureConfig.icon.startsWith('http') ? featureConfig.icon : supabase.storage.from('assets').getPublicUrl(featureConfig.icon).data.publicUrl} alt={featureConfig.name} className="w-20 h-20 object-contain" />
                                            ) : (
                                                <DynamicIcon name={featureConfig.icon} size={40} className="text-blue-500/80" />
                                            )}
                                        </div>
                                        <h3 className="text-4xl font-['Special_Gothic_Expanded_One'] bg-gradient-to-r from-sky-400 to-blue-500 bg-clip-text text-transparent tracking-wide leading-tight pb-2">Halo, {displayName.split(' ')[0]}</h3>
                                        <h2 className="text-sm font-['Special_Gothic_Expanded_One'] text-slate-500 mb-3 tracking-widest uppercase">{featureConfig.name}</h2>
                                        <p className="text-sm text-slate-500 max-w-md mt-2 leading-relaxed">
                                            {featureConfig.description}
                                        </p>
                                    </div>
                                )}

                                {/* Processing State Overlay Removed */}
                            </div>

                        </div>

                        {/* Bottom Input Area - Gemini-style with gradient fade */}
                        <div className="shrink-0 p-2 md:p-3 bg-gradient-to-t from-white via-white to-white/80 backdrop-blur-sm z-30">
                            <div className="w-full max-w-3xl mx-auto">
                                <div
                                    className={`
                                            relative bg-white border rounded-[28px] transition-all
                                            ${inputs?.dragActive ? 'border-blue-500 bg-blue-50 ring-4 ring-blue-100' : 'border-slate-200 hover:border-slate-300 focus-within:border-blue-400'}
                                            ${(isSubmitting || processingJob) ? 'opacity-80' : ''}
                                        `}
                                    onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setInputs(prev => ({ ...prev, dragActive: true })); }}
                                    onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); setInputs(prev => ({ ...prev, dragActive: false })); }}
                                    onDrop={handleGlobalDrop}
                                >
                                    <form onSubmit={handleSubmit} className="flex flex-col relative">
                                        {/* Top: Chips for attached files */}
                                        {/* Top: Chips for attached files */}
                                        {modelFields.some(f => ['file', 'image', 'audio', 'video', 'pdf', 'docx', 'excel', 'txt', 'file_multi'].includes(f.type) && f.key !== 'prompt' && ((inputs[f.key] && (Array.isArray(inputs[f.key]) ? inputs[f.key].length > 0 : true)) || uploadingFields[f.key])) && (
                                            <div className="px-4 pt-3 flex flex-wrap gap-2">
                                                {modelFields.map(f => {
                                                    // Filter Types
                                                    if (!['file', 'image', 'audio', 'video', 'pdf', 'docx', 'excel', 'txt', 'file_multi'].includes(f.type) || f.key === 'prompt') return null;

                                                    const val = inputs[f.key];
                                                    const isUploading = uploadingFields[f.key];
                                                    const hasValue = val && (Array.isArray(val) ? val.length > 0 : true);

                                                    // If nothing to show (no val and not uploading), skip
                                                    if (!hasValue && !isUploading) return null;

                                                    // Guard: If value is just a number string (Limit), don't render as chip
                                                    if (typeof val === 'string' && /^\d+$/.test(val)) return null;

                                                    const renderChip = (v, idx = null) => {
                                                        // Guard: If value is just a number string (Limit) inside array, don't render
                                                        if (typeof v === 'string' && /^\d+$/.test(v)) return null;

                                                        const name = typeof v === 'string' ? getCleanFilename(v) : v.name;
                                                        const { Icon, color } = getFileIconAndColor(name);

                                                        // Check if image or video for thumbnail
                                                        const isImg = (typeof v === 'object' && v.type?.startsWith('image/')) ||
                                                            (typeof v === 'string' && (v.match(/\.(jpeg|jpg|gif|png|webp)$/i) || v.startsWith('data:image')));

                                                        const isVideo = (typeof v === 'object' && v.type?.startsWith('video/')) ||
                                                            (typeof v === 'string' && (v.match(/\.(mp4|webm|mov|mkv)$/i) || v.startsWith('data:video')));

                                                        if (isImg || isVideo) {
                                                            const src = typeof v === 'object' ? URL.createObjectURL(v) : v;
                                                            return (
                                                                <div key={idx !== null ? `${f.key}-${idx}` : f.key} className="relative group shrink-0 w-16 h-16 rounded-lg border border-slate-200 overflow-hidden shadow-sm animate-in zoom-in-95 bg-black/5">
                                                                    {isImg ? (
                                                                        <img src={src} className="w-full h-full object-cover" alt="preview" />
                                                                    ) : (
                                                                        <video
                                                                            src={src}
                                                                            className="w-full h-full object-cover"
                                                                            muted
                                                                            preload="metadata"
                                                                            playsInline
                                                                            onMouseEnter={e => e.target.play()}
                                                                            onMouseLeave={e => e.target.pause()}
                                                                        />
                                                                    )}
                                                                    {isVideo && (
                                                                        <div className="absolute top-1 left-1 bg-black/60 text-white rounded-full p-1 shadow-sm pointer-events-none z-10">
                                                                            <Icons.Video size={10} />
                                                                        </div>
                                                                    )}
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => idx !== null ? removeFile(f.key, idx) : handleInputChange(f.key, null)}
                                                                        className="absolute top-0.5 right-0.5 bg-black/50 text-white rounded-full p-0.5 hover:bg-rose-500 transition-colors z-20"
                                                                    >
                                                                        <Icons.X size={10} />
                                                                    </button>
                                                                </div>
                                                            );
                                                        }

                                                        return (
                                                            <div key={idx !== null ? `${f.key}-${idx}` : f.key} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border animate-in zoom-in-95 ${color} bg-white shadow-sm`}>
                                                                <Icon size={14} />
                                                                <span className="max-w-[120px] truncate">{name}</span>
                                                                <button
                                                                    type="button"
                                                                    onClick={() => idx !== null ? removeFile(f.key, idx) : handleInputChange(f.key, null)}
                                                                    className="hover:bg-slate-200 rounded-full p-0.5 text-current opacity-60 hover:opacity-100 transition-all hover:text-rose-500"
                                                                >
                                                                    <Icons.X size={12} />
                                                                </button>
                                                            </div>
                                                        );
                                                    };

                                                    return (
                                                        <React.Fragment key={f.key}>
                                                            {/* 1. Render Existing Files */}
                                                            {hasValue && (Array.isArray(val) ? val.map((v, i) => renderChip(v, i)) : renderChip(val))}

                                                            {/* 2. Render Loading Indicator (Explicitly Inlined) */}
                                                            {isUploading && (
                                                                <div
                                                                    key={`${f.key}-loading`}
                                                                    className="relative group shrink-0 w-24 h-16 rounded-lg border border-sky-300 overflow-hidden shadow-sm flex flex-col items-center justify-center animate-pulse"
                                                                    style={{
                                                                        backgroundImage: "url('/assets/dashboard/background.webp')",
                                                                        backgroundSize: 'cover',
                                                                        backgroundPosition: 'center'
                                                                    }}
                                                                >
                                                                    {/* Overlay for better text readability */}
                                                                    <div className="absolute inset-0 bg-white/60"></div>

                                                                    {/* Content */}
                                                                    <div className="relative z-10 flex flex-col items-center">
                                                                        <Icons.Loader2 size={18} className="animate-spin text-sky-600 mb-1" />
                                                                        <span className="text-[9px] text-sky-700 font-bold">Sedang Upload...</span>
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </React.Fragment>
                                                    );
                                                })}
                                            </div>
                                        )}

                                        {/* Middle: Textarea & + Button */}
                                        {/* Input Area Container - Vertical Layout */}
                                        <div className="flex flex-col p-2 relative">

                                            {/* Row 1: Main Textarea */}
                                            <div className="w-full relative">
                                                {(() => {
                                                    const textField = modelFields.find(f => f.type === 'textarea');
                                                    const maxLength = textField?.default_value ? parseInt(textField.default_value) : undefined;
                                                    let currentVal = inputs[textField?.key] || inputs.prompt || "";

                                                    // EMERGENCY GUARD: If current value matches the default limit (e.g. "10"), hide it
                                                    if (textField?.default_value && currentVal === textField.default_value && /^\d+$/.test(currentVal)) {
                                                        currentVal = "";
                                                    }

                                                    return (
                                                        <>
                                                            <textarea
                                                                className="w-full max-h-40 min-h-[44px] bg-transparent border-none focus:ring-0 focus:outline-none outline-none shadow-none ring-0 text-[12px] text-slate-800 placeholder:text-slate-400 py-3 px-3 resize-none scrollbar-hide"
                                                                placeholder={textField?.placeholder || "Tanyakan apa saja"}
                                                                rows={1}
                                                                disabled={isSubmitting}
                                                                value={currentVal}
                                                                maxLength={maxLength}
                                                                onChange={(e) => {
                                                                    if (textField) handleInputChange(textField.key, e.target.value);
                                                                    else handleInputChange('prompt', e.target.value);

                                                                    // Auto-grow
                                                                    e.target.style.height = 'auto';
                                                                    e.target.style.height = Math.min(e.target.scrollHeight, 160) + 'px';
                                                                }}
                                                                onKeyDown={(e) => {
                                                                    if (e.key === 'Enter' && !e.shiftKey) {
                                                                        e.preventDefault(); // Prevent newline
                                                                        handleSubmit(e);
                                                                    }
                                                                }}
                                                            />
                                                            {maxLength && (
                                                                <div className="absolute bottom-2 right-2 text-[10px] text-slate-300 pointer-events-none">
                                                                    {currentVal.length} / {maxLength}
                                                                </div>
                                                            )}
                                                        </>
                                                    );
                                                })()}
                                            </div>

                                            {/* Row 2: Action Toolbar */}
                                            <div className="flex items-center justify-between px-2 pb-1 pt-1">
                                                {/* Left: Attachments & Model Selector */}
                                                <div className="flex items-center gap-2">

                                                    {/* PLUS MENU for Attachments (Only if fields exist) */}
                                                    {modelFields.some(f => ['file', 'image', 'audio', 'file_multi'].includes(f.type) && !f.is_hidden) && (
                                                        <div className="relative" ref={attachmentMenuRef}>
                                                            <button
                                                                type="button"
                                                                disabled={isSubmitting}
                                                                onClick={() => setShowAttachmentMenu(!showAttachmentMenu)}
                                                                className={`p-1.5 md:p-2 rounded-full transition-all active:scale-95 flex items-center justify-center border border-transparent ${showAttachmentMenu ? 'bg-blue-100 text-blue-600' : 'bg-slate-50 text-slate-400 hover:text-blue-600 hover:bg-blue-50 hover:border-slate-200'}`}
                                                                title="Tambahkan Lampiran"
                                                            >
                                                                <Icons.Plus className={`w-4 h-4 md:w-5 md:h-5 transition-transform duration-200 ${showAttachmentMenu ? 'rotate-45' : ''}`} />
                                                            </button>

                                                            {/* Popover Menu - Responsive Layout (Vertical Mobile, Horizontal Desktop) */}
                                                            {showAttachmentMenu && (
                                                                <div className="absolute bottom-full left-0 mb-3 flex flex-col md:flex-row gap-1.5 bg-white rounded-2xl shadow-xl border border-slate-100 p-1.5 md:p-2 animate-in slide-in-from-bottom-2 fade-in duration-200 z-50 w-[85vw] md:w-auto md:max-w-[850px] max-h-[60vh] md:max-h-none overflow-y-auto md:overflow-visible scrollbar-hide">

                                                                    {/* LEFT COLUMN: ACTIONS */}
                                                                    <div className="flex flex-col w-full md:w-52 shrink-0">
                                                                        {modelFields.filter(f => f.type !== 'textarea' && f.type !== 'text' && !f.is_hidden).map(f => {
                                                                            // Check for a Multi-File field to consolidate uploads
                                                                            const multiField = modelFields.find(mf => mf.type === 'file_multi' && !mf.is_hidden);
                                                                            const targetKey = (multiField && ['file', 'image', 'audio', 'video'].includes(f.type)) ? multiField.key : f.key;
                                                                            const isRedirected = targetKey !== f.key;

                                                                            return (
                                                                                <label key={f.id} className="flex items-center gap-3 px-3 py-2.5 hover:bg-slate-50 rounded-xl cursor-pointer transition-colors group/item relative overflow-hidden">
                                                                                    {/* Icon */}
                                                                                    <div className="text-slate-500 group-hover/item:text-slate-700 relative z-10">
                                                                                        {f.type === 'image' && <Icons.Image size={18} />}
                                                                                        {f.type === 'file' && <Icons.FileText size={18} />}
                                                                                        {f.type === 'pdf' && <Icons.FileText size={18} />}
                                                                                        {f.type === 'docx' && <Icons.FileType2 size={18} />}
                                                                                        {f.type === 'excel' && <Icons.FileSpreadsheet size={18} />}
                                                                                        {f.type === 'txt' && <Icons.FileText size={18} />}
                                                                                        {f.type === 'file_multi' && <Icons.Files size={18} />}
                                                                                        {f.type === 'audio' && <Icons.Mic size={18} />}
                                                                                        {f.type === 'video' && <Icons.Video size={18} />}
                                                                                        {f.type === 'slider' && <Icons.Sliders size={18} />}
                                                                                    </div>

                                                                                    {/* Label */}
                                                                                    <span className="text-sm font-medium text-slate-600 group-hover/item:text-slate-900 relative z-10">
                                                                                        {f.label}
                                                                                    </span>

                                                                                    {/* Hidden Inputs */}
                                                                                    {['file', 'image', 'audio', 'video', 'pdf', 'docx', 'excel', 'txt'].includes(f.type) && (
                                                                                        <input
                                                                                            type="file"
                                                                                            className="hidden"
                                                                                            multiple={isRedirected}
                                                                                            accept={(() => {
                                                                                                if (f.type === 'image') return "image/*";
                                                                                                if (f.type === 'audio') return "audio/*";
                                                                                                if (f.type === 'video') return "video/*";
                                                                                                if (f.type === 'pdf') return ".pdf,application/pdf";
                                                                                                if (f.type === 'docx') return ".doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document";
                                                                                                if (f.type === 'excel') return ".xls,.xlsx,.csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv";
                                                                                                if (f.type === 'txt') return ".txt,text/plain";
                                                                                                // Fallback for generic file type but with explicit label check
                                                                                                if (f.type === 'file' && f.label.toLowerCase().includes('pdf')) return ".pdf,application/pdf";
                                                                                                return "*/*";
                                                                                            })()}
                                                                                            onChange={(e) => {
                                                                                                if (isRedirected) handleMultiFileUpload(targetKey, e.target.files);
                                                                                                else handleLocalFileSelect(e.target.files[0], f.key);
                                                                                                setShowAttachmentMenu(false);
                                                                                            }}
                                                                                            onClick={(e) => e.target.value = null}
                                                                                        />
                                                                                    )}
                                                                                    {f.type === 'file_multi' && (
                                                                                        <input
                                                                                            type="file"
                                                                                            className="hidden"
                                                                                            multiple
                                                                                            onChange={(e) => {
                                                                                                handleMultiFileUpload(f.key, e.target.files);
                                                                                                setShowAttachmentMenu(false);
                                                                                            }}
                                                                                            onClick={(e) => e.target.value = null}
                                                                                        />
                                                                                    )}
                                                                                </label>
                                                                            );
                                                                        })}
                                                                        {modelFields.every(f => f.type === 'textarea' || f.type === 'text' || f.is_hidden) && (
                                                                            <div className="px-3 py-4 text-xs text-slate-400 text-center italic">
                                                                                Tidak ada opsi lampiran.
                                                                            </div>
                                                                        )}
                                                                    </div>

                                                                    {/* DIVIDER (Horizontal Mobile, Vertical Desktop) */}
                                                                    {(recentAssets.length > 0 || loadingRecent) && (
                                                                        <div className="h-[1px] w-full md:w-[1px] md:h-auto bg-slate-200/80 md:mx-1 my-2 md:my-0 shrink-0"></div>
                                                                    )}

                                                                    {/* RIGHT COLUMN: GALLERY */}
                                                                    {(recentAssets.length > 0 || loadingRecent) && (
                                                                        <div className="w-full md:w-[480px] shrink-0 flex flex-col p-1.5 md:p-1">
                                                                            {loadingRecent ? (
                                                                                <div className="flex-1 flex items-center justify-center min-h-[120px]">
                                                                                    <Icons.Loader2 className="w-5 h-5 text-slate-300 animate-spin" />
                                                                                </div>
                                                                            ) : (
                                                                                <div className="grid grid-rows-3 grid-flow-col auto-cols-[100px] gap-2 overflow-x-auto lg:grid-rows-none lg:grid-flow-row lg:grid-cols-5 xl:grid-cols-6 lg:overflow-y-auto lg:gap-1.5 pr-1 flex-1 content-start [&::-webkit-scrollbar]:hidden [-ms-overflow-style:'none'] [scrollbar-width:'none']">
                                                                                    {recentAssets.slice(0, 24).map((asset, idx) => {
                                                                                        const ext = asset.name.split('.').pop().toLowerCase();
                                                                                        const isImg = ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext);
                                                                                        const isVideo = ['mp4', 'mov', 'avi', 'webm', 'mkv', 'wmv', 'ts', 'm4v'].includes(ext);

                                                                                        // File Type Styling Helper
                                                                                        const getFileStyle = (ext) => {
                                                                                            if (['pdf'].includes(ext)) return { color: 'text-rose-500', bg: 'bg-rose-50', border: 'border-rose-200', icon: Icons.FileText };
                                                                                            if (['doc', 'docx'].includes(ext)) return { color: 'text-blue-500', bg: 'bg-blue-50', border: 'border-blue-200', icon: Icons.FileType2 };
                                                                                            if (['xls', 'xlsx', 'csv'].includes(ext)) return { color: 'text-emerald-500', bg: 'bg-emerald-50', border: 'border-emerald-200', icon: Icons.FileSpreadsheet };
                                                                                            if (['ppt', 'pptx'].includes(ext)) return { color: 'text-amber-500', bg: 'bg-amber-50', border: 'border-amber-200', icon: Icons.Presentation };
                                                                                            if (['zip', 'rar', '7z'].includes(ext)) return { color: 'text-yellow-600', bg: 'bg-yellow-50', border: 'border-yellow-200', icon: Icons.Archive };
                                                                                            if (['mp3', 'wav', 'ogg', 'm4a'].includes(ext)) return { color: 'text-purple-500', bg: 'bg-purple-50', border: 'border-purple-200', icon: Icons.Mic };
                                                                                            if (['mp4', 'mov', 'avi', 'webm', 'mkv', 'wmv', 'ts', 'm4v'].includes(ext)) return { color: 'text-pink-500', bg: 'bg-pink-50', border: 'border-pink-200', icon: Icons.Video };
                                                                                            return { color: 'text-slate-500', bg: 'bg-slate-50', border: 'border-slate-200', icon: Icons.File };
                                                                                        };

                                                                                        const style = getFileStyle(ext);
                                                                                        const FileIcon = style.icon;

                                                                                        const isActive = activeAssetMenuUrl === asset.url;
                                                                                        const isMedia = isImg || isVideo;
                                                                                        const baseClass = isMedia ? 'bg-slate-50 border-slate-200' : `${style.bg} ${style.border}`;
                                                                                        const hoverClass = `hover:border-sky-400 hover:shadow-sm ${baseClass}`;
                                                                                        const activeClass = 'border-sky-500 ring-2 ring-sky-200 z-10 shadow-md';
                                                                                        const itemClassName = `group relative aspect-square rounded-md border overflow-hidden transition-all ${isActive ? activeClass : hoverClass} ${isMedia ? 'bg-slate-50' : style.bg}`;

                                                                                        return (
                                                                                            <div
                                                                                                role="button"
                                                                                                tabIndex={0}
                                                                                                key={idx}
                                                                                                onTouchStart={() => {
                                                                                                    isLongPress.current = false;
                                                                                                    longPressTimer.current = setTimeout(() => {
                                                                                                        isLongPress.current = true;
                                                                                                        setActiveAssetMenuUrl(prev => prev === asset.url ? null : asset.url);
                                                                                                        if (navigator.vibrate) navigator.vibrate(50);
                                                                                                    }, 500);
                                                                                                }}
                                                                                                onTouchEnd={() => {
                                                                                                    if (longPressTimer.current) clearTimeout(longPressTimer.current);
                                                                                                }}
                                                                                                onTouchMove={() => {
                                                                                                    if (longPressTimer.current) clearTimeout(longPressTimer.current);
                                                                                                }}
                                                                                                onClick={(e) => {
                                                                                                    if (isLongPress.current) {
                                                                                                        e.preventDefault();
                                                                                                        e.stopPropagation();
                                                                                                        isLongPress.current = false;
                                                                                                        return;
                                                                                                    }
                                                                                                    handleAttachAsset(asset.url, { name: asset.name, size: asset.size });
                                                                                                }}
                                                                                                className={itemClassName}
                                                                                                title={asset.name}
                                                                                            >
                                                                                                {isImg ? (
                                                                                                    <img src={BLOB_CACHE.get(asset.url) || asset.url} className="w-full h-full object-cover opacity-90 group-hover:opacity-100 transition-opacity" loading="eager" />
                                                                                                ) : isVideo ? (
                                                                                                    <>
                                                                                                        <video src={asset.url} className="w-full h-full object-cover opacity-90 group-hover:opacity-100 transition-opacity pointer-events-none" preload="metadata" muted playsInline />
                                                                                                        <div className="absolute top-1 left-1 z-20 pointer-events-none drop-shadow-md bg-black/50 rounded-full p-0.5">
                                                                                                            <Icons.Video size={10} className="text-white" />
                                                                                                        </div>
                                                                                                    </>
                                                                                                ) : (
                                                                                                    <div className="w-full h-full flex flex-col items-center justify-between p-1">
                                                                                                        <div className="flex-1 flex items-center justify-center">
                                                                                                            <FileIcon className={`w-4 h-4 ${style.color} opacity-90 group-hover:scale-110 transition-transform duration-200`} strokeWidth={1.5} />
                                                                                                        </div>
                                                                                                        <div className="w-full text-center">
                                                                                                            <p className={`text-[6px] leading-tight font-medium truncate w-full ${style.color} opacity-90`}>
                                                                                                                {asset.name}
                                                                                                            </p>
                                                                                                            <p className="text-[5px] uppercase font-bold opacity-50 tracking-wider">
                                                                                                                {ext}
                                                                                                            </p>
                                                                                                        </div>
                                                                                                    </div>
                                                                                                )}
                                                                                                {/* Hover Overlay with Delete Action - Standardized */}
                                                                                                <div
                                                                                                    className="absolute inset-0 bg-black/40 flex flex-col items-end p-1 opacity-100 xl:opacity-0 xl:group-hover:opacity-100 transition-opacity duration-200 pointer-events-none"
                                                                                                    onContextMenu={(e) => e.preventDefault()}
                                                                                                >
                                                                                                    {/* Delete Button */}
                                                                                                    <button
                                                                                                        type="button"
                                                                                                        className="w-5 h-5 bg-white/90 hover:bg-white text-red-500 rounded flex items-center justify-center shadow-sm cursor-pointer hover:scale-110 transition-transform mb-1 pointer-events-auto"
                                                                                                        onClick={(e) => {
                                                                                                            e.stopPropagation();
                                                                                                            setAssetToDelete(asset);
                                                                                                        }}
                                                                                                        title="Hapus File"
                                                                                                    >
                                                                                                        <Icons.Trash2 size={12} />
                                                                                                    </button>

                                                                                                    {isImg && (
                                                                                                        <div
                                                                                                            className="w-5 h-5 bg-white/90 hover:bg-white text-sky-500 rounded flex items-center justify-center shadow-sm cursor-pointer hover:scale-110 transition-transform pointer-events-auto"
                                                                                                            onClick={(e) => {
                                                                                                                e.stopPropagation();
                                                                                                                handleEditAsset(asset);
                                                                                                            }}
                                                                                                            title="Edit Gambar"
                                                                                                        >
                                                                                                            <Icons.Pencil size={10} />
                                                                                                        </div>
                                                                                                    )}
                                                                                                </div>
                                                                                            </div>
                                                                                        );
                                                                                    })}
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}

                                                    {/* Model Selector Pill inside Toolbar */}
                                                    <button
                                                        type="button"
                                                        onClick={() => setShowModelSelector(true)}
                                                        className="h-10 md:h-9 px-3 md:px-3 bg-slate-100 hover:bg-slate-200 border border-transparent rounded-full flex items-center gap-1.5 md:gap-2 text-xs md:text-xs font-semibold text-slate-700 transition-colors"
                                                        disabled={isSubmitting}
                                                    >
                                                        {selectedModel?.ai_model_providers?.icon_url && (
                                                            <img src={selectedModel.ai_model_providers.icon_url.startsWith('http') ? selectedModel.ai_model_providers.icon_url : supabase.storage.from('assets').getPublicUrl(selectedModel.ai_model_providers.icon_url).data.publicUrl} className="w-3.5 h-3.5 md:w-4 md:h-4 rounded-sm object-cover" alt="icon" />
                                                        )}
                                                        <span className="hidden sm:inline">{selectedModel?.name || "Pilih Model"}</span>
                                                        <span className="sm:hidden max-w-[120px] truncate">{selectedModel?.name || "Model"}</span>
                                                        <Icons.ChevronDown className="w-3 h-3 md:w-3.5 md:h-3.5 text-slate-500" />
                                                    </button>
                                                </div>



                                                {/* Right: Submit Button */}
                                                <button
                                                    type="submit"
                                                    disabled={isSubmitting || processingJob || (backgroundNotification?.message?.includes('Generate in')) || (isChatMode && sessionRuns.some(r => r.status === 'processing'))}
                                                    style={{ backgroundColor: (isSubmitting || processingJob || (backgroundNotification?.message?.includes('Generate in')) || (isChatMode && sessionRuns.some(r => r.status === 'processing'))) ? '#94a3b8' : undefined }}
                                                    className={`
                                                            h-10 md:h-9 px-4 md:px-5 rounded-full transition-all flex items-center justify-center gap-1.5 md:gap-2 font-bold text-sm md:text-xs
                                                            ${(isSubmitting || processingJob || (backgroundNotification?.message?.includes('Generate in')) || (isChatMode && sessionRuns.some(r => r.status === 'processing'))) ? 'text-slate-500 cursor-not-allowed pointer-events-none' : 'bg-sky-600 text-white hover:bg-sky-700 shadow-md hover:shadow-lg'}
                                                         `}
                                                >
                                                    {isSubmitting || (processingJob && (processingJob.feature_code === slug || processingJob.feature_code === featureConfig?.slug)) ? (
                                                        <Icons.Loader2 className="w-3.5 h-3.5 md:w-4 md:h-4 animate-spin" />
                                                    ) : (
                                                        <span className="inline">
                                                            {backgroundNotification
                                                                ? `${backgroundNotification.message} ${backgroundNotification.countdown > 0 ? `(${backgroundNotification.countdown})` : ''}`
                                                                : processingJob
                                                                    ? `Generate in ${processingJob.feature_name || processingJob.feature_code}`
                                                                    : isChatMode
                                                                        ? ((selectedModel?.price_credits && parseInt(selectedModel.price_credits) > 0)
                                                                            ? `Kirim (${parseInt(selectedModel.price_credits)} Kredit)`
                                                                            : "Kirim")
                                                                        : (selectedModel?.price_credits && selectedModel.price_credits > 0)
                                                                            ? `Generate (${selectedModel.price_credits} Kredit)`
                                                                            : "Generate"
                                                            }
                                                        </span>
                                                    )}
                                                </button>
                                            </div>
                                        </div>
                                    </form>
                                </div>
                                <div className="w-full max-w-2xl mx-auto px-4 pb-4 md:pb-0">
                                    <p className="text-[10px] md:text-xs text-center text-slate-400 mt-3 md:mt-2 flex items-center justify-center gap-1.5 opacity-70">
                                        <Icons.ShieldCheck size={12} className="shrink-0" />
                                        <span>AI dapat melakukan kesalahan. Selalu verifikasi informasi penting.</span>
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Mobile Overlay for History */}
                    {
                        !!showHistory && (
                            <div
                                className="fixed inset-0 bg-black/20 backdrop-blur-sm z-30 lg:hidden"
                                onClick={() => setShowHistory(false)}
                            />
                        )
                    }

                    {/* RIGHT: History Sidebar (Responsive) */}
                    <div className={`
                            fixed inset-y-0 right-0 z-40 w-full md:w-80 bg-slate-50 border-l border-slate-200/60 flex flex-col h-full 
                            transform transition-transform duration-300 ease-in-out
                            lg:relative lg:translate-x-0
                            ${showHistory ? 'translate-x-0 shadow-2xl lg:shadow-none lg:w-80 lg:flex' : 'translate-x-full lg:hidden lg:w-0'}
                        `}>
                        <div className="p-4 flex justify-between items-center bg-slate-50">
                            <h3 className="font-bold text-slate-700 text-xs uppercase tracking-wider flex items-center gap-2">
                                <Icons.History size={14} className="text-slate-400" />
                                Riwayat
                            </h3>
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => setShowHistory(false)}
                                    className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                                >
                                    <Icons.X size={18} />
                                </button>
                                {chatSessions.length > 0 && (
                                    <>
                                        <button
                                            onClick={openDeleteModal}
                                            className="p-1.5 hover:bg-white hover:shadow-sm rounded-lg text-slate-400 hover:text-red-600 transition-all border border-transparent hover:border-slate-200"
                                            title="Hapus Semua Chat"
                                        >
                                            <Icons.Trash2 size={18} />
                                        </button>

                                        {/* Delete Modal - Inline */}
                                        {/* Delete Modal - Inline */}
                                        {/* Delete Modal - Component */}
                                        <DeleteConfirmationModal
                                            isOpen={showDeleteConfirm}
                                            onClose={() => setShowDeleteConfirm(false)}
                                            onConfirm={confirmDeleteAllChats}
                                        />
                                    </>
                                )}
                                <button
                                    onClick={handleNewChat} // Fix: Use handleNewChat
                                    className="p-1.5 hover:bg-white hover:shadow-sm rounded-lg text-slate-400 hover:text-blue-600 transition-all border border-transparent hover:border-slate-200 tooltip tooltip-left"
                                    data-tip="Chat Baru"
                                >
                                    <Icons.PlusSquare size={18} />
                                </button>
                            </div>
                        </div>
                        <div className="flex-1 overflow-y-auto p-3 space-y-2 scrollbar-thin scrollbar-thumb-slate-200">
                            {chatSessions.map((session) => {
                                const isSelected = session.id === currentSession?.id;

                                return (
                                    <div
                                        key={session.id}
                                        onClick={() => {
                                            // 1. Optimistic Update (Title/Active State)
                                            setCurrentSession(session);

                                            // 2. Client-Side Cache Check (Instant Load)
                                            const hasCache = sessionCacheRef.current && sessionCacheRef.current[session.id];

                                            if (hasCache) {
                                                // console.log("🚀 Instant load from cache:", session.id);
                                                setSessionRuns(sessionCacheRef.current[session.id]);
                                                isClientSideNavigationRef.current = true; // Signal initSession to skip spinner
                                            } else {
                                                // 3. No Cache: Clear old data and Show Spinner
                                                // console.log("⏳ No cache, clearing and loading...", session.id);
                                                setSessionRuns([]);
                                                setIsLoadingSession(true);
                                                // Do NOT fetch manually here if we are navigating; let the URL effect handle it to avoid double-loading.
                                            }

                                            // 4. Update URL (will trigger initSession via Effect)
                                            if (session.public_id) {
                                                navigate(`/dashboard/feature/${slug}/${session.public_id}`);
                                            } else if (!hasCache) {
                                                // Fallback: If no navigation (legacy), we must fetch manually
                                                loadSessionRuns(session.id).then(() => setIsLoadingSession(false));
                                            }
                                        }}
                                        className={`
                                                group px-3 py-2.5 rounded-xl border cursor-pointer transition-all relative
                                                ${isSelected
                                                ? 'bg-white border-blue-200 shadow-sm ring-1 ring-blue-50'
                                                : 'bg-transparent border-transparent hover:bg-white hover:border-slate-200 hover:shadow-sm'}
                                            `}
                                    >
                                        <div className="flex justify-between items-center min-h-[20px]">
                                            {/* Edit Mode vs Display Mode */}
                                            {editingSessionId === session.id ? (
                                                <div className="flex-1 mr-2" onClick={e => e.stopPropagation()}>
                                                    <input
                                                        type="text"
                                                        value={editTitle}
                                                        onChange={(e) => setEditTitle(e.target.value)}
                                                        onBlur={() => handleSaveTitle(session.id)}
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'Enter') handleSaveTitle(session.id);
                                                            if (e.key === 'Escape') handleCancelEdit();
                                                        }}
                                                        className="w-full text-xs border border-blue-300 rounded px-1 py-0.5 focus:outline-none focus:ring-1 focus:ring-blue-400"
                                                    />
                                                </div>
                                            ) : (
                                                <div className="flex-1 mr-6 flex items-center gap-2 overflow-hidden">
                                                    {session.is_pinned && (
                                                        <Icons.Pin size={12} className="text-orange-500 shrink-0 fill-current" />
                                                    )}
                                                    <span className={`text-xs font-semibold truncate ${isSelected ? 'text-blue-700' : 'text-slate-600'}`}>
                                                        {session.title || "Percakapan Baru"}
                                                    </span>
                                                </div>
                                            )}

                                            {/* Action Menu (Three Dots) - Always visible or persistent style requested */}
                                            {!editingSessionId && (
                                                <div className="relative">
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setOpenMenuSessionId(openMenuSessionId === session.id ? null : session.id);
                                                        }}
                                                        className="p-1 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                                                    >
                                                        <Icons.MoreHorizontal size={16} />
                                                    </button>

                                                    {/* Dropdown Menu */}
                                                    {openMenuSessionId === session.id && (
                                                        <>
                                                            {/* Click Outside Backdrop */}
                                                            <div
                                                                className="fixed inset-0 z-10 cursor-default"
                                                                onClick={(e) => { e.stopPropagation(); setOpenMenuSessionId(null); }}
                                                            ></div>

                                                            <div className="absolute top-6 right-0 w-32 bg-white rounded-lg shadow-xl border border-slate-100 z-20 py-1 flex flex-col animate-in fade-in zoom-in-95 duration-100">
                                                                <button
                                                                    onClick={(e) => {
                                                                        handlePinSession(e, session);
                                                                        setOpenMenuSessionId(null);
                                                                    }}
                                                                    className="text-left px-3 py-2 text-[10px] font-medium text-slate-600 hover:bg-slate-50 flex items-center gap-2"
                                                                >
                                                                    <Icons.Pin size={12} className={session.is_pinned ? "text-orange-500 fill-current" : ""} />
                                                                    {session.is_pinned ? "Lepas Pin" : "Sematkan"}
                                                                </button>

                                                                {/* Side-by-Side Toggle (Added) */}
                                                                <button
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        if (sideBySideSessionId === session.id) {
                                                                            setSideBySideSessionId(null);
                                                                        } else {
                                                                            setSideBySideSessionId(session.id);
                                                                        }
                                                                        setOpenMenuSessionId(null);
                                                                    }}
                                                                    className={`text-left px-3 py-2 text-[10px] font-medium flex items-center gap-2 ${sideBySideSessionId === session.id ? 'text-indigo-600 bg-indigo-50 hover:bg-indigo-100' : 'text-slate-600 hover:bg-slate-50'}`}
                                                                >
                                                                    <Icons.Columns size={12} />
                                                                    {sideBySideSessionId === session.id ? "Tutup Comparison" : "Side-by-Side"}
                                                                </button>
                                                                <button
                                                                    onClick={(e) => {
                                                                        handleStartRename(e, session);
                                                                        setOpenMenuSessionId(null);
                                                                    }}
                                                                    className="text-left px-3 py-2 text-[10px] font-medium text-slate-600 hover:bg-slate-50 flex items-center gap-2"
                                                                >
                                                                    <Icons.Edit2 size={12} />
                                                                    Ganti Judul
                                                                </button>
                                                                <button
                                                                    onClick={(e) => {
                                                                        handleDeleteSession(e, session);
                                                                        // setOpenMenuSessionId(null); // managed in handler
                                                                    }}
                                                                    className="text-left px-3 py-2 text-[10px] font-medium text-slate-600 hover:text-red-600 hover:bg-red-50 flex items-center gap-2"
                                                                >
                                                                    <Icons.Trash2 size={12} />
                                                                    Hapus
                                                                </button>
                                                            </div>
                                                        </>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                        {/* Date/Time removed as per request */}
                                    </div>
                                );
                            })}
                            {history.length === 0 && (
                                <div className="text-center py-20 text-slate-400 flex flex-col items-center opacity-50">
                                    <Icons.MessageSquareDashed size={32} className="mb-2" />
                                    <p className="text-xs italic">Belum ada sesi chat.</p>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* === DELETE CONFIRMATION MODAL === */}
                    {
                        sessionToDelete && (
                            <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm animate-in fade-in duration-200">
                                <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6 animate-in zoom-in-95 duration-200">
                                    <div className="flex flex-col text-left">
                                        <h3 className="text-lg font-bold text-slate-800 mb-2 px-4">Hapus Chat?</h3>
                                        <p className="text-sm text-slate-600 mb-2 leading-relaxed px-4">
                                            Apakah anda yakin ingin menghapus sesi chat <span className="font-semibold text-slate-800">"{sessionToDelete.title}"</span>?
                                        </p>
                                        <p className="text-xs text-slate-400 mb-6 leading-relaxed px-4">
                                            Tindakan ini akan menghapus perintah, respons, dan masukan dari Aktivitas Aplikasi Neoai Anda, serta konten apa pun yang Anda buat.
                                        </p>
                                        <div className="flex gap-3 w-full px-4 justify-end">
                                            <button
                                                onClick={() => setSessionToDelete(null)}
                                                className="py-2.5 px-4 text-slate-500 font-medium hover:text-slate-700 transition-colors text-xs sm:text-sm"
                                            >
                                                Batal
                                            </button>
                                            <button
                                                onClick={confirmDeleteSession}
                                                className="py-2.5 px-6 rounded-xl bg-white border border-red-200 text-red-600 font-bold hover:bg-red-50 hover:border-red-300 hover:shadow-sm transition-all text-xs sm:text-sm"
                                            >
                                                Hapus
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )
                    }

                    {/* === MODEL SWITCH CONFIRMATION MODAL === */}
                    {
                        showSwitchModelModal && (
                            <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm animate-in fade-in duration-200">
                                <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6 animate-in zoom-in-95 duration-200">
                                    <div className="flex flex-col text-left">
                                        <div className="flex justify-between items-start mb-2 px-4">
                                            <h3 className="text-lg font-bold text-slate-800">Mulai percakapan baru?</h3>
                                        </div>
                                        <p className="text-sm text-slate-600 mb-6 leading-relaxed px-4">
                                            Memilih model ini akan memulai percakapan baru. Anda dapat kembali ke percakapan ini kapan saja.
                                        </p>

                                        {/* Don't Ask Again Checkbox */}
                                        <div className="flex items-center gap-2 px-4 mb-6">
                                            <input
                                                type="checkbox"
                                                id="dontAskAgain"
                                                className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                                                checked={dontAskSwitchModel}
                                                onChange={(e) => setDontAskSwitchModel(e.target.checked)}
                                            />
                                            <label htmlFor="dontAskAgain" className="text-sm text-slate-600 select-none cursor-pointer">
                                                Jangan tanyakan lagi
                                            </label>
                                        </div>

                                        <div className="flex gap-3 w-full px-4 justify-end">
                                            <button
                                                onClick={() => { setShowSwitchModelModal(false); setPendingModelId(null); }}
                                                className="py-2.5 px-4 text-slate-500 font-medium hover:text-slate-700 transition-colors text-xs sm:text-sm"
                                            >
                                                Batal
                                            </button>
                                            <button
                                                onClick={() => {
                                                    setShowSwitchModelModal(false);
                                                    if (pendingModelId) {
                                                        setSelectedModelId(pendingModelId);
                                                        // Reset session
                                                        setCurrentSession(null);
                                                        setSessionRuns([]);
                                                        setIsLoadingSession(false);
                                                        // Possibly trigger initSession via effect or manually
                                                    }
                                                }}
                                                className="py-2.5 px-6 rounded-xl bg-blue-600 text-white font-bold hover:bg-blue-700 hover:shadow-lg transition-all text-xs sm:text-sm"
                                            >
                                                Mulai Baru
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )
                    }

                    {/* === ASSET DELETE MODAL === */}
                    <DeleteItemModal
                        isOpen={!!assetToDelete}
                        onClose={() => setAssetToDelete(null)}
                        onConfirm={confirmDeleteAsset}
                        title="Hapus File?"
                        message={`Apakah anda yakin ingin menghapus file "${assetToDelete?.name}"? Tindakan ini tidak dapat dibatalkan.`}
                    />


                </div >

                {/* Reuse Existing Modals */}
                {/* Reuse Existing Modals */}
                {
                    showModelSelector && (
                        <ModelSelectorModal
                            models={models}
                            selectedId={selectedModelId}
                            onSelect={handleModelSelect}
                            onClose={() => setShowModelSelector(false)}
                            capabilities={modelCapabilities}
                            currentTotalCredits={totalCredits}
                            inputs={inputs}
                            isOpen={showModelSelector}
                        />
                    )
                }

                {/* DEBUG INFO MODAL (Triggered by clicking Model Title) */}
                {
                    showDebugInfo && (
                        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm animate-in fade-in p-4" onClick={() => setShowDebugInfo(false)}>
                            <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[85vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
                                <div className="p-4 border-b border-slate-200 flex justify-between items-center bg-slate-50">
                                    <div>
                                        <h3 className="font-bold text-slate-800 text-lg">Debug Info: {selectedModel?.name}</h3>
                                        <p className="text-xs text-slate-500">Feature: {featureConfig?.name} ({slug})</p>
                                    </div>
                                    <button onClick={() => setShowDebugInfo(false)} className="p-2 hover:bg-slate-200 rounded-lg transition-colors">
                                        <Icons.X size={20} />
                                    </button>
                                </div>
                                <div className="p-6 overflow-y-auto font-mono text-xs space-y-6">

                                    {/* 1. Field Configuration (The requested "Values") */}
                                    <div className="space-y-2">
                                        <h4 className="font-bold text-slate-900 text-sm border-b border-slate-200 pb-2 flex items-center gap-2">
                                            <Icons.Settings2 size={16} /> Field Configuration & Current Values
                                        </h4>
                                        <div className="border rounded-lg overflow-hidden border-slate-200">
                                            <table className="w-full text-left bg-white">
                                                <thead className="bg-slate-100 text-slate-600 font-semibold border-b border-slate-200">
                                                    <tr>
                                                        <th className="p-3 w-[20%]">Field</th>
                                                        <th className="p-3 w-[30%]">Current Value</th>
                                                        <th className="p-3 w-[25%]">Limits / Default</th>
                                                        <th className="p-3 w-[25%]">Config (Options)</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="divide-y divide-slate-100">
                                                    {modelFields.map(field => {
                                                        // Parse Options
                                                        let opts = field.options || {};
                                                        if (typeof opts === 'string') {
                                                            try { opts = JSON.parse(opts); } catch (e) { }
                                                        }

                                                        // Determine Limits text
                                                        // Determine Limits text
                                                        let limitsText = [];

                                                        // Fallback logic for Max Files (consistent with handler)
                                                        if (field.type === 'file_multi') {
                                                            const limit = field.default_value ? field.default_value : "5 (System Default)";
                                                            limitsText.push(`Max Files: ${limit}`);
                                                        } else if (field.default_value) {
                                                            if (field.type === 'slider') limitsText.push(`Range: 1 - ${field.default_value}`);
                                                            else if ((field.type === 'text' || field.type === 'textarea') && /^\d+$/.test(field.default_value)) limitsText.push(`Max Chars: ${field.default_value}`);
                                                            else limitsText.push(`Default: ${field.default_value}`);
                                                        }

                                                        // Single file fallback
                                                        if (!field.default_value && ['file', 'image', 'video', 'audio', 'pdf', 'docx', 'excel', 'txt'].includes(field.type)) {
                                                            limitsText.push("Max Files: 1");
                                                        }

                                                        // Parse Config text
                                                        let configText = [];
                                                        if (opts.allowed_types) configText.push(`Types: ${Array.isArray(opts.allowed_types) ? opts.allowed_types.join(', ') : opts.allowed_types}`);

                                                        return (
                                                            <tr key={field.key} className="hover:bg-slate-50/50">
                                                                <td className="p-3 align-top">
                                                                    <div className="flex items-center gap-2">
                                                                        <span className="font-bold text-sky-600 font-mono">{field.key}</span>
                                                                        {field.is_required && <span className="text-rose-500 text-[10px] border border-rose-200 px-1 rounded bg-rose-50">Req</span>}
                                                                    </div>
                                                                    <div className="text-[10px] text-slate-500">{field.label}</div>
                                                                    <span className="bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded border border-slate-200 uppercase text-[9px] font-bold mt-1 inline-block">
                                                                        {field.type}
                                                                    </span>
                                                                </td>
                                                                <td className="p-3 align-top break-all">
                                                                    {inputs[field.key] !== undefined && inputs[field.key] !== null && inputs[field.key] !== "" && (Array.isArray(inputs[field.key]) ? inputs[field.key].length > 0 : true)
                                                                        ? (typeof inputs[field.key] === 'object'
                                                                            ? <div className="text-purple-600 text-[10px] font-mono bg-purple-50 p-1 rounded border border-purple-100">{JSON.stringify(inputs[field.key])}</div>
                                                                            : <div className="text-slate-800 font-medium">{String(inputs[field.key])}</div>
                                                                        )
                                                                        : <span className="text-slate-300 italic text-[10px]">-- empty --</span>
                                                                    }
                                                                </td>
                                                                <td className="p-3 align-top">
                                                                    {limitsText.length > 0 ? (
                                                                        <div className="flex flex-col gap-1">
                                                                            {limitsText.map((t, i) => (
                                                                                <span key={i} className="text-slate-700 font-mono text-[10px] bg-yellow-50 px-1.5 py-0.5 rounded border border-yellow-100 w-fit whitespace-nowrap">
                                                                                    {t}
                                                                                </span>
                                                                            ))}
                                                                        </div>
                                                                    ) : <span className="text-slate-300 text-[10px]">-</span>}
                                                                </td>
                                                                <td className="p-3 align-top">
                                                                    {configText.length > 0 ? (
                                                                        <div className="flex flex-col gap-1">
                                                                            {configText.map((t, i) => (
                                                                                <span key={i} className="text-slate-700 font-mono text-[10px] bg-blue-50 px-1.5 py-0.5 rounded border border-blue-100 w-fit whitespace-nowrap">
                                                                                    {t}
                                                                                </span>
                                                                            ))}
                                                                        </div>
                                                                    ) : (
                                                                        <span className="text-slate-400 text-[10px] italic">System Default</span>
                                                                    )}
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <h4 className="font-bold text-slate-900 text-sm border-b border-slate-200 pb-2">Full Raw Inputs</h4>
                                            <pre className="bg-slate-900 text-green-400 p-3 rounded-lg overflow-auto max-h-60 border border-slate-700">
                                                {JSON.stringify(inputs, null, 2)}
                                            </pre>
                                        </div>
                                        <div className="space-y-2">
                                            <h4 className="font-bold text-slate-900 text-sm border-b border-slate-200 pb-2">Model Config</h4>
                                            <pre className="bg-slate-50 text-slate-600 p-3 rounded-lg overflow-auto max-h-60 border border-slate-200">
                                                {JSON.stringify(selectedModel, null, 2)}
                                            </pre>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )
                }

                {/* Image Editor Modal */}
                {
                    imageEditorAsset && (
                        <ImageEditorModal
                            isOpen={!!imageEditorAsset}
                            imageSrc={imageEditorAsset.url}
                            onClose={() => setImageEditorAsset(null)}
                            onSave={handleSaveEditedImage}
                            isSaving={isSavingEditedImage}
                        />
                    )
                }

                {/* FULL SCREEN MEDIA MODAL */}
                {
                    mediaModal && (
                        <div
                            className="fixed inset-0 z-[100] flex items-center justify-center bg-black animate-in fade-in duration-200 outline-none focus:outline-none"
                            onClick={() => setMediaModal(null)}
                        >
                            {(() => {
                                // Determine Layout Mode based on content type
                                const isDoc = (mediaModal.mime_type && (
                                    mediaModal.mime_type === 'application/pdf' ||
                                    mediaModal.mime_type === 'text/plain' ||
                                    mediaModal.mime_type.includes('application/') // For other docs
                                )) || (mediaModal.public_url && (
                                    mediaModal.public_url.endsWith('.pdf') ||
                                    mediaModal.public_url.endsWith('.txt') ||
                                    mediaModal.public_url.endsWith('.doc') ||
                                    mediaModal.public_url.endsWith('.docx')
                                ));

                                return (
                                    <div
                                        className={`relative w-full h-full flex flex-col items-center justify-center pointer-events-none outline-none focus:outline-none ${isDoc ? 'p-4 md:p-8 max-w-5xl gap-2' : ''}`}
                                    >
                                        {/* Header/Controls */}
                                        {isDoc ? (
                                            <div className="w-full flex justify-end gap-3 pointer-events-auto bg-black/50 p-2 rounded-xl backdrop-blur-md">
                                                {mediaModal.public_url && (
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            const downloadName = getCleanFilename(mediaModal.storage_path || mediaModal.public_url);
                                                            handleDownload(mediaModal.public_url, downloadName);
                                                        }}
                                                        className="flex items-center gap-2 px-4 py-2 bg-sky-600 hover:bg-sky-500 text-white rounded-lg text-xs font-bold transition-all shadow-sm"
                                                        title="Download"
                                                    >
                                                        <Icons.Download size={14} /> Download File
                                                    </button>
                                                )}

                                                {isChatMode && (
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handlePinConversation(e, {
                                                                public_url: mediaModal.public_url || mediaModal.storage_path,
                                                                result: mediaModal.public_url
                                                            });
                                                            setMediaModal(null);
                                                        }}
                                                        className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white rounded-lg text-xs font-bold transition-all shadow-sm"
                                                        title="Pin Conversation Context"
                                                    >
                                                        <Icons.Pin size={14} className="rotate-45" /> Pin Context
                                                    </button>
                                                )}
                                                <button
                                                    onClick={() => setMediaModal(null)}
                                                    className="p-2 bg-white/10 hover:bg-white/20 text-white rounded-lg hover:scale-105 transition-all text-xs font-bold"
                                                >
                                                    <Icons.X size={18} />
                                                </button>
                                            </div>
                                        ) : (
                                            <div className="absolute top-4 right-4 z-[110] flex items-center gap-3 pointer-events-auto">
                                                {mediaModal.public_url && (
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            const downloadName = getCleanFilename(mediaModal.storage_path || mediaModal.public_url);
                                                            handleDownload(mediaModal.public_url, downloadName);
                                                        }}
                                                        className="p-3 bg-white/10 hover:bg-white/20 text-white rounded-full hover:scale-105 transition-all backdrop-blur-md border border-white/10 shadow-sm"
                                                        title="Download"
                                                    >
                                                        <Icons.Download size={20} />
                                                    </button>
                                                )}
                                                <button
                                                    onClick={() => setMediaModal(null)}
                                                    className="p-3 bg-white/10 hover:bg-white/20 text-white rounded-full hover:scale-105 transition-all backdrop-blur-md border border-white/10 shadow-sm"
                                                >
                                                    <Icons.X size={20} />
                                                </button>
                                            </div>
                                        )}

                                        <div
                                            className={`w-full ${isDoc ? 'flex-1 min-h-0' : 'h-full'} flex items-center justify-center overflow-hidden pointer-events-auto outline-none focus:outline-none`}
                                            onClick={e => e.stopPropagation()}
                                        >
                                            {renderResultPreview(mediaModal)}
                                        </div>
                                    </div>
                                );
                            })()}
                        </div>
                    )
                }



            </>
        );
    }

    return (
        <>
            <div className="hidden" onDragOver={handleDragOver} onDrop={handleGlobalDrop} />

            {/* Sidebar Handled by Inline DashboardLayout in App.jsx to prevent reloading */}

            <main className="flex-1 flex flex-col p-3 sm:p-4 bg-slate-50/50 overflow-y-auto xl:overflow-hidden h-full">
                <div className="w-full xl:h-full flex flex-col max-w-[1400px] mx-auto md:min-h-full xl:min-h-0">
                    <div className={`grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-3 xl:flex-1 xl:min-h-0 h-auto md:flex-1 ${(sideBySideSessionId && (featureConfig?.output === 'chatbox' || featureConfig?.output_type === 'chatbox' || featureConfig?.output_type?.includes?.('Chat Application'))) ? "xl:grid-cols-[1fr_1fr_340px]" : "xl:grid-cols-[280px_1fr_340px] 2xl:grid-cols-[300px_1fr_380px]"}`}>

                        {/* 1. HERO / INFO CARD OR SIDE-BY-SIDE PANEL */}
                        {(!!sideBySideSessionId && (featureConfig?.output === 'chatbox' || featureConfig?.output_type === 'chatbox' || featureConfig?.output_type?.includes?.('Chat Application'))) ? (
                            // --- SIDE-BY-SIDE PANEL ---
                            <div className="relative rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden flex flex-col md:col-span-2 xl:col-span-1 h-[500px] md:h-full xl:h-full animate-in slide-in-from-left duration-300">
                                {/* Header */}
                                <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
                                    <div className="flex items-center gap-2">
                                        <div className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center border border-indigo-200 shadow-sm">
                                            <Icons.Columns size={16} />
                                        </div>
                                        <div>
                                            <h3 className="text-xs font-bold text-slate-800">Side-by-Side View</h3>
                                            <p className="text-[10px] text-slate-500 truncate max-w-[150px]">{chatSessions.find(s => s.id === sideBySideSessionId)?.title || 'Percakapan'}</p>
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => setSideBySideSessionId(null)}
                                        className="p-1.5 hover:bg-rose-100 rounded-md text-slate-400 hover:text-rose-500 transition-colors"
                                        title="Tutup Side-by-Side"
                                    >
                                        <Icons.X size={16} />
                                    </button>
                                </div>

                                {/* Content */}
                                <div className="flex-1 overflow-y-auto p-4 space-y-6 bg-slate-50/50 scrollbar-thin">
                                    {isLoadingSideBySide ? (
                                        <div className="h-full flex flex-col items-center justify-center text-slate-400 gap-2">
                                            <Icons.Loader2 size={24} className="animate-spin text-indigo-400" />
                                            <p className="text-xs">Memuat percakapan...</p>
                                        </div>
                                    ) : sideBySideRuns.length === 0 ? (
                                        <div className="h-full flex flex-col items-center justify-center text-slate-400 text-center p-4">
                                            <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center mb-3">
                                                <Icons.MessageSquare size={20} className="opacity-30" />
                                            </div>
                                            <p className="text-xs font-medium text-slate-500">Percakapan Kosong</p>
                                            <p className="text-[10px]">Belum ada pesan dalam sesi ini.</p>
                                        </div>
                                    ) : (
                                        sideBySideRuns.map((run, idx) => {
                                            // The newest run is at index 0
                                            const isNewestRun = idx === 0;
                                            // Only auto-scroll to this prompt if this run is the *newest* one AND it has a result.
                                            const shouldScrollToPrompt = isNewestRun && run.result;

                                            return (
                                                <div key={run.id || idx} className="contents">
                                                    {/* 1. User Message */}
                                                    {(run.inputs?.prompt || run.inputs?.message) && (
                                                        <ChatMessage
                                                            ref={shouldScrollToPrompt ? lastUserMessageRef : null}
                                                            role="user"
                                                            content={run.inputs.prompt || run.inputs.message}
                                                            inputs={run.inputs}
                                                            timestamp={run.created_at}
                                                            onPreviewMedia={setMediaModal}
                                                        />
                                                    )}

                                                    {/* 2. AI Message */}
                                                    {run.result && (
                                                        <ChatMessage
                                                            role="assistant"
                                                            content={run.result}
                                                            modelName={run.model || run.model_name}
                                                            credits={run.total_cost || 0}
                                                            timestamp={run.created_at} // Same timestamp roughly
                                                            onPreviewMedia={setMediaModal}
                                                            onDownloadMedia={(url, name) => handleDownload(url, name)}
                                                        />
                                                    )}
                                                </div>
                                            );
                                        })
                                    )}
                                </div>
                            </div>
                        ) : (
                            <div className="relative rounded-xl border border-sky-100 bg-gradient-to-br from-[#F0F9FF] via-white to-[#E0F2FE] shadow-sm p-4 md:p-2.5 overflow-hidden flex flex-row xl:flex-col items-center xl:items-stretch md:justify-start xl:justify-between xl:h-full h-auto gap-4 md:gap-2 md:col-span-2 xl:col-span-1">

                                {/* Image / Icon Section */}
                                <div className="order-first xl:order-last shrink-0 relative flex items-center xl:items-end justify-center xl:flex-1 text-sky-200/50 xl:-mb-2.5 xl:-mx-2.5">
                                    {/* 1. MODEL COVER IMAGE PRIORITY */}
                                    {selectedModel?.configuration_options?.cover_image ? (
                                        <img
                                            src={selectedModel.configuration_options.cover_image.startsWith('http') ? selectedModel.configuration_options.cover_image : supabase.storage.from('assets').getPublicUrl(selectedModel.configuration_options.cover_image).data.publicUrl}
                                            alt={selectedModel.name}
                                            className="w-20 sm:w-24 md:w-32 lg:w-40 xl:w-full h-auto object-contain drop-shadow-md xl:drop-shadow-none"
                                            onError={(e) => { e.currentTarget.style.display = 'none'; }}
                                        />
                                    ) : (
                                        /* 2. FALLBACK TO FEATURE CONFIG ICON */
                                        <>
                                            {featureConfig.icon && (featureConfig.icon.includes('.') || featureConfig.icon.includes('/')) ? (
                                                <div className="relative w-20 h-20 sm:w-24 sm:h-24 md:w-20 md:h-20 lg:w-24 lg:h-24 xl:w-44 xl:h-44 flex items-center justify-center">
                                                    <img
                                                        src={`https://zarwmajsgjyymvehvgco.supabase.co/storage/v1/object/public/assets/${featureConfig.icon}`}
                                                        alt={featureConfig.name}
                                                        className="w-full h-full object-contain relative z-10 drop-shadow-lg xl:drop-shadow-2xl"
                                                        onError={(e) => { e.currentTarget.style.display = 'none'; }}
                                                    />
                                                </div>
                                            ) : (
                                                /* DynamicIcon with responsive sizes */
                                                <>
                                                    <DynamicIcon name={featureConfig.icon} size={64} className="block md:hidden drop-shadow-sm opacity-50" />
                                                    <DynamicIcon name={featureConfig.icon} size={72} className="hidden md:block xl:hidden drop-shadow-sm opacity-50" />
                                                    <DynamicIcon name={featureConfig.icon} size={140} className="hidden xl:block drop-shadow-sm opacity-50" />
                                                </>
                                            )}
                                        </>
                                    )}
                                </div>

                                {/* Text Section */}
                                <div className="flex-1 flex flex-col gap-1.5 relative z-10 md:shrink-0 items-start text-left">

                                    <div className="min-w-0 flex flex-col">
                                        {/* 1. Feature Name (Small - Label) */}
                                        <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-0.5">
                                            {featureConfig.name}
                                        </div>

                                        {/* 2. Model Name (Prominent - Main Title) */}
                                        <h1
                                            className="text-lg sm:text-xl md:text-xl xl:text-2xl font-extrabold text-slate-900 leading-tight mb-1"
                                        >
                                            {selectedModel?.name || featureConfig.name}
                                        </h1>

                                        {/* 3. Description - Shown on tablet+ */}
                                        <div className="">
                                            <p className="text-xs text-slate-600 leading-relaxed font-medium">
                                                {(featureConfig.description || "").split("|||CONFIG|||")[0] || "Gunakan AI canggih ini untuk kebutuhan Anda."}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* 2. INPUT FORM CARD - Narrower width (320px) */}
                        <div className="xl:h-full md:h-full md:min-h-[450px] h-auto flex flex-col min-h-0">
                            {models.length === 0 && !loading ? (
                                <div className="bg-white/95 backdrop-blur rounded-xl border border-dashed border-slate-300 bg-slate-50/50 p-8 xl:h-full md:h-full h-[400px] flex-1 flex flex-col items-center justify-center text-center relative z-20">
                                    <div className="w-20 h-20 bg-white rounded-2xl flex items-center justify-center mb-6 border border-slate-100 shadow-sm rotate-3 transform transition-transform hover:rotate-6">
                                        <Icons.Rocket size={32} className="text-sky-400" />
                                    </div>
                                    <h3 className="font-extrabold text-slate-800 text-xl mb-3">Segera Hadir</h3>
                                    <p className="text-slate-500 text-sm leading-relaxed max-w-[240px]">
                                        Fitur <strong className="text-sky-600">{featureConfig.name}</strong> sedang dalam tahap pengembangan akhir kami.
                                    </p>
                                    <div className="mt-8 flex gap-2">
                                        <span className="px-3 py-1 bg-sky-100 text-sky-600 text-[10px] font-bold rounded-full uppercase tracking-wider">Coming Soon</span>
                                    </div>
                                </div>
                            ) : (
                                <div
                                    className="bg-white/95 backdrop-blur rounded-xl border border-slate-200 shadow-sm p-3.5 xl:h-full md:h-full h-auto flex-1 flex flex-col relative z-20"
                                >

                                    <div className="space-y-0.5 shrink-0 mb-2">
                                        {/* Model Selector Trigger */}
                                        <label className="text-xs sm:text-[10px] font-semibold text-slate-500 ml-0.5">Model AI</label>
                                        <button
                                            onClick={() => setShowModelSelector(true)}
                                            className="w-full bg-white hover:bg-slate-50 text-slate-900 rounded-lg p-2 flex items-center justify-between transition-all border border-slate-200 hover:border-sky-300 group shadow-sm text-left relative overflow-hidden"
                                        >
                                            <div className="flex items-center gap-2.5 relative z-10 w-full">
                                                {/* Provider Icon or Default - Transparent/Clean */}
                                                {/* Provider Icon or Default - Transparent/Clean - ONLY RENDER IF EXISTS */}
                                                {selectedModel?.ai_model_providers?.icon_url && (
                                                    <div className="w-5 h-5 flex items-center justify-center shrink-0">
                                                        <img
                                                            src={selectedModel.ai_model_providers.icon_url.startsWith('http') ? selectedModel.ai_model_providers.icon_url : supabase.storage.from('assets').getPublicUrl(selectedModel.ai_model_providers.icon_url).data.publicUrl}
                                                            className="w-full h-full object-contain"
                                                            alt="Provider"
                                                        />
                                                    </div>
                                                )}

                                                <div className="flex-1 min-w-0">
                                                    <h3 className="font-bold text-sm lg:text-xs text-slate-800 truncate">{selectedModel?.name || "Select Model"}</h3>
                                                </div>
                                                <Icons.ChevronDown size={14} className="text-slate-400 group-hover:text-slate-600 transition-colors" />
                                            </div>
                                        </button>
                                    </div>

                                    {/* Model Selector Modal */}
                                    {showModelSelector && (
                                        <ModelSelectorModal
                                            models={models}
                                            selectedId={selectedModelId}
                                            onSelect={(id) => {
                                                handleModelSelect(id);
                                                setShowModelSelector(false);
                                            }}
                                            onClose={() => setShowModelSelector(false)}
                                            capabilities={modelCapabilities}
                                        />
                                    )}



                                    <div className="h-px bg-slate-100 my-0.5 shrink-0 mb-3"></div>
                                    <div className="lg:flex-1 grid grid-cols-2 lg:grid-cols-6 gap-5 min-h-0 lg:overflow-y-auto scrollbar-thin pr-1 pb-4 lg:pb-0 content-start">
                                        {(() => {
                                            // 1. Group consecutive choice_audio fields
                                            const groupedFields = [];
                                            modelFields.forEach((field) => {
                                                const lastGroup = groupedFields[groupedFields.length - 1];
                                                if (field.type === 'choice_audio' && !field.is_hidden) {
                                                    if (lastGroup && lastGroup.type === 'choice_audio_group') {
                                                        lastGroup.fields.push(field);
                                                    } else {
                                                        groupedFields.push({ type: 'choice_audio_group', fields: [field], key: `group_${field.key}` });
                                                    }
                                                } else {
                                                    groupedFields.push(field);
                                                }
                                            });

                                            return groupedFields.map(field => {
                                                // Handle Group Rendering
                                                if (field.type === 'choice_audio_group') {
                                                    const groupLabel = field.fields[0].label; // Use first label
                                                    const hasActivePreview = field.fields.some(f => inputs['choice_audio'] === f.key);

                                                    return (
                                                        <div key={field.key} className="col-span-2 lg:col-span-6 space-y-2.5">
                                                            <div className="text-[10px] font-semibold text-slate-500 w-full flex justify-between">
                                                                <span>{groupLabel}</span>
                                                            </div>
                                                            <div className="flex flex-wrap gap-2">
                                                                {field.fields.map((subField, idx) => {
                                                                    const isActive = inputs['choice_audio'] === subField.key;
                                                                    return (
                                                                        <button
                                                                            key={subField.key}
                                                                            type="button"
                                                                            onClick={() => {
                                                                                // New Logic: Single Key 'choice_audio' holds the selected Subfield Key
                                                                                const newValue = isActive ? "" : subField.key;
                                                                                handleInputChange('choice_audio', newValue);
                                                                            }}
                                                                            className={`px-4 py-2.5 rounded-xl border text-xs font-bold transition-all flex items-center gap-2 ${isActive
                                                                                ? 'bg-sky-500 text-white border-sky-500 shadow-md shadow-sky-200'
                                                                                : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                                                                                }`}
                                                                        >
                                                                            {isActive ? <Icons.PauseCircle size={16} /> : <Icons.PlayCircle size={16} />}
                                                                            {subField.placeholder || `Option ${idx + 1}`}
                                                                        </button>
                                                                    );
                                                                })}
                                                            </div>

                                                            {/* Active Previews Area (Below Buttons) */}
                                                            {hasActivePreview && (
                                                                <div className="grid gap-2 animate-in slide-in-from-top-2 fade-in">
                                                                    {field.fields.map(subField => {
                                                                        // Show preview ONLY if this specific subfield is the one selected in 'choice_audio'
                                                                        if (inputs['choice_audio'] !== subField.key || !subField.default_value) return null;
                                                                        return (
                                                                            <div key={`preview_${subField.key}`} className="p-2.5 bg-sky-50/50 rounded-xl border border-sky-100 flex items-center gap-3">
                                                                                <div className="flex-1 min-w-0">
                                                                                    <p className="text-[10px] font-bold text-sky-800 truncate mb-1">{subField.placeholder || "Audio Preview"}</p>
                                                                                    <audio controls src={subField.default_value} className="w-full h-6 block" style={{ height: '24px' }} />
                                                                                </div>
                                                                            </div>
                                                                        );
                                                                    })}
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                }

                                                if (field.is_hidden) return null;

                                                const isImage = field.type === 'image';
                                                const isAudio = field.type === 'audio'; // Added audio support
                                                const isVideo = field.type === 'video';
                                                const isPdf = field.type === 'pdf';
                                                const isDocx = field.type === 'docx';
                                                const isExcel = field.type === 'excel';
                                                const isTxt = field.type === 'txt';
                                                const isFile = field.type === 'file'; // Generic
                                                const isDropdown = field.type === 'dropdown';

                                                const isUpload = isImage || isAudio || isVideo || isPdf || isDocx || isExcel || isTxt || isFile;

                                                // Grid Logic:
                                                // Mobile (grid-cols-2):
                                                // - Default/Text/Dropdown: col-span-2 (Full)
                                                // - Upload: col-span-1 (Half)
                                                // Desktop (lg:grid-cols-6): 
                                                // - Default/Text: lg:col-span-6 (Full)
                                                // - Upload: lg:col-span-3 (Half)
                                                // - Dropdown: lg:col-span-3 (Half -> 2 per row)

                                                let colSpan = "col-span-2 lg:col-span-6"; // Default Full
                                                if (isUpload) colSpan = "col-span-1 lg:col-span-3"; // Upload Half
                                                if (isDropdown) colSpan = sidebarCollapsed ? "col-span-2 lg:col-span-2" : "col-span-2 lg:col-span-3"; // Dropdown: 3 per row if collapsed, 2 per row if expanded

                                                // Determine Label & Icon
                                                let uploadLabel = "Choose file";
                                                let UploadIcon = Icons.Paperclip;

                                                if (isImage) {
                                                    uploadLabel = "Choose Image";
                                                    UploadIcon = Icons.Image;
                                                } else if (isAudio) {
                                                    uploadLabel = "Choose Audio";
                                                    UploadIcon = Icons.Music;
                                                } else if (isVideo) {
                                                    uploadLabel = "Choose Video";
                                                    UploadIcon = Icons.Video;
                                                } else if (isPdf) {
                                                    uploadLabel = "Upload PDF";
                                                    UploadIcon = Icons.FileText;
                                                } else if (isDocx) {
                                                    uploadLabel = "Upload Word";
                                                    UploadIcon = Icons.FileType2;
                                                } else if (isExcel) {
                                                    uploadLabel = "Upload Excel";
                                                    UploadIcon = Icons.FileSpreadsheet;
                                                } else if (isTxt) {
                                                    uploadLabel = "Upload Text";
                                                    UploadIcon = Icons.FileText;
                                                } else if (isFile) {
                                                    uploadLabel = "Choose File";
                                                    UploadIcon = Icons.File;
                                                }

                                                // Determine Max Length
                                                const maxLength = (['textarea', 'text'].includes(field.type) && field.default_value && /^\d+$/.test(field.default_value))
                                                    ? parseInt(field.default_value)
                                                    : undefined;

                                                // Hide Single Fields Logic (Unified UI)
                                                // If model has file_multi, hide individual image/video/etc fields to avoid duplication
                                                const hasMultiFile = modelFields.some(f => f.type === 'file_multi' && !f.is_hidden);
                                                const isUnifiedType = ['image', 'video', 'audio', 'pdf', 'docx', 'excel', 'txt', 'file'].includes(field.type);
                                                if (hasMultiFile && isUnifiedType && field.type !== 'file_multi') return null;

                                                return (
                                                    <div key={field.key} className={`${colSpan} space-y-2`}>
                                                        <div className="text-[10px] font-semibold text-slate-500 flex justify-between items-center w-full">
                                                            <span>
                                                                {/* Hide Label for File Inputs (moved inside) */}
                                                                {/* Hide Label for File Inputs (moved inside) */}
                                                                {!isUpload && (
                                                                    <>
                                                                        {field.label} {field.is_required && <span className="text-rose-500">*</span>}
                                                                    </>
                                                                )}
                                                                {field.type === 'slider' && (
                                                                    <span className="ml-2 text-xs lg:text-[10px] font-bold text-blue-600 bg-blue-50 px-1.5 py-0 rounded-sm">
                                                                        {inputs[field.key]}
                                                                    </span>
                                                                )}
                                                            </span>
                                                            {field.type === 'textarea' &&
                                                                (field.label.toLowerCase().includes('prompt') || field.label.toLowerCase().includes('promt')) &&
                                                                modelFields.some(f => f.key === 'image_url' || f.key === 'image_url_2') && (
                                                                    <button
                                                                        onClick={() => handleEnhancePrompt(field.key)}
                                                                        disabled={(!inputs[field.key] || inputs[field.key].trim().length === 0) || isMagicLoading}
                                                                        className={`py-1.5 px-3 rounded text-xs font-bold flex items-center gap-1 transition-all shadow-sm border ${(!inputs[field.key] || inputs[field.key].trim().length === 0)
                                                                            ? "bg-slate-50 text-slate-300 border-slate-100 cursor-not-allowed opacity-60"
                                                                            : "bg-sky-600 text-white border-sky-600 hover:bg-sky-700 hover:border-sky-700 hover:shadow-md active:scale-95 shadow-sky-200"
                                                                            }`}
                                                                        title={(!inputs[field.key] || inputs[field.key].trim().length === 0) ? "Ketik prompt terlebih dahulu" : "Enhance Prompt with AI"}
                                                                    >
                                                                        {isMagicLoading ? <Icons.Loader2 size={10} className="animate-spin" /> : <Icons.Wand2 size={10} />}
                                                                        {(inputs[field.key] && inputs[field.key].trim().length > 0) ? "Enhance Prompt" : "Magic Prompt"}
                                                                    </button>
                                                                )}
                                                        </div>
                                                        {
                                                            field.type === 'choice_audio' ? (
                                                                <div className="flex flex-col gap-2">
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => handleInputChange(field.key, inputs[field.key] ? "" : field.default_value)}
                                                                        className={`w-full flex items-center justify-between p-3 rounded-xl border transition-all ${inputs[field.key]
                                                                            ? 'bg-indigo-50 border-indigo-200 text-indigo-700 shadow-sm ring-1 ring-indigo-500/10'
                                                                            : 'bg-white border-slate-200 hover:border-slate-300 hover:bg-slate-50 text-slate-600'
                                                                            }`}
                                                                    >
                                                                        <div className="flex items-center gap-2.5">
                                                                            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${inputs[field.key] ? 'bg-indigo-100' : 'bg-slate-100'}`}>
                                                                                <Icons.Music size={14} className={inputs[field.key] ? 'text-indigo-600' : 'text-slate-400'} />
                                                                            </div>
                                                                            <span className="text-xs font-bold">{field.placeholder || "Select Audio"}</span>
                                                                        </div>
                                                                        {inputs[field.key] && <Icons.CheckCircle2 size={16} className="text-indigo-600" />}
                                                                    </button>
                                                                    {inputs[field.key] && field.default_value && (
                                                                        <div className="p-2 bg-slate-50 rounded-lg border border-slate-200 animate-in slide-in-from-top-1 fade-in">
                                                                            <audio controls src={field.default_value} className="w-full h-8 block" />
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            ) : field.type === 'textarea' ? (
                                                                <div className="flex flex-col gap-2 relative">
                                                                    <textarea
                                                                        className="w-full p-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-blue-500/10 focus:border-blue-500 outline-none text-slate-700 placeholder:text-slate-400 text-xs resize-none h-28 leading-relaxed shadow-sm bg-white scrollbar-thin"
                                                                        value={inputs[field.key] || ""}
                                                                        maxLength={maxLength}
                                                                        onChange={e => handleInputChange(field.key, e.target.value)}
                                                                        placeholder={field.helper_text}
                                                                    />
                                                                    {maxLength && (
                                                                        <div className="absolute bottom-2 right-2 text-[9px] text-slate-400 pointer-events-none bg-white/80 px-1 rounded">
                                                                            {(inputs[field.key] || "").length} / {maxLength}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            ) : field.type === 'slider' ? (
                                                                <div className="w-full pt-2 pb-1">

                                                                    <input
                                                                        type="range"
                                                                        min="1"
                                                                        max={parseInt(field.default_value) || 10}
                                                                        value={inputs[field.key] || 1}
                                                                        onChange={e => handleInputChange(field.key, e.target.value)}
                                                                        className="w-full h-1.5 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                                                                        style={{
                                                                            backgroundImage: `linear-gradient(to right, #2563eb ${((parseInt(inputs[field.key] || 1) - 1) / ((parseInt(field.default_value) || 10) - 1)) * 100}%, #e2e8f0 ${((parseInt(inputs[field.key] || 1) - 1) / ((parseInt(field.default_value) || 10) - 1)) * 100}%)`,
                                                                            backgroundSize: '100% 100%',
                                                                            backgroundRepeat: 'no-repeat'
                                                                        }}
                                                                    />
                                                                    <div className="flex justify-between text-[10px] text-slate-400 mt-1 font-medium">
                                                                        <span>Min: 1</span>
                                                                        <span>Max: {parseInt(field.default_value) || 10}</span>
                                                                    </div>
                                                                    <style>{`
                                                                    input[type=range]::-webkit-slider-thumb {
                                                                        -webkit-appearance: none;
                                                                        height: 12px;
                                                                        width: 12px;
                                                                        border-radius: 50%;
                                                                        background: #2563eb;
                                                                        border: 1.5px solid white;
                                                                        box-shadow: 0 1px 2px rgba(0,0,0,0.2);
                                                                        margin-top: 0px; 
                                                                    }
                                                                    /* Reset standard track if using background gradient on input */
                                                                    input[type=range]::-webkit-slider-runnable-track {
                                                                        -webkit-appearance: none;
                                                                        box-shadow: none;
                                                                        border: none;
                                                                        background: transparent;
                                                                    }
                                                                `}</style>
                                                                </div>
                                                            ) : field.type === 'file_multi' ? (
                                                                <div className="space-y-2">
                                                                    {/* File List */}
                                                                    {inputs[field.key] && inputs[field.key].length > 0 && (
                                                                        <div className="grid grid-cols-2 gap-2">
                                                                            {inputs[field.key].map((v, idx) => {
                                                                                const name = typeof v === 'string' ? getCleanFilename(v) : (v.name || "File");
                                                                                let checkSource = name;
                                                                                if (typeof v === 'string') checkSource = v.split(/[?#]/)[0];
                                                                                else if (v.name) checkSource = v.name;
                                                                                checkSource = checkSource.toLowerCase();

                                                                                const isImg = /\.(jpg|jpeg|png|gif|webp)$/i.test(checkSource);
                                                                                const isVid = /\.(mp4|mov|avi|webm|mkv)$/i.test(checkSource);

                                                                                if (isImg) {
                                                                                    return (
                                                                                        <div key={idx} className="relative group aspect-square rounded-xl overflow-hidden border border-slate-200 bg-slate-100 shadow-sm">
                                                                                            <img src={v} className="w-full h-full object-cover" alt={name} />
                                                                                            <button
                                                                                                onClick={(e) => {
                                                                                                    e.preventDefault();
                                                                                                    setImageEditorAsset({ url: v, targetFieldKey: field.key, targetIndex: idx });
                                                                                                }}
                                                                                                className="absolute top-1 left-1 p-1 bg-white/90 hover:bg-white text-sky-600 rounded-full hover:scale-110 transition-all shadow-sm z-20"
                                                                                                title="Edit Gambar"
                                                                                            >
                                                                                                <Icons.Pencil size={12} />
                                                                                            </button>
                                                                                            <button onClick={() => removeFile(field.key, idx)} className="absolute top-1 right-1 p-1 bg-rose-500 text-white rounded-full hover:bg-rose-600 shadow-sm z-20 hover:scale-105 transition-all">
                                                                                                <Icons.X size={12} />
                                                                                            </button>
                                                                                        </div>
                                                                                    );
                                                                                }
                                                                                if (isVid) {
                                                                                    return (
                                                                                        <div key={idx} className="relative group aspect-square rounded-xl overflow-hidden border border-slate-200 bg-slate-100 shadow-sm">
                                                                                            <video src={v} className="w-full h-full object-cover" muted playsInline />
                                                                                            <div className="absolute top-1 left-1 z-10 pointer-events-none drop-shadow-md bg-black/40 rounded-full p-1"><Icons.Video size={10} className="text-white" /></div>
                                                                                            <button onClick={() => removeFile(field.key, idx)} className="absolute top-1 right-1 p-1 bg-rose-500 text-white rounded-full hover:bg-rose-600 shadow-sm z-20 hover:scale-105 transition-all"><Icons.X size={12} /></button>
                                                                                        </div>
                                                                                    );
                                                                                }
                                                                                const { Icon, color } = getFileIconAndColor(name);
                                                                                return (
                                                                                    <div key={idx} className={`relative group border rounded-lg p-2 flex items-center gap-2 ${color.replace('text-', 'border-').split(' ')[2]} bg-white`}>
                                                                                        <div className={`w-8 h-8 rounded flex items-center justify-center shrink-0 ${color.split(' ')[0]} ${color.split(' ')[1]}`}><Icon size={16} /></div>
                                                                                        <div className="flex-1 min-w-0">
                                                                                            <p className="text-[10px] text-slate-700 font-medium truncate">{name}</p>
                                                                                            {typeof v === 'string' && <p className="text-[8px] text-slate-400">External</p>}
                                                                                        </div>
                                                                                        <button onClick={() => removeFile(field.key, idx)} className="p-1 text-rose-400 hover:text-rose-600 hover:bg-rose-50 rounded transition-colors"><Icons.X size={12} /></button>
                                                                                    </div>
                                                                                );
                                                                            })}
                                                                        </div>
                                                                    )}

                                                                    {/* Upload Button / Menu Trigger */}
                                                                    {(!inputs[field.key] || inputs[field.key].length < (parseInt(field.default_value) || 5)) && (
                                                                        <div className="relative">
                                                                            <input
                                                                                type="file"
                                                                                multiple
                                                                                id={`file-multi-${field.key}`}
                                                                                className="hidden"
                                                                                onChange={(e) => {
                                                                                    const files = Array.from(e.target.files);
                                                                                    // 1. Max Count Check
                                                                                    const rawVal = inputs[field.key];
                                                                                    const currentFiles = Array.isArray(rawVal) ? rawVal : (rawVal ? [rawVal] : []);
                                                                                    const maxFiles = parseInt(field.default_value) || 5;

                                                                                    if (currentFiles.length + files.length > maxFiles) {
                                                                                        showToast(`Maksimal ${maxFiles} file. Sisa slot: ${maxFiles - currentFiles.length}`, 'error');
                                                                                        e.target.value = '';
                                                                                        return;
                                                                                    }

                                                                                    // 2. Size Check (10MB)
                                                                                    const MAX_MB = 10;
                                                                                    if (files.some(f => f.size > MAX_MB * 1024 * 1024)) {
                                                                                        showToast(`Ukuran file maksimal ${MAX_MB}MB per file.`, 'error');
                                                                                        e.target.value = '';
                                                                                        return;
                                                                                    }

                                                                                    handleMultiFileUpload(field.key, e.target.files);
                                                                                }}
                                                                            />

                                                                            <div
                                                                                id={`trigger-${field.key}`}
                                                                                className={`relative flex items-center justify-center gap-2 w-full p-4 border-2 border-dashed border-slate-300 rounded-xl cursor-pointer bg-slate-50 hover:bg-slate-100 transition-colors group ${uploadingFields[field.key] ? 'opacity-50 pointer-events-none' : ''}`}
                                                                                onClick={(e) => {
                                                                                    // Toggle Menu Global
                                                                                    const menuId = `menu-${field.key}`;
                                                                                    if (activeMobileMenuId === menuId) {
                                                                                        setActiveMobileMenuId(null);
                                                                                        setMenuAnchor(null);
                                                                                    } else {
                                                                                        // Capture position
                                                                                        const rect = e.currentTarget.getBoundingClientRect();
                                                                                        setMenuAnchor(rect);
                                                                                        setActiveMobileMenuId(menuId);
                                                                                    }
                                                                                    e.preventDefault();
                                                                                }}
                                                                                onDragOver={handleDragOver}
                                                                                onDrop={(e) => handleDrop(e, field.key)}
                                                                            >
                                                                                {uploadingFields[field.key] ? (
                                                                                    <>
                                                                                        <Icons.Loader2 size={16} className="animate-spin text-blue-500" />
                                                                                        <span className="text-xs text-slate-500">Uploading...</span>
                                                                                    </>
                                                                                ) : (
                                                                                    <>
                                                                                        <Icons.UploadCloud size={16} className="text-slate-400 group-hover:text-blue-500 transition-colors" />
                                                                                        <span className="text-xs font-semibold text-slate-600 group-hover:text-slate-900">Upload Files (Max {parseInt(field.default_value) || 5})</span>
                                                                                    </>
                                                                                )}

                                                                            </div>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            ) : isUpload ? (
                                                                <div className="relative">
                                                                    <input
                                                                        type="file"
                                                                        id={`file-${field.key}`}
                                                                        className="hidden"
                                                                        onChange={e => handleFileUpload(e, field.key)}
                                                                        accept={isImage ? "image/*" : isAudio ? "audio/*" : isVideo ? "video/mp4,video/*" : isPdf ? ".pdf" : isDocx ? ".doc,.docx" : isExcel ? ".xls,.xlsx,.csv" : isTxt ? ".txt" : isFile ? ".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt" : "*/*"}
                                                                    />
                                                                    <div
                                                                        id={`trigger-${field.key}`}
                                                                        onClick={(e) => {
                                                                            e.preventDefault();
                                                                            const menuId = `menu-${field.key}`;
                                                                            if (activeMobileMenuId === menuId) {
                                                                                setActiveMobileMenuId(null);
                                                                                setMenuAnchor(null);
                                                                            } else {
                                                                                const rect = e.currentTarget.getBoundingClientRect();
                                                                                setMenuAnchor(rect);
                                                                                setActiveMobileMenuId(menuId);
                                                                            }
                                                                        }}
                                                                        onDragOver={handleDragOver}
                                                                        onDrop={(e) => handleDrop(e, field.key)}
                                                                        className={`group relative flex flex-col items-center justify-center w-full aspect-[4/3] border-2 border-dashed border-slate-300 rounded-xl cursor-pointer bg-slate-50 hover:bg-slate-100 transition-colors overflow-hidden ${uploadingFields[field.key] ? 'opacity-50 pointer-events-none' : ''}`}
                                                                    >
                                                                        {uploadingFields[field.key] ? (
                                                                            <div className="flex flex-col items-center justify-center p-2 text-slate-500">
                                                                                <Icons.Loader2 size={24} className="animate-spin mb-2 text-blue-500" />
                                                                                <p className="text-[10px] font-medium">Uploading...</p>
                                                                            </div>
                                                                        ) : inputs[field.key] ? (
                                                                            (isImage || isVideo || (typeof inputs[field.key] === 'string' && (/\.(jpg|jpeg|png|gif|webp|mp4|mov|avi|webm)(\?.*)?$/i.test(inputs[field.key]) || inputs[field.key].startsWith('blob:')))) ? (
                                                                                <div className="w-full h-full relative group/preview">
                                                                                    {isImage || (typeof inputs[field.key] === 'string' && (/\.(jpg|jpeg|png|gif|webp)(\?.*)?$/i.test(inputs[field.key]) || inputs[field.key].startsWith('blob:'))) ? (
                                                                                        <img
                                                                                            src={inputs[field.key]}
                                                                                            alt="Selected"
                                                                                            className="w-full h-full object-cover rounded-xl"
                                                                                        />
                                                                                    ) : (
                                                                                        <video
                                                                                            src={inputs[field.key]}
                                                                                            className="w-full h-full object-cover rounded-xl"
                                                                                            controls
                                                                                            muted
                                                                                        />
                                                                                    )}
                                                                                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover/preview:opacity-100 transition-opacity flex items-center justify-center rounded-xl pointer-events-none">
                                                                                        <p className="text-white text-[10px] font-medium">Change</p>
                                                                                    </div>
                                                                                    <button
                                                                                        onClick={(e) => {
                                                                                            e.preventDefault();
                                                                                            e.stopPropagation();
                                                                                            handleInputChange(field.key, "");
                                                                                        }}
                                                                                        className="absolute top-1 right-1 p-1 bg-rose-500 text-white rounded-full hover:bg-rose-600 shadow-sm z-20 hover:scale-105 transition-all"
                                                                                        title="Remove File"
                                                                                    >
                                                                                        <Icons.X size={12} />
                                                                                    </button>
                                                                                    {(isImage || (typeof inputs[field.key] === 'string' && (/\.(jpg|jpeg|png|gif|webp)(\?.*)?$/i.test(inputs[field.key]) || inputs[field.key].startsWith('blob:')))) && (
                                                                                        <button
                                                                                            onClick={(e) => {
                                                                                                e.preventDefault();
                                                                                                e.stopPropagation();
                                                                                                setImageEditorAsset({ url: inputs[field.key], targetFieldKey: field.key });
                                                                                            }}
                                                                                            className="absolute top-1 left-1 p-1.5 bg-white/90 hover:bg-white text-sky-600 rounded-full hover:scale-110 transition-all shadow-sm z-20"
                                                                                            title="Edit Gambar"
                                                                                        >
                                                                                            <Icons.Pencil size={12} />
                                                                                        </button>
                                                                                    )}
                                                                                    {/* Bottom Label Overlay */}
                                                                                    <div className="absolute bottom-3 left-3 z-10 pointer-events-none drop-shadow-md">
                                                                                        <span className="text-black text-xs font-bold bg-white/80 px-2 py-1 rounded">{field.label}</span>
                                                                                    </div>
                                                                                </div>
                                                                            ) : (
                                                                                <div className="flex flex-col items-center p-2 text-center relative group/preview">
                                                                                    <button
                                                                                        onClick={(e) => {
                                                                                            e.preventDefault();
                                                                                            e.stopPropagation();
                                                                                            handleInputChange(field.key, "");
                                                                                        }}
                                                                                        className="absolute -top-1 -right-1 p-1 bg-rose-500 text-white rounded-full hover:bg-rose-600 shadow-sm z-20 hover:scale-105 transition-all opacity-0 group-hover/preview:opacity-100"
                                                                                        title="Remove File"
                                                                                    >
                                                                                        <Icons.X size={10} />
                                                                                    </button>
                                                                                    <div className="w-6 h-6 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mb-1">
                                                                                        <Icons.Check size={12} />
                                                                                    </div>
                                                                                    <p className="text-[9px] text-slate-500 max-w-[100px] truncate">
                                                                                        Selected
                                                                                    </p>
                                                                                    {typeof inputs[field.key] === 'string' && inputs[field.key].includes('http') && (
                                                                                        <p className="text-[8px] text-slate-400 mt-0.5 max-w-[100px] truncate">{inputs[field.key].split('/').pop()}</p>
                                                                                    )}
                                                                                    <div className="absolute bottom-2 left-0 w-full text-center pointer-events-none">
                                                                                        <span className="text-slate-600 text-[10px] font-bold">{field.label}</span>
                                                                                    </div>
                                                                                </div>
                                                                            )
                                                                        ) : (
                                                                            <>
                                                                                {/* Default State: Icon Top Left + Label Bottom Left */}
                                                                                <div className="absolute inset-0 flex flex-col justify-between p-4 pointer-events-none group-hover:opacity-0 transition-opacity duration-200">
                                                                                    {/* Icon */}
                                                                                    <div className="relative w-8 h-8">
                                                                                        <div className="w-8 h-8 rounded-lg bg-white border border-slate-200 flex items-center justify-center shadow-sm relative z-10">
                                                                                            <UploadIcon size={16} className="text-slate-400" />
                                                                                        </div>
                                                                                        {/* Badges for Start/End */}
                                                                                        {field.key === 'image_url' && (
                                                                                            <div className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-white border border-slate-100 flex items-center justify-center shadow-sm z-20">
                                                                                                <Icons.Play size={8} className="text-slate-600 fill-slate-600" />
                                                                                            </div>
                                                                                        )}
                                                                                        {field.key === 'image_url_2' && (
                                                                                            <div className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-white border border-slate-100 flex items-center justify-center shadow-sm z-20">
                                                                                                <Icons.Square size={8} className="text-slate-600 fill-slate-600" />
                                                                                            </div>
                                                                                        )}
                                                                                    </div>

                                                                                    {/* Label (Smaller & Required Mark) */}
                                                                                    <span className="text-[10px] font-bold text-slate-600">
                                                                                        {field.label} {field.is_required && <span className="text-rose-500">*</span>}
                                                                                    </span>
                                                                                </div>

                                                                                {/* Hover State: Centered Text & Icon (Reveal) */}
                                                                                <div className="flex flex-col items-center justify-center p-2 opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-y-2 group-hover:translate-y-0 relative z-20 bg-slate-50/80 backdrop-blur-sm rounded-xl inset-1 absolute">
                                                                                    <div className="w-8 h-8 rounded-full border border-dashed border-slate-400 flex items-center justify-center mb-1.5 text-slate-400">
                                                                                        <UploadIcon size={14} />
                                                                                    </div>
                                                                                    <p className="mb-0.5 text-[10px] text-slate-700 font-semibold text-center leading-tight">{uploadLabel}</p>
                                                                                    <p className="text-[8px] text-slate-400 text-center leading-tight">Drag & drop</p>
                                                                                </div>
                                                                            </>
                                                                        )}
                                                                    </div>

                                                                    {/* SWAP BUTTON (Placed OUTSIDE label to avoid overflow clipping) */}
                                                                    {
                                                                        field.key === 'image_url' && modelFields.some(f => f.key === 'image_url_2') && (
                                                                            <button
                                                                                onClick={(e) => {
                                                                                    e.preventDefault();
                                                                                    e.stopPropagation();
                                                                                    const val1 = inputs['image_url'];
                                                                                    const val2 = inputs['image_url_2'];
                                                                                    if (val1 && val2) {
                                                                                        setInputs(prev => ({ ...prev, image_url: val2, image_url_2: val1 }));
                                                                                    }
                                                                                }}
                                                                                disabled={!inputs['image_url'] || !inputs['image_url_2']}
                                                                                className={`absolute top-1/2 -right-[19px] -translate-y-1/2 z-[50] w-7 h-7 rounded-full border shadow-md flex items-center justify-center transition-all ${(inputs['image_url'] && inputs['image_url_2'])
                                                                                    ? "bg-white border-blue-200 text-blue-600 hover:scale-110 hover:bg-blue-50 cursor-pointer"
                                                                                    : "bg-slate-50 border-slate-200 text-slate-300 cursor-not-allowed"
                                                                                    }`}
                                                                                title="Switch Images"
                                                                            >
                                                                                <Icons.ArrowRightLeft size={12} />
                                                                            </button>
                                                                        )
                                                                    }
                                                                </div>
                                                            ) : field.type === 'dropdown' ? (
                                                                // DROPDOWN TYPE
                                                                (() => {
                                                                    // Parse options from field.options
                                                                    let dropdownOptions = [];



                                                                    try {
                                                                        if (typeof field.options === 'string' && field.options.trim()) {
                                                                            dropdownOptions = JSON.parse(field.options);
                                                                        } else if (Array.isArray(field.options)) {
                                                                            dropdownOptions = field.options;
                                                                        } else if (field.options && typeof field.options === 'object') {
                                                                            // Handle if options is already a parsed object/array
                                                                            if (Array.isArray(field.options)) {
                                                                                dropdownOptions = field.options;
                                                                            }
                                                                        }
                                                                    } catch (e) {
                                                                        // console.warn('[Dropdown] Failed to parse options:', e);
                                                                    }

                                                                    // Ensure array format
                                                                    if (!Array.isArray(dropdownOptions)) {
                                                                        dropdownOptions = [];
                                                                    }



                                                                    // --- CUSTOM MODE CHECK ---
                                                                    const isCustomizable = field.default_value === 'active';
                                                                    const isCustomMode = customFieldModes[field.key];

                                                                    // --- SMART VALUE RECOVERY LOGIC ---
                                                                    const rawValue = inputs[field.key];
                                                                    const hasOptions = dropdownOptions.length > 0;
                                                                    const isValidOption = (val) => dropdownOptions.some(o => o.value == val); // loose equality

                                                                    let effectiveValue = "";
                                                                    let effectiveLabel = "";

                                                                    if (isCustomMode) {
                                                                        // In Custom Mode, raw value is the truth
                                                                        effectiveValue = rawValue || "";
                                                                    } else if (hasOptions) {
                                                                        // 1. Check current input value
                                                                        if (rawValue !== undefined && rawValue !== null && rawValue !== "" && isValidOption(rawValue)) {
                                                                            effectiveValue = rawValue;
                                                                        }
                                                                        // 2. Check DB Default Value (if valid & NOT 'active' keyword)
                                                                        else if (field.default_value && field.default_value !== 'active' && isValidOption(field.default_value)) {
                                                                            effectiveValue = field.default_value;
                                                                        }
                                                                        // 3. Last Resort: First Option
                                                                        else {
                                                                            effectiveValue = dropdownOptions[0].value;
                                                                        }

                                                                        const activeOpt = dropdownOptions.find(o => o.value == effectiveValue);
                                                                        effectiveLabel = activeOpt ? activeOpt.label : (field.placeholder || "Pilih...");
                                                                    } else {
                                                                        effectiveLabel = field.placeholder || "Pilih...";
                                                                    }

                                                                    // Auto-correct state (ONLY IF NOT IN CUSTOM MODE)
                                                                    if (!isCustomMode && hasOptions && rawValue != effectiveValue) {
                                                                        // setTimeout(() => handleInputChange(field.key, effectiveValue), 0);
                                                                    }

                                                                    const handleSelect = (val) => {
                                                                        if (val === '__custom_trigger__') {
                                                                            setCustomFieldModes(prev => ({ ...prev, [field.key]: true }));
                                                                            handleInputChange(field.key, ""); // Reset for fresh typing
                                                                        } else {
                                                                            handleInputChange(field.key, val);
                                                                        }
                                                                        setActiveDropdown(null);
                                                                        dropdownTriggerRef.current = null;
                                                                    };

                                                                    // --- RENDER CUSTOM INPUT MODE ---
                                                                    if (isCustomMode) {
                                                                        return (
                                                                            <div className="relative group">
                                                                                <textarea
                                                                                    value={effectiveValue}
                                                                                    onChange={(e) => {
                                                                                        const val = e.target.value.slice(0, 500); // Max 500 chars
                                                                                        handleInputChange(field.key, val);
                                                                                    }}
                                                                                    placeholder="Ketik nilai kustom..."
                                                                                    className="w-full text-[11px] border border-blue-500 ring-2 ring-blue-500/10 rounded-xl px-3 py-2 pr-8 min-h-[38px] resize-none focus:outline-none bg-white text-slate-700 leading-snug"
                                                                                    rows={1}
                                                                                    style={{ minHeight: '34px', height: 'auto' }} // Auto grow ideally, but fixed for now
                                                                                />
                                                                                <button
                                                                                    onClick={() => {
                                                                                        setCustomFieldModes(prev => ({ ...prev, [field.key]: false }));
                                                                                        handleInputChange(field.key, ""); // Will trigger auto-correct to first option
                                                                                    }}
                                                                                    className="absolute right-2 top-2 text-slate-400 hover:text-rose-500 bg-white rounded-full"
                                                                                    title="Kembali ke Pilihan"
                                                                                >
                                                                                    <Icons.X size={14} />
                                                                                </button>
                                                                                <div className="absolute -bottom-4 right-0 text-[9px] text-slate-400 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity">
                                                                                    {effectiveValue.length}/500
                                                                                </div>
                                                                            </div>
                                                                        );
                                                                    }

                                                                    // --- RENDER DROPDOWN MODE ---
                                                                    const activeLabel = effectiveLabel;
                                                                    const currentValue = effectiveValue; // For rendering logic consistency
                                                                    const isOpen = activeDropdown === field.key;

                                                                    // Handle Trigger Click: Calculate Position & Open
                                                                    const handleTriggerClick = (e) => {
                                                                        if (isOpen) {
                                                                            setActiveDropdown(null);
                                                                            dropdownTriggerRef.current = null;
                                                                        } else {
                                                                            // Store trigger element reference for scroll position updates
                                                                            dropdownTriggerRef.current = e.currentTarget;

                                                                            const rect = e.currentTarget.getBoundingClientRect();
                                                                            const isMobile = window.innerWidth < 768;

                                                                            // DESKTOP SMART SIZING LOGIC
                                                                            let finalWidth = rect.width;
                                                                            let finalLeft = rect.left;

                                                                            if (!isMobile) {
                                                                                // Make it at least 240px wide for "leluasa" feel, or wider if trigger is wider
                                                                                const MIN_WIDTH = 240;
                                                                                finalWidth = Math.max(rect.width, MIN_WIDTH);

                                                                                // Check Right Edge Overflow
                                                                                // If menu goes off-screen (left + width > screen_width), align it to the RIGHT edge of trigger instead
                                                                                if (finalLeft + finalWidth > window.innerWidth - 20) {
                                                                                    // Align right edge of menu with right edge of trigger
                                                                                    finalLeft = rect.right - finalWidth;
                                                                                }
                                                                            }

                                                                            // Check space below for auto-flip (Desktop only)
                                                                            const spaceBelow = window.innerHeight - rect.bottom;
                                                                            const openUpwards = !isMobile && spaceBelow < 250; // Threshold 250px

                                                                            setDropdownPos({
                                                                                top: rect.bottom + 4,
                                                                                left: finalLeft,
                                                                                width: finalWidth,
                                                                                bottom: window.innerHeight - rect.top + 4, // For upward opening
                                                                                openUpwards,
                                                                                isMobile
                                                                            });
                                                                            setActiveDropdown(field.key);
                                                                        }
                                                                    };

                                                                    return (
                                                                        <div className="relative">
                                                                            {/* Trigger Button */}
                                                                            <div
                                                                                onClick={handleTriggerClick}
                                                                                className={`w-full h-8 px-3 rounded-xl border flex items-center justify-between cursor-pointer transition-all duration-200 bg-white shadow-sm hover:border-blue-300 hover:shadow-md ${isOpen ? 'border-blue-500 ring-2 ring-blue-500/10' : 'border-slate-200'}`}
                                                                            >
                                                                                <span className={`text-[11px] font-medium truncate leading-none ${!currentValue && !field.default_value ? 'text-slate-400' : 'text-slate-700'}`}>
                                                                                    {activeLabel}
                                                                                </span>
                                                                                <Icons.ChevronDown
                                                                                    size={14}
                                                                                    className={`text-slate-400 shrink-0 ml-1 transition-transform duration-300 ${isOpen ? 'rotate-180 text-blue-500' : ''}`}
                                                                                />
                                                                            </div>

                                                                            {/* Portal Options Menu (Responsive) */}
                                                                            {isOpen && ReactDOM.createPortal(
                                                                                <>
                                                                                    {/* Backdrop */}
                                                                                    <div
                                                                                        className="fixed inset-0 z-[60] bg-black/20 backdrop-blur-[1px] transition-opacity"
                                                                                        onClick={() => { setActiveDropdown(null); dropdownTriggerRef.current = null; }}
                                                                                    />

                                                                                    {/* Menu Content */}
                                                                                    <div
                                                                                        className={`fixed z-[70] bg-white shadow-2xl border border-slate-100 overflow-hidden flex flex-col
                                                                                        ${dropdownPos.isMobile
                                                                                                ? "bottom-0 left-0 right-0 w-full rounded-t-2xl animate-in slide-in-from-bottom duration-300" // Mobile Bottom Sheet
                                                                                                : "rounded-xl animate-in fade-in zoom-in-95 duration-200" // Desktop Dropdown
                                                                                            }
                                                                                    `}
                                                                                        style={dropdownPos.isMobile ? {
                                                                                            maxHeight: '70vh'
                                                                                        } : {
                                                                                            top: dropdownPos.openUpwards ? 'auto' : dropdownPos.top,
                                                                                            bottom: dropdownPos.openUpwards ? dropdownPos.bottom : 'auto',
                                                                                            left: dropdownPos.left,
                                                                                            width: dropdownPos.width,
                                                                                            maxHeight: '300px',
                                                                                            transformOrigin: dropdownPos.openUpwards ? 'bottom' : 'top'
                                                                                        }}
                                                                                    >
                                                                                        {/* Mobile Header Line */}
                                                                                        {dropdownPos.isMobile && (
                                                                                            <div className="w-full flex justify-center pt-3 pb-1">
                                                                                                <div className="w-12 h-1.5 bg-slate-200 rounded-full"></div>
                                                                                            </div>
                                                                                        )}

                                                                                        <div className="overflow-y-auto scrollbar-thin p-1" style={{ maxHeight: dropdownPos.isMobile ? 'calc(70vh - 20px)' : '300px' }}>
                                                                                            {dropdownOptions.length === 0 ? (
                                                                                                <div className="p-4 text-center text-sm text-slate-400 italic">
                                                                                                    Tidak ada opsi tersedia
                                                                                                </div>
                                                                                            ) : (
                                                                                                <>
                                                                                                    {dropdownOptions.map((opt, idx) => {
                                                                                                        const isSelected = opt.value === effectiveValue;
                                                                                                        return (
                                                                                                            <div
                                                                                                                key={idx}
                                                                                                                onClick={() => handleSelect(opt.value)}
                                                                                                                className={`px-3 py-2.5 rounded-lg text-xs font-medium cursor-pointer flex items-center justify-between transition-colors mb-0.5 last:mb-0 
                                                                                                                ${isSelected ? 'bg-blue-50 text-blue-700' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'}
                                                                                                                ${dropdownPos.isMobile ? 'py-4 px-4 text-sm border-b border-slate-50 last:border-0' : ''}
                                                                                                            `}
                                                                                                            >
                                                                                                                <span className="truncate pr-2">{opt.label}</span>
                                                                                                                {isSelected && <Icons.Check size={dropdownPos.isMobile ? 18 : 14} className="text-blue-600 shrink-0" />}
                                                                                                            </div>
                                                                                                        );
                                                                                                    })}

                                                                                                    {/* CUSTOM OPTION (IF ENABLED) */}
                                                                                                    {isCustomizable && (
                                                                                                        <>
                                                                                                            <div className="h-px bg-slate-100 my-1 mx-2"></div>
                                                                                                            <div
                                                                                                                onClick={() => handleSelect('__custom_trigger__')}
                                                                                                                className={`px-3 py-2.5 rounded-lg text-xs font-bold text-blue-600 cursor-pointer hover:bg-blue-50 flex items-center gap-2 transition-colors
                                                                                                                ${dropdownPos.isMobile ? 'py-4 px-4 text-sm' : ''}
                                                                                                             `}
                                                                                                            >
                                                                                                                <Icons.Edit3 size={dropdownPos.isMobile ? 16 : 14} />
                                                                                                                <span>Kustom / Isi Sendiri</span>
                                                                                                            </div>
                                                                                                        </>
                                                                                                    )}
                                                                                                </>
                                                                                            )}
                                                                                        </div>
                                                                                    </div>
                                                                                </>,
                                                                                document.body
                                                                            )}
                                                                        </div>
                                                                    );
                                                                })()
                                                            ) : field.type === 'button_group' ? (
                                                                (() => {
                                                                    // --- BUTTON GROUP COMPONENT LOGIC ---
                                                                    let dropdownOptions = [];

                                                                    // ROBUST PARSING (Same as Dropdown)
                                                                    try {
                                                                        if (Array.isArray(field.options)) {
                                                                            dropdownOptions = field.options;
                                                                        } else if (typeof field.options === 'string' && field.options.trim()) {
                                                                            dropdownOptions = JSON.parse(field.options);
                                                                        } else if (field.options && typeof field.options === 'object') {
                                                                            // Handle if it's an object wrapping an array or similar
                                                                            if (Array.isArray(field.options)) {
                                                                                dropdownOptions = field.options;
                                                                            } else {
                                                                                // Fallback: try to see if it has values or something, otherwise empty
                                                                                dropdownOptions = [];
                                                                            }
                                                                        }
                                                                    } catch (e) {
                                                                        // console.warn('Button Group options parse fail:', e);
                                                                        dropdownOptions = [];
                                                                    }

                                                                    if (!Array.isArray(dropdownOptions)) dropdownOptions = [];

                                                                    const isCustomizable = field.default_value === 'active';
                                                                    const isCustomMode = customFieldModes[field.key];
                                                                    const rawValue = inputs[field.key];
                                                                    const hasOptions = dropdownOptions.length > 0;

                                                                    // Helper
                                                                    const isValidOption = (val) => dropdownOptions.some(o => o.value == val);

                                                                    let effectiveValue = "";
                                                                    if (isCustomMode) {
                                                                        effectiveValue = rawValue || "";
                                                                    } else if (hasOptions) {
                                                                        if (rawValue && isValidOption(rawValue)) effectiveValue = rawValue;
                                                                        else if (field.default_value && field.default_value !== 'active' && isValidOption(field.default_value)) effectiveValue = field.default_value;
                                                                        // else leave empty (no forced default)
                                                                    }

                                                                    // Auto-correct (Only if rawValue is present but invalid, or if we need to sync default)
                                                                    // We don't want to force-set if it's just empty and allowed to be empty
                                                                    if (!isCustomMode && hasOptions && rawValue && rawValue != effectiveValue) {
                                                                        // setTimeout(() => handleInputChange(field.key, effectiveValue), 0);
                                                                    }

                                                                    const handleSelect = (val) => {
                                                                        if (val === '__custom_trigger__') {
                                                                            setCustomFieldModes(prev => ({ ...prev, [field.key]: true }));
                                                                            handleInputChange(field.key, "");
                                                                        } else {
                                                                            // Toggle logic: if clicking already selected, deselect
                                                                            if (val === effectiveValue) {
                                                                                handleInputChange(field.key, "");
                                                                            } else {
                                                                                handleInputChange(field.key, val);
                                                                            }
                                                                        }
                                                                    };

                                                                    // --- RENDER CUSTOM MODE (TEXTAREA) ---
                                                                    if (isCustomMode) {
                                                                        return (
                                                                            <div className="relative group">
                                                                                <textarea
                                                                                    value={effectiveValue}
                                                                                    onChange={(e) => handleInputChange(field.key, e.target.value.slice(0, 500))}
                                                                                    placeholder="Ketik nilai kustom..."
                                                                                    className="w-full text-[11px] border border-sky-500 ring-2 ring-sky-500/10 rounded-xl px-3 py-2 pr-8 min-h-[38px] resize-none focus:outline-none bg-white text-slate-700 leading-snug"
                                                                                    rows={1}
                                                                                    autoFocus
                                                                                />
                                                                                <button
                                                                                    onClick={() => {
                                                                                        setCustomFieldModes(prev => ({ ...prev, [field.key]: false }));
                                                                                        handleInputChange(field.key, "");
                                                                                    }}
                                                                                    className="absolute right-2 top-2 text-slate-400 hover:text-rose-500 bg-white rounded-full"
                                                                                >
                                                                                    <Icons.X size={14} />
                                                                                </button>
                                                                                <div className="absolute -bottom-4 right-0 text-[9px] text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity">{effectiveValue.length}/500</div>
                                                                            </div>
                                                                        );
                                                                    }

                                                                    // --- RENDER BUTTONS MODE ---
                                                                    return (
                                                                        <div className="flex flex-wrap gap-2">
                                                                            {dropdownOptions.map((opt, idx) => {
                                                                                const isSelected = opt.value === effectiveValue;
                                                                                return (
                                                                                    <button
                                                                                        key={idx}
                                                                                        onClick={(e) => {
                                                                                            e.preventDefault();
                                                                                            handleSelect(opt.value);
                                                                                        }}
                                                                                        className={`px-4 py-2 rounded-lg text-xs font-medium border transition-all transform active:scale-95 flex-auto text-center min-w-[80px]
                                                                                    ${isSelected
                                                                                                ? 'bg-sky-500 text-white border-sky-500 shadow-md shadow-sky-500/20'
                                                                                                : 'bg-white text-slate-600 border-slate-200 hover:border-sky-300 hover:text-sky-600 hover:shadow-sm'
                                                                                            }
                                                                                `}
                                                                                    >
                                                                                        {opt.label}
                                                                                    </button>
                                                                                );
                                                                            })}
                                                                            {isCustomizable && (
                                                                                <button
                                                                                    onClick={(e) => {
                                                                                        e.preventDefault();
                                                                                        handleSelect('__custom_trigger__');
                                                                                    }}
                                                                                    className="px-4 py-2 rounded-lg text-xs font-bold border border-dashed border-sky-300 text-sky-600 hover:bg-sky-50 transition-colors flex items-center justify-center gap-1.5 flex-auto min-w-[80px]"
                                                                                >
                                                                                    <Icons.Edit3 size={12} />
                                                                                    <span>Kustom</span>
                                                                                </button>
                                                                            )}
                                                                        </div>
                                                                    );
                                                                })()
                                                            ) : (
                                                                <div className="relative">
                                                                    <input type={field.type} maxLength={maxLength} className="w-full h-8 px-2.5 rounded-lg border border-slate-200 focus:ring-1 focus:ring-blue-500/20 focus:border-blue-500 outline-none text-slate-700 text-xs" value={inputs[field.key] || ""} onChange={e => handleInputChange(field.key, e.target.value)} placeholder={field.helper_text} />
                                                                    {maxLength && (
                                                                        <div className="absolute top-1/2 -translate-y-1/2 right-2 text-[9px] text-slate-300 pointer-events-none">
                                                                            {(inputs[field.key] || "").length}/{maxLength}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            )
                                                        }
                                                    </div>
                                                );
                                            })
                                        })()}
                                    </div>
                                    <div className="pt-3 mt-auto shrink-0 border-t border-slate-100 relative">
                                        {/* CUSTOM COMPACT CONFIGURATION CHIPS */}
                                        {(() => {
                                            // Normalization for Render
                                            let activeConfigs = selectedModel?.configuration_options;
                                            if (!Array.isArray(activeConfigs)) {
                                                if (activeConfigs && Array.isArray(activeConfigs.categories)) {
                                                    activeConfigs = activeConfigs.categories;
                                                } else {
                                                    activeConfigs = [];
                                                }
                                            }

                                            if (!activeConfigs || activeConfigs.length === 0) return null;

                                            return (
                                                <div className="mb-4 grid gap-2 grid-flow-col auto-cols-fr items-center">
                                                    {openConfigCatId && <CloseBackdrop />}

                                                    {activeConfigs.map(cat => {
                                                        const CatIcon = getCategoryIcon(cat.name);
                                                        const selectedOptId = selectedConfigs[cat.id];
                                                        const selectedOpt = cat.options.find(o => o.id == selectedOptId) || cat.options[0];
                                                        const isOpen = openConfigCatId === cat.id;

                                                        // Determine main icon for the trigger button
                                                        // If we have a special option icon (like ratio shape), use that. Otherwise use category icon.
                                                        const OptionShape = selectedOpt ? renderOptionIcon(cat.name, selectedOpt.label, isOpen || true) : null;

                                                        return (
                                                            <div key={cat.id} className="relative z-50 w-full min-w-0">
                                                                <button
                                                                    type="button"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        setOpenConfigCatId(isOpen ? null : cat.id);
                                                                    }}
                                                                    title={selectedOpt ? (selectedOpt.description || selectedOpt.label) : "Select"}
                                                                    className={`w-full flex items-center justify-center gap-2 px-3 py-1.5 rounded-lg border text-xs font-semibold transition-all select-none
                                                                                        ${isOpen
                                                                            ? "bg-slate-800 text-white border-slate-800 ring-4 ring-slate-100"
                                                                            : "bg-white text-slate-700 border-slate-200 hover:border-slate-300 hover:bg-slate-50"
                                                                        }`}
                                                                >
                                                                    {OptionShape ? (
                                                                        <div className={isOpen ? "brightness-200 grayscale" : ""}>{OptionShape}</div>
                                                                    ) : (
                                                                        <CatIcon size={14} className={`shrink-0 ${isOpen ? "text-slate-200" : "text-slate-400"}`} />
                                                                    )}

                                                                    <span>{selectedOpt ? selectedOpt.label : "Select"}</span>
                                                                </button>

                                                                {/* DROPDOWN MENU (Upwards) */}
                                                                {
                                                                    isOpen && (
                                                                        <div className="absolute bottom-full left-0 mb-2 w-56 bg-white rounded-xl border border-slate-100 shadow-xl shadow-slate-200/50 p-1 flex flex-col gap-0.5 animate-in fade-in zoom-in-95 duration-200 origin-bottom-left z-[100]">
                                                                            <div className="px-3 py-2 border-b border-slate-50 mb-0.5 bg-slate-50/50 rounded-t-lg">
                                                                                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">{cat.name}</p>
                                                                            </div>
                                                                            <div className="max-h-60 overflow-y-auto scrollbar-none p-1">
                                                                                {cat.options.map(opt => {
                                                                                    const isSelected = opt.id === selectedOptId;
                                                                                    const ItemShape = renderOptionIcon(cat.name, opt.label, isSelected);

                                                                                    return (
                                                                                        <button
                                                                                            key={opt.id}
                                                                                            type="button"
                                                                                            onClick={(e) => {
                                                                                                e.stopPropagation();
                                                                                                handleConfigChange(cat.id, opt.id);
                                                                                                setOpenConfigCatId(null);
                                                                                            }}
                                                                                            className={`w-full text-left px-3 py-2 rounded-lg text-xs flex items-center justify-between transition-colors group
                                                                                        ${isSelected
                                                                                                    ? "bg-sky-50 text-sky-700"
                                                                                                    : "text-slate-600 hover:bg-slate-50"
                                                                                                }`}
                                                                                        >
                                                                                            <div className="flex items-center gap-3">
                                                                                                {/* Show Shape Icon if valid, otherwise nothing (or could show check?) */}
                                                                                                {ItemShape && <div className="w-4 flex justify-center shrink-0">{ItemShape}</div>}

                                                                                                <span className={`font-bold ${isSelected ? 'text-sky-700' : 'text-slate-700'}`}>{opt.label}</span>
                                                                                                <span className={`text-[10px] ${isSelected ? 'text-sky-500' : 'text-slate-400 group-hover:text-slate-500'}`}>{opt.description}</span>
                                                                                            </div>
                                                                                            {isSelected && <Icons.Check size={12} className="text-sky-600" />}
                                                                                        </button>
                                                                                    );
                                                                                })}
                                                                            </div>
                                                                        </div>
                                                                    )
                                                                }
                                                            </div>
                                                        );
                                                    })}
                                                </div >
                                            );
                                        })()}

                                        {/* Pinned Conversation Context Field (Chat Mode) */}
                                        {isChatMode && pinnedConversation && (
                                            <div className="col-span-2 w-full mt-4 mb-6 animate-in slide-in-from-top duration-300 order-last">
                                                <div className="bg-white/80 backdrop-blur-sm border border-indigo-100 rounded-xl overflow-hidden shadow-sm ring-1 ring-indigo-50/50 hover:shadow-md transition-shadow">
                                                    {/* Premium Header */}
                                                    <div className="px-3 py-2 bg-gradient-to-r from-indigo-50/80 to-blue-50/80 border-b border-indigo-50 flex justify-between items-center">
                                                        <label className="text-[10px] font-bold text-indigo-900 flex items-center gap-1.5 tracking-wide uppercase">
                                                            <div className="p-1 bg-indigo-100 rounded-md text-indigo-600">
                                                                <Icons.Pin size={10} className="rotate-45" />
                                                            </div>
                                                            Pinned Context
                                                        </label>
                                                        <button
                                                            onClick={() => setPinnedConversation(null)}
                                                            className="text-[10px] font-semibold text-slate-400 hover:text-rose-500 transition-colors flex items-center gap-1 px-2 py-1 rounded hover:bg-rose-50"
                                                        >
                                                            <Icons.X size={12} /> Unpin
                                                        </button>
                                                    </div>

                                                    {/* Content Area */}
                                                    <div className="p-0">
                                                        <textarea
                                                            value={pinnedConversation}
                                                            onChange={(e) => setPinnedConversation(e.target.value)}
                                                            className="w-full h-28 p-3 bg-white/50 text-xs text-slate-600 font-mono outline-none resize-y border-none focus:ring-0 placeholder:text-slate-300 leading-relaxed"
                                                            placeholder="Context conversation..."
                                                        />
                                                    </div>

                                                    {/* Footer Info */}
                                                    <div className="px-3 py-1.5 bg-slate-50/50 border-t border-slate-50 flex items-center justify-between">
                                                        <p className="text-[9px] text-slate-400 font-medium flex items-center gap-1.5">
                                                            <Icons.Sparkles size={10} className="text-indigo-400" />
                                                            Context sent to AI
                                                        </p>
                                                        <span className="text-[9px] text-slate-300 font-mono">
                                                            {pinnedConversation.length} chars
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        {/* Status Messages Area */}
                                        {(submitError || submitSuccess) && (
                                            <div className="mb-3 flex flex-col gap-2 animate-in slide-in-from-bottom-2 fade-in duration-300">
                                                {submitError && (
                                                    <div className="p-2.5 bg-rose-50 text-rose-600 text-[10px] font-medium rounded-lg border border-rose-100 flex items-center gap-2 shadow-sm">
                                                        <Icons.AlertCircle size={14} className="shrink-0" />
                                                        <span>
                                                            {submitError.includes("<!DOCTYPE") || submitError.includes("<html")
                                                                ? "Server Timeout: Proses lama. Cek riwayat nanti."
                                                                : submitError}
                                                        </span>
                                                    </div>
                                                )}
                                                {submitSuccess && (
                                                    <div className="p-2.5 bg-emerald-50 text-emerald-600 text-[10px] font-medium rounded-lg border border-emerald-100 flex items-center gap-2 shadow-sm">
                                                        <Icons.CheckCircle2 size={14} className="shrink-0" />
                                                        <span>{submitSuccess}</span>
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {/* Wrapper with Framer Motion Chasing Border */}
                                        <div className="relative">
                                            {(isSubmitting || (processingJob && processingJob.feature_code === slug)) && (
                                                <motion.svg
                                                    className="absolute -inset-[3px] pointer-events-none"
                                                    style={{ width: 'calc(100% + 6px)', height: 'calc(100% + 6px)', filter: 'drop-shadow(0 0 6px rgba(34, 211, 238, 0.4))' }}
                                                    initial={{ opacity: 0 }}
                                                    animate={{ opacity: 1 }}
                                                    transition={{ duration: 0.3 }}
                                                >
                                                    <motion.rect
                                                        x="3"
                                                        y="3"
                                                        width="calc(100% - 6px)"
                                                        height="calc(100% - 6px)"
                                                        rx="8"
                                                        fill="none"
                                                        stroke="#22d3ee"
                                                        strokeWidth="3"
                                                        strokeDasharray="400 600"
                                                        strokeLinecap="round"
                                                        animate={{
                                                            strokeDashoffset: [0, -1000]
                                                        }}
                                                        transition={{
                                                            duration: 2,
                                                            repeat: Infinity,
                                                            ease: "linear"
                                                        }}
                                                    />
                                                </motion.svg>
                                            )}

                                            <button
                                                type="button"
                                                onClick={
                                                    (profile && (profile.credit_balance || 0) < totalCredits)
                                                        ? () => navigate('/dashboard/topup')
                                                        : handleSubmit
                                                }
                                                disabled={
                                                    !profile ||
                                                    isSubmitting ||
                                                    processingJob
                                                }
                                                style={{ backgroundColor: (isSubmitting || processingJob) ? '#94a3b8' : undefined }}
                                                className={`w-full py-2.5 rounded-lg font-bold text-white shadow-sm flex items-center justify-center gap-2 transition-all active:scale-[0.98] text-xs relative overflow-hidden
                                            ${(isSubmitting || processingJob)
                                                        ? "bg-slate-400 cursor-not-allowed pointer-events-none"
                                                        : (profile && (profile.credit_balance || 0) < totalCredits)
                                                            ? "bg-amber-500 hover:bg-amber-600"
                                                            : "bg-sky-600 hover:bg-sky-700"
                                                    }
                                            ${(isSubmitting || (processingJob && processingJob.feature_code === slug)) ? 'animate-glow' : ''}`}
                                            >


                                                {/* Icon Logic - No spinner, just static icon */}
                                                <span className="relative z-10">
                                                    {(processingJob && processingJob.feature_code === slug) ? (
                                                        <Icons.Loader2 size={16} className="animate-spin" /> // Show spinner if processing THIS feature
                                                    ) : processingJob ? (
                                                        <Icons.Lock size={16} />
                                                    ) : (profile && (profile.credit_balance || 0) < totalCredits) ? (
                                                        <Icons.Coins size={16} />
                                                    ) : (
                                                        <Icons.Wand2 size={16} />
                                                    )}
                                                </span>

                                                {/* Text Logic */}
                                                <span className="translate-y-[0.5px] relative z-10">
                                                    {(isSubmitting || (processingJob && processingJob.feature_code === slug))
                                                        ? "Sedang Memproses..."
                                                        : backgroundNotification
                                                            ? `${backgroundNotification.message} ${backgroundNotification.countdown > 0 ? `(${backgroundNotification.countdown})` : ''}`
                                                            : processingJob
                                                                ? `Generate in ${processingJob.feature_name || processingJob.feature_code}`
                                                                : (profile && (profile.credit_balance || 0) < totalCredits)
                                                                    ? `Top Up (Sisa: ${profile.credit_balance} Kredit)`
                                                                    : `Generate (${totalCredits} Kredit)`
                                                    }
                                                </span>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* 3. HISTORY GRID (RIGHT COLUMN) */}
                        <div className="flex lg:h-full h-auto md:h-auto md:min-h-[400px] lg:min-h-0 md:col-span-1 lg:col-span-1 flex-col min-h-0">
                            <div className="bg-white/95 backdrop-blur rounded-xl border border-slate-200 shadow-sm h-full flex flex-col overflow-hidden">
                                <div className="flex justify-between items-center px-3.5 pt-3.5 mb-3 shrink-0">
                                    <div>
                                        <h3 className="font-bold text-slate-800 text-xs sm:text-sm">20 Generate terakhir</h3>
                                        <p className="text-[9px] text-slate-400">20 Riwayat Generate kamu pada tools ini.</p>
                                    </div>
                                    <button onClick={() => navigate('/dashboard/riwayat-generate')} className="text-sky-600 text-[9px] font-semibold hover:underline">Lihat Semua Riwayat</button>
                                </div>



                                {/* GRID CONTENT */}
                                <div className="overflow-y-auto flex-1 scrollbar-thin scrollbar-track-transparent px-3.5 pb-2 flex flex-col">
                                    {isChatMode ? (
                                        // --- CHAT SESSION LIST ---
                                        <div className="flex flex-col gap-1 p-1 flex-1">

                                            {/* New Chat Button (Explicit) */}
                                            <button
                                                onClick={handleNewChat}
                                                className="w-full flex items-center gap-2 p-2.5 rounded-xl border border-dashed border-sky-300 bg-sky-50 text-sky-700 hover:bg-sky-100 hover:border-sky-400 transition-all font-bold text-xs mb-2 group"
                                            >
                                                <div className="w-5 h-5 rounded-full bg-sky-200 flex items-center justify-center group-hover:scale-110 transition-transform">
                                                    <Icons.Plus size={12} />
                                                </div>
                                                Buat Chat Baru
                                            </button>

                                            {chatSessions.length === 0 ? (
                                                <div className="text-center p-4 text-slate-400 text-xs py-8">
                                                    <Icons.MessageSquare size={24} className="mx-auto mb-2 opacity-50" />
                                                    <p>Belum ada riwayat chat.</p>
                                                </div>
                                            ) : (
                                                chatSessions.map(session => (
                                                    <button
                                                        key={session.id}
                                                        onClick={async () => {
                                                            if (currentSession?.id === session.id) return;

                                                            // Update latest request tracker
                                                            latestSessionIdRef.current = session.id;

                                                            // CRITICAL: Set pending scroll to force scroll to user prompt
                                                            pendingScrollSessionRef.current = session.id;
                                                            // Reset scroll tracking to allow new scroll
                                                            mainChatScrolledRef.current = null;

                                                            const cacheKey = `session_runs_${session.id}`;

                                                            // 1. Check in-memory cache first (fastest - 0ms)
                                                            if (sessionCacheRef.current && sessionCacheRef.current[session.id]) {
                                                                setCurrentSession(session);
                                                                setSessionRuns(sessionCacheRef.current[session.id]);
                                                                // Background refresh (safe)
                                                                loadSessionRuns(session.id);
                                                            }
                                                            // 2. Check localStorage cache (persistent - ~1ms)
                                                            else {
                                                                const cachedRuns = CacheService.get(cacheKey);
                                                                if (cachedRuns && cachedRuns.length > 0) {
                                                                    setCurrentSession(session);
                                                                    setSessionRuns(cachedRuns);
                                                                    // Populate in-memory cache
                                                                    if (sessionCacheRef.current) {
                                                                        sessionCacheRef.current[session.id] = cachedRuns;
                                                                    }
                                                                    // Background refresh (safe)
                                                                    loadSessionRuns(session.id);
                                                                }
                                                                // 3. Cache Miss: Need to fetch from DB
                                                                else {
                                                                    setIsLoadingSession(true);
                                                                    setSessionRuns([]);
                                                                    setCurrentSession(session);

                                                                    await loadSessionRuns(session.id);

                                                                    if (latestSessionIdRef.current === session.id) {
                                                                        setIsLoadingSession(false);
                                                                    }
                                                                }
                                                            }
                                                        }}
                                                        className={`text-left p-3 rounded-xl border transition-all group relative ${currentSession?.id === session.id
                                                            ? 'bg-sky-50/80 border-sky-200 shadow-sm ring-1 ring-sky-100'
                                                            : 'bg-white border-slate-100 hover:border-slate-300 hover:shadow-sm'
                                                            }`
                                                        }
                                                    >
                                                        <div className="flex justify-between items-start gap-2 pr-4">
                                                            <h4 className={`text-xs font-bold line-clamp-2 ${currentSession?.id === session.id ? 'text-sky-800' : 'text-slate-700 group-hover:text-slate-900'}`}>
                                                                {session.title || 'Percakapan Baru'}
                                                            </h4>
                                                            {currentSession?.id === session.id && (
                                                                <span className="w-1.5 h-1.5 rounded-full bg-sky-500 mt-1 shrink-0 animate-pulse" />
                                                            )}
                                                        </div>
                                                        <div className="flex items-center justify-between mt-1.5">
                                                            <p className="text-[9px] text-slate-400 flex items-center gap-1">
                                                                <Icons.Clock size={10} />
                                                                {new Date(session.updated_at).toLocaleString('id-ID', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                                                            </p>

                                                            {/* Side-by-Side Toggle Button */}
                                                            <div
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    if (sideBySideSessionId === session.id) {
                                                                        setSideBySideSessionId(null);
                                                                    } else {
                                                                        setSideBySideSessionId(session.id);
                                                                    }
                                                                }}
                                                                className={`p-1.5 rounded-md transition-all cursor-pointer ${sideBySideSessionId === session.id
                                                                    ? 'bg-indigo-100 text-indigo-600 opacity-100'
                                                                    : 'hover:bg-slate-100 text-slate-400 opacity-0 group-hover:opacity-100'
                                                                    }`}
                                                                title={sideBySideSessionId === session.id ? "Tutup Side-by-Side" : "Buka Side-by-Side"}
                                                            >
                                                                <Icons.Columns size={12} />
                                                            </div>
                                                        </div>
                                                    </button>
                                                ))
                                            )}
                                        </div>
                                    ) : (
                                        // --- EXISTING STANDARD HISTORY (Grid) ---
                                        history.length === 0 && !isSubmitting && !processingJob ? (
                                            <div className="flex-1 flex flex-col items-center justify-center text-center border-2 border-dashed border-slate-100 rounded-xl bg-slate-50/30 min-h-[200px]">
                                                <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center shadow-sm text-slate-300 mb-2"><Icons.History size={14} /></div>
                                                <p className="text-slate-500 text-[9px] font-medium">Belum ada riwayat.</p>
                                            </div>
                                        ) : (
                                            <div className="grid gap-2 auto-rows-min flex-1 content-start grid-cols-3">
                                                {/* --- OPTIMISTIC PROCESSING CARDS --- */}
                                                {(isSubmitting || (processingJob && processingJob.feature_code === slug)) && (
                                                    Array.from({ length: parseInt(inputs?.jumlah_gambar) || 1 }).map((_, i) => (
                                                        <div
                                                            key={`processing-${i}`}
                                                            className="relative cursor-not-allowed group relative rounded-xl bg-white shadow-sm border border-sky-200 overflow-hidden aspect-[4/3] flex flex-col"
                                                            style={{
                                                                backgroundImage: "url('/assets/dashboard/background.webp')",
                                                                backgroundSize: 'cover',
                                                                backgroundPosition: 'center',
                                                                animationDelay: `${i * 100}ms`
                                                            }}
                                                        >
                                                            <div className="absolute inset-0 bg-white/40 backdrop-blur-[1px] flex items-center justify-center">
                                                                <div className="relative">
                                                                    <div className="absolute inset-0 rounded-full border-2 border-sky-300/40 animate-ping" />
                                                                    <div className="w-8 h-8 rounded-full border-2 border-sky-100 border-t-sky-500 animate-spin bg-white/90 shadow-sm flex items-center justify-center">
                                                                        <Icons.Loader2 size={16} className="text-sky-600" />
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))
                                                )}

                                                {/* --- ACTUAL HISTORY ITEMS --- */}
                                                {history.slice(0, 20).map((item, historyIndex) => {
                                                    const result = item.parsedResult;

                                                    // Determine Mime Type for Click Logic
                                                    let itemMime = result.mime_type;
                                                    if (!itemMime && result.public_url) {
                                                        const ext = result.public_url.split('.').pop().toLowerCase();
                                                        if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) itemMime = 'image/jpeg';
                                                        else if (['mp4', 'webm', 'mov', 'mkv', 'avi', 'wmv', 'ts', 'm4v'].includes(ext)) itemMime = 'video/mp4';
                                                        else itemMime = 'application/octet-stream';
                                                    }

                                                    // Check if model is available for Recreate
                                                    const canRecreate = item.model_key
                                                        ? models.some(m => m.key === item.model_key)
                                                        : models.some(m => m.name === item.model_name);

                                                    return (
                                                        <div
                                                            key={item.uniqueId}
                                                            onTouchStart={() => {
                                                                isLongPress.current = false;
                                                                longPressTimer.current = setTimeout(() => {
                                                                    isLongPress.current = true;
                                                                    setActiveMobileMenuId(item.uniqueId);
                                                                    if (navigator.vibrate) navigator.vibrate(50); // Feedback
                                                                }, 500);
                                                            }}
                                                            onTouchEnd={() => {
                                                                if (longPressTimer.current) clearTimeout(longPressTimer.current);
                                                            }}
                                                            onTouchMove={() => {
                                                                if (longPressTimer.current) clearTimeout(longPressTimer.current);
                                                            }}
                                                            onClick={(e) => {
                                                                // If it was a long press, don't open modal
                                                                if (isLongPress.current) return;

                                                                // If clicking outside buttons while menu is active, just close menu? 
                                                                // or open modal? Let's open modal but also ensure menu closes if we switch items
                                                                if (activeMobileMenuId === item.uniqueId) {
                                                                    // toggle off if tapping again?
                                                                    // setActiveMobileMenuId(null);
                                                                    // return;
                                                                }

                                                                setActiveMobileMenuId(null); // Close any open menu
                                                                setMediaModal(result);
                                                                setMediaModalIndex(historyIndex); // Track position for navigation
                                                            }}
                                                            className={`group relative rounded-xl bg-white shadow-sm hover:shadow-[0_0_15px_rgba(34,211,238,0.3)] hover:border-cyan-400 transition-all overflow-hidden aspect-[4/3] flex flex-col cursor-pointer ${(activeMobileMenuId === item.uniqueId)
                                                                ? 'border-2 border-cyan-400 shadow-[0_0_15px_rgba(34,211,238,0.3)]'
                                                                : (pinnedHistoryItem?.uniqueId === item.uniqueId)
                                                                    ? 'border-2 border-amber-400 shadow-[0_0_15px_rgba(251,191,36,0.4)] z-10'
                                                                    : 'border border-slate-100'
                                                                }`}
                                                        >
                                                            {/* Render Content Thumbnail */}
                                                            <div className="flex-1 overflow-hidden bg-slate-50 flex items-center justify-center relative pointer-events-none">
                                                                {renderResultPreview(result, true)}
                                                            </div>

                                                            {/* Overlay Buttons - Always visible on mobile, hover-only on desktop */}
                                                            <div className={`absolute inset-0 transition-opacity z-10 flex justify-end items-start p-1.5 gap-1.5 
                                                                    opacity-100 bg-black/10 
                                                                    xl:opacity-0 xl:bg-transparent 
                                                                    xl:group-hover:opacity-100 xl:group-hover:bg-black/10 
                                                                    ${activeMobileMenuId === item.uniqueId ? '!opacity-100 !bg-black/10' : ''}`}>
                                                                {/* Recreate Button */}
                                                                {canRecreate && (
                                                                    <button
                                                                        onClick={(e) => handleRecreate(e, item)}
                                                                        className="p-1 bg-white/90 text-slate-600 hover:text-sky-600 hover:bg-white rounded-full shadow-sm hover:scale-105 transition-transform backdrop-blur-sm"
                                                                        title="Recreate (Use Prompt Again)"
                                                                    >
                                                                        <Icons.RotateCcw size={10} />
                                                                    </button>
                                                                )}

                                                                {/* Pin Button (Chat Mode: Context, Standard Mode: Target Action) */}
                                                                <button
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        if (isChatMode) {
                                                                            handlePinConversation(e, item);
                                                                        } else {
                                                                            handlePinHistoryItem(e, item);
                                                                        }
                                                                        setActiveMobileMenuId(null);
                                                                    }}
                                                                    className={`p-1 text-white rounded-full shadow-sm hover:scale-105 transition-transform ${(isChatMode ? false : pinnedHistoryItem?.uniqueId === item.uniqueId)
                                                                        ? "bg-amber-600 ring-2 ring-white"
                                                                        : "bg-amber-500 hover:bg-amber-600"
                                                                        }`}
                                                                    title={isChatMode ? "Pin as Context" : (pinnedHistoryItem?.uniqueId === item.uniqueId ? "Unpin Item" : "Pin Item for Actions")}
                                                                >
                                                                    <Icons.Pin size={10} className={pinnedHistoryItem?.uniqueId === item.uniqueId ? "fill-current" : ""} />
                                                                </button>
                                                                {/* Edit Button - Images Only */}
                                                                {(itemMime && itemMime.startsWith('image/')) && (
                                                                    <button
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            setImageEditorAsset({ url: result.public_url });
                                                                            setActiveMobileMenuId(null);
                                                                        }}
                                                                        className="p-1 bg-sky-500 hover:bg-sky-600 text-white rounded-full shadow-sm hover:scale-105 transition-transform backdrop-blur-sm"
                                                                        title="Edit Image"
                                                                    >
                                                                        <Icons.Pencil size={10} />
                                                                    </button>
                                                                )}
                                                                {/* Delete Button */}
                                                                <button
                                                                    onClick={(e) => {
                                                                        e.stopPropagation(); // prevent modal open
                                                                        handleDeleteItem(e, item);
                                                                        setActiveMobileMenuId(null);
                                                                    }}
                                                                    className="p-1 bg-rose-500 hover:bg-rose-600 text-white rounded-full shadow-sm hover:scale-105 transition-transform"
                                                                    title="Hapus gambar (Riwayat tetap ada)"
                                                                >
                                                                    <Icons.Trash2 size={10} />
                                                                </button>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )
                                    )}
                                </div>
                                {/* END SCROLL CONTAINER */}

                                {/* BANNER SECTION - Outside scroll, stays at bottom on desktop */}
                                <div className="shrink-0 p-3.5 pt-0">
                                    {/* EXTERNAL BUTTONS (History Bottom - Banner Only) */}
                                    {/* Hide if pinned item exists as requested */}
                                    {actionButtons.length > 0 && ((!(hasGenerated || pinnedHistoryItem) || isSubmitting || (processingJob && processingJob.feature_code === slug)) || actionButtons.filter(b => b.url && b.url.trim().length > 0 && b.url !== '#').length === 0) && (() => {
                                        const bannerButtons = actionButtons.filter(b => b.banner_image);
                                        if (bannerButtons.length === 0) return null;

                                        const activeIndex = currentBannerIndex % bannerButtons.length;
                                        const activeBtn = bannerButtons[activeIndex];

                                        const publicUrl = activeBtn.banner_image.startsWith('http')
                                            ? activeBtn.banner_image
                                            : supabase.storage.from('assets').getPublicUrl(activeBtn.banner_image).data.publicUrl;

                                        const hasLink = activeBtn.banner_link && activeBtn.banner_link !== '#';

                                        return (
                                            <div className="mt-4 pt-3 border-t border-slate-100 shrink-0">
                                                <div
                                                    onClick={(e) => {
                                                        if (!hasLink) return;
                                                        e.preventDefault();

                                                        const url = activeBtn.banner_link;
                                                        let navUrl = url;
                                                        let useRouter = false;

                                                        if (url.includes('/dashboard/')) {
                                                            navUrl = url.substring(url.indexOf('/dashboard/'));
                                                            navUrl = navUrl.replace(/^\/+/, '/');
                                                            useRouter = true;
                                                        } else if (url.startsWith('/') || url.includes(window.location.origin)) {
                                                            navUrl = url.replace(window.location.origin, '');
                                                            useRouter = true;
                                                        }

                                                        if (useRouter) {
                                                            navigate(navUrl);
                                                        } else {
                                                            window.location.href = url;
                                                        }
                                                    }}
                                                    className={`block relative overflow-hidden rounded-xl border border-slate-200 transition-all group w-full h-20 ${hasLink ? 'cursor-pointer shadow-sm hover:shadow-md' : ''}`}
                                                >
                                                    <img
                                                        src={publicUrl}
                                                        alt={activeBtn.name}
                                                        className={`w-full h-full transition-transform duration-700 ${hasLink ? 'group-hover:scale-105' : ''}`}
                                                    />
                                                </div>
                                            </div>
                                        );
                                    })()}

                                    {/* ACTION BUTTONS (After Generation OR Pinned Item) */}
                                    {(() => {
                                        const validButtons = actionButtons.filter(b => b.url && b.url.trim().length > 0 && b.url !== '#');
                                        const shouldShow = validButtons.length > 0 && (hasGenerated || pinnedHistoryItem) && !isSubmitting && !(processingJob && processingJob.feature_code === slug);

                                        if (!shouldShow) return null;

                                        return (
                                            <div className="mt-4 pt-3 border-t border-slate-100 grid grid-cols-1 md:grid-cols-2 gap-3 shrink-0 animate-in fade-in slide-in-from-bottom-2 duration-500 relative">
                                                {validButtons.map((btn, idx) => (
                                                    <div key={idx} className="flex flex-col gap-1">
                                                        {btn.description && (
                                                            <span className="text-[9px] text-slate-500 font-medium ml-1">
                                                                {btn.description}
                                                            </span>
                                                        )}
                                                        <button
                                                            onClick={(e) => {
                                                                e.preventDefault();

                                                                let navUrl = btn.url;
                                                                let useRouter = false;

                                                                // Strategy: ALWAYS Same Tab (User Request)

                                                                // 1. Detect if it's an App Route -> Use navigate()
                                                                if (btn.url.includes('/dashboard/')) {
                                                                    navUrl = btn.url.substring(btn.url.indexOf('/dashboard/'));
                                                                    navUrl = navUrl.replace(/^\/+/, '/');
                                                                    useRouter = true;
                                                                } else if (btn.url.startsWith('/') || btn.url.includes(window.location.origin)) {
                                                                    navUrl = btn.url.replace(window.location.origin, '');
                                                                    useRouter = true;
                                                                }

                                                                // 2. Determine Output Image (Pinned > Generated)
                                                                const targetImage = pinnedHistoryItem?.parsedResult?.public_url || generatedOutput?.public_url;

                                                                // AUTO-UNPIN (Clean up UI immediately)
                                                                setPinnedHistoryItem(null);

                                                                // 3. Execute
                                                                if (useRouter) {
                                                                    const state = targetImage ? { outputImage: targetImage } : {};
                                                                    navigate(navUrl, { state });
                                                                } else {
                                                                    // Force same tab
                                                                    window.location.href = btn.url;
                                                                }
                                                            }}
                                                            style={{ backgroundColor: btn.button_color || '#0284c7' }}
                                                            className={`w-full text-white rounded-lg py-3 px-4 text-xs font-bold text-center transition-all shadow-sm flex items-center justify-center hover:opacity-90 hover:scale-[1.02] ${pinnedHistoryItem ? 'ring-2 ring-amber-400 ring-offset-1' : ''}`}
                                                        >
                                                            {btn.name || 'Open Tool'}
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>
                                        );
                                    })()}
                                </div>
                                {/* END BANNER SECTION */}


                            </div>
                        </div>
                    </div >
                </div >
            </main >


            {/* FULL SCREEN MEDIA MODAL */}
            {
                mediaModal && (() => {
                    // Navigation Helpers
                    const maxIndex = Math.min(history.length, 20) - 1;
                    const canGoPrev = mediaModalIndex > 0;
                    const canGoNext = mediaModalIndex >= 0 && mediaModalIndex < maxIndex;

                    const goToPrev = (e) => {
                        if (e) e.stopPropagation();
                        if (canGoPrev) {
                            const prevItem = history[mediaModalIndex - 1];
                            if (prevItem?.parsedResult) {
                                setMediaModal(prevItem.parsedResult);
                                setMediaModalIndex(mediaModalIndex - 1);
                            }
                        }
                    };

                    const goToNext = (e) => {
                        if (e) e.stopPropagation();
                        if (canGoNext) {
                            const nextItem = history[mediaModalIndex + 1];
                            if (nextItem?.parsedResult) {
                                setMediaModal(nextItem.parsedResult);
                                setMediaModalIndex(mediaModalIndex + 1);
                            }
                        }
                    };

                    // Determine Layout Mode based on content type
                    const isDoc = (mediaModal.mime_type && (
                        mediaModal.mime_type === 'application/pdf' ||
                        mediaModal.mime_type === 'text/plain' ||
                        mediaModal.mime_type.includes('application/') // For other docs
                    )) || (mediaModal.public_url && (
                        mediaModal.public_url.endsWith('.pdf') ||
                        mediaModal.public_url.endsWith('.txt') ||
                        mediaModal.public_url.endsWith('.doc') ||
                        mediaModal.public_url.endsWith('.docx')
                    ));

                    return (
                        <div
                            className="fixed inset-0 z-[100050] flex items-center justify-center bg-black animate-in fade-in duration-200"
                            onClick={() => { setMediaModal(null); setMediaModalIndex(-1); }}
                            onKeyDown={(e) => {
                                if (e.key === 'ArrowLeft') goToPrev();
                                else if (e.key === 'ArrowRight') goToNext();
                                else if (e.key === 'Escape') { setMediaModal(null); setMediaModalIndex(-1); }
                            }}
                            tabIndex={0}
                        >
                            {/* Navigation Arrow - Previous */}
                            {mediaModalIndex >= 0 && canGoPrev && (
                                <button
                                    onClick={goToPrev}
                                    className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 z-[120] p-2 sm:p-2.5 bg-white/10 hover:bg-white/30 text-white rounded-full hover:scale-110 transition-all backdrop-blur-md border border-white/20 shadow-lg pointer-events-auto group"
                                    title="Previous (←)"
                                >
                                    <Icons.ChevronLeft size={18} className="sm:w-5 sm:h-5 group-hover:-translate-x-0.5 transition-transform" />
                                </button>
                            )}

                            {/* Navigation Arrow - Next */}
                            {mediaModalIndex >= 0 && canGoNext && (
                                <button
                                    onClick={goToNext}
                                    className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 z-[120] p-2 sm:p-2.5 bg-white/10 hover:bg-white/30 text-white rounded-full hover:scale-110 transition-all backdrop-blur-md border border-white/20 shadow-lg pointer-events-auto group"
                                    title="Next (→)"
                                >
                                    <Icons.ChevronRight size={18} className="sm:w-5 sm:h-5 group-hover:translate-x-0.5 transition-transform" />
                                </button>
                            )}

                            {/* Position Counter */}
                            {mediaModalIndex >= 0 && maxIndex >= 0 && (
                                <div className="absolute bottom-4 sm:bottom-6 left-1/2 -translate-x-1/2 z-[120] px-4 py-2 bg-black/60 backdrop-blur-md rounded-full text-white text-xs sm:text-sm font-medium border border-white/10 pointer-events-none">
                                    {mediaModalIndex + 1} / {maxIndex + 1}
                                </div>
                            )}

                            <div
                                className={`relative w-full h-full flex flex-col items-center justify-center pointer-events-none ${isDoc ? 'p-4 md:p-8 max-w-5xl gap-2' : ''}`}
                            >
                                {/* Header/Controls */}
                                {isDoc ? (
                                    // Document Mode: Header Bar (No Overlap)
                                    <div className="w-full flex justify-end gap-3 pointer-events-auto bg-black/50 p-2 rounded-xl backdrop-blur-md">
                                        {mediaModal.public_url && (
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    const downloadName = getCleanFilename(mediaModal.storage_path || mediaModal.public_url);
                                                    handleDownload(mediaModal.public_url, downloadName);
                                                }}
                                                className="flex items-center gap-2 px-4 py-2 bg-sky-600 hover:bg-sky-500 text-white rounded-lg text-xs font-bold transition-all shadow-sm"
                                                title="Download"
                                            >
                                                <Icons.Download size={14} /> Download File
                                            </button>
                                        )}

                                        {/* PIN BUTTON (Chat Mode Only) */}
                                        {isChatMode && (
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handlePinConversation(e, {
                                                        public_url: mediaModal.public_url || mediaModal.storage_path,
                                                        result: mediaModal.public_url
                                                    });
                                                    setMediaModal(null); // Close modal
                                                    setMediaModalIndex(-1);
                                                }}
                                                className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white rounded-lg text-xs font-bold transition-all shadow-sm"
                                                title="Pin Conversation Context"
                                            >
                                                <Icons.Pin size={14} className="rotate-45" /> Pin Context
                                            </button>
                                        )}
                                        <button
                                            onClick={() => { setMediaModal(null); setMediaModalIndex(-1); }}
                                            className="p-2 bg-white/10 hover:bg-white/20 text-white rounded-lg hover:scale-105 transition-all text-xs font-bold"
                                        >
                                            <Icons.X size={18} />
                                        </button>
                                    </div>
                                ) : (
                                    // Media Mode: Floating Buttons (Immersive)
                                    <div className="absolute top-4 right-4 z-[110] flex items-center gap-3 pointer-events-auto">
                                        {mediaModal.public_url && (
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    const downloadName = getCleanFilename(mediaModal.storage_path || mediaModal.public_url);
                                                    handleDownload(mediaModal.public_url, downloadName);
                                                }}
                                                className="p-3 bg-white/10 hover:bg-white/20 text-white rounded-full hover:scale-105 transition-all backdrop-blur-md border border-white/10 shadow-sm"
                                                title="Download"
                                            >
                                                <Icons.Download size={20} />
                                            </button>
                                        )}
                                        <button
                                            onClick={() => { setMediaModal(null); setMediaModalIndex(-1); }}
                                            className="p-3 bg-white/10 hover:bg-white/20 text-white rounded-full hover:scale-105 transition-all backdrop-blur-md border border-white/10 shadow-sm"
                                        >
                                            <Icons.X size={20} />
                                        </button>
                                    </div>
                                )}

                                {/* Content Container */}
                                <div
                                    className={`w-full ${isDoc ? 'flex-1 min-h-0' : 'h-full'} flex items-center justify-center overflow-hidden pointer-events-auto`}
                                    onClick={e => e.stopPropagation()}
                                >
                                    {renderResultPreview(mediaModal)}
                                </div>
                            </div>
                        </div>
                    );
                })()
            }

            {/* Model Selector Modal */}
            {/* Model Selector Modal (Always Rendered for Eager Video Loading) */}
            <ModelSelectorModal
                models={models}
                selectedId={selectedModelId}
                onSelect={(id) => {
                    setSelectedModelId(id);
                    setShowModelSelector(false);
                    // Force new session if in chat mode
                    if (isChatMode && user?.id) {
                        initSession(user.id, true);
                    }
                }}

                onClose={() => setShowModelSelector(false)}
                capabilities={modelCapabilities}
                currentTotalCredits={totalCredits}
                inputs={inputs}
                isOpen={showModelSelector}
            />

            {/* PIN SELECTION MODAL - Moved to Root Level */}
            {
                pinSelectionModal && (
                    <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/60 backdrop-blur-sm animate-in fade-in duration-200 p-4" onClick={() => setPinSelectionModal(null)}>
                        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-in zoom-in-95 duration-200" onClick={e => e.stopPropagation()}>
                            <div className="p-4 bg-slate-50 border-b border-slate-100 flex items-center justify-between">
                                <h3 className="font-bold text-slate-800">Pilih Target Input</h3>
                                <button onClick={() => setPinSelectionModal(null)} className="p-1 hover:bg-slate-200 rounded-full transition-colors">
                                    <Icons.X size={18} className="text-slate-500" />
                                </button>
                            </div>

                            <div className="p-4">
                                <div className="flex items-center gap-3 mb-4 p-3 bg-sky-50 rounded-xl border border-sky-100">
                                    <div className="w-12 h-12 rounded-lg bg-white shrink-0 overflow-hidden border border-sky-200">
                                        {pinSelectionModal.assetType === 'image' ? (
                                            <img src={pinSelectionModal.url} className="w-full h-full object-cover" />
                                        ) : (
                                            <div className="w-full h-full flex items-center justify-center text-sky-500"><Icons.FileText size={20} /></div>
                                        )}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-xs font-semibold text-sky-800 truncate">Asset Terpilih</p>
                                    </div>
                                </div>

                                <p className="text-xs text-slate-500 mb-3">
                                    Asset ini cocok untuk beberapa input. Pilih input mana yang ingin Anda isi:
                                </p>

                                <div className="space-y-2">
                                    {pinSelectionModal.candidates.map(field => (
                                        <button
                                            key={field.key}
                                            onClick={() => handlePinToField(field, pinSelectionModal.url)}
                                            className="w-full p-3 flex items-center gap-3 rounded-xl border border-slate-200 hover:border-sky-400 hover:bg-sky-50 transition-all text-left group"
                                        >
                                            <div className="w-8 h-8 rounded-full bg-slate-100 group-hover:bg-white flex items-center justify-center text-slate-500 group-hover:text-sky-600 border border-slate-200 group-hover:border-sky-200 transition-colors">
                                                {field.type === 'image' ? <Icons.Image size={14} /> :
                                                    field.type === 'file_multi' ? <Icons.Files size={14} /> :
                                                        <Icons.FileText size={14} />}
                                            </div>
                                            <div>
                                                <div className="font-bold text-sm text-slate-700 group-hover:text-sky-700">{field.label || field.key}</div>
                                            </div>
                                            <Icons.ArrowRight size={14} className="ml-auto text-slate-300 group-hover:text-sky-500 opacity-0 group-hover:opacity-100 transition-all -translate-x-2 group-hover:translate-x-0" />
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                )
            }

            {/* === DELETE ITEM CONFIRMATION MODAL (Granular) === */}
            {
                itemToDelete && (
                    <DeleteItemModal
                        isOpen={!!itemToDelete}
                        onClose={() => setItemToDelete(null)}
                        onConfirm={confirmDeleteItem}
                    />
                )
            }


            {/* --- Global Popover for Multi Inputs (Portal-like) --- */}
            {
                activeMobileMenuId && typeof activeMobileMenuId === 'string' && activeMobileMenuId.startsWith('menu-') && menuAnchor && (() => {
                    // Find the associated field config
                    const fieldKey = activeMobileMenuId.replace('menu-', '');
                    const field = modelFields.find(f => f.key === fieldKey);

                    if (!field) return null;

                    return ReactDOM.createPortal(
                        <div
                            className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/50 px-4 transition-opacity duration-200"
                            onClick={() => {
                                setActiveMobileMenuId(null);
                                setMenuAnchor(null);
                            }}
                        >
                            <div
                                className="bg-white rounded-2xl shadow-2xl border border-slate-100 overflow-hidden flex flex-row min-h-[500px] max-h-[80vh] max-w-5xl w-full"
                                onClick={(e) => e.stopPropagation()}
                            >
                                {/* Left Sidebar: Input Types List */}
                                <div className="w-64 shrink-0 bg-slate-50 border-r border-slate-100 p-4 flex flex-col gap-3 overflow-y-auto">
                                    <p className="px-2 py-1.5 text-[10px] font-bold text-slate-400 uppercase tracking-wider">Tipe File</p>

                                    {
                                        (() => {
                                            const filteredOptions = modelFields
                                                .filter(f => !f.is_hidden && ['file_multi', 'file', 'image', 'video', 'audio', 'pdf', 'excel', 'docx', 'doc', 'text', 'txt'].includes(f.type))
                                                .filter(targetF => {
                                                    if (field.type !== 'file_multi' && field.type !== 'file') {
                                                        return targetF.key === field.key;
                                                    }
                                                    return true;
                                                })
                                                .map(f => {
                                                    let icon = Icons.FileText;
                                                    let accept = '*/*';

                                                    switch (f.type) {
                                                        case 'image': icon = Icons.Image; accept = 'image/*'; break;
                                                        case 'video': icon = Icons.Video; accept = 'video/mp4,video/*'; break;
                                                        case 'audio': icon = Icons.Music; accept = 'audio/*'; break;
                                                        case 'pdf': icon = Icons.FileText; accept = '.pdf'; break;
                                                        case 'excel': icon = Icons.FileSpreadsheet; accept = '.xls,.xlsx,.csv'; break;
                                                        case 'docx':
                                                        case 'doc': icon = Icons.FileType2; accept = '.doc,.docx'; break;
                                                        case 'txt':
                                                        case 'text': icon = Icons.FileText; accept = '.txt'; break;
                                                        case 'file_multi': icon = Icons.Files; accept = '*/*'; break;
                                                        default: icon = Icons.File; accept = '*/*'; break;
                                                    }

                                                    return { id: f.type, label: f.label, icon, accept, typeId: f.type, key: f.key };
                                                });

                                            const isSingle = filteredOptions.length === 1;

                                            return filteredOptions.map(type => (
                                                <button
                                                    key={type.key} // Using unique key
                                                    onClick={(e) => {
                                                        e.stopPropagation();

                                                        // 1. Check Total Count Constraint
                                                        const rawVal = inputs[field.key];
                                                        const currentFiles = Array.isArray(rawVal) ? rawVal : (rawVal ? [rawVal] : []);
                                                        const maxFiles = parseInt(field.default_value) || 5;

                                                        if (currentFiles.length >= maxFiles) {
                                                            showToast(`Maksimal ${maxFiles} file sudah tercapai. Hapus file lama untuk menambah baru.`, "error");
                                                            return;
                                                        }

                                                        // 2. Trigger Upload
                                                        let input = document.getElementById(`file-multi-${field.key}`);
                                                        if (!input) input = document.getElementById(`file-${field.key}`);

                                                        if (input) {
                                                            if (field.type === 'file_multi') {
                                                                input.accept = type.accept;
                                                            }
                                                            input.click();
                                                        }
                                                        setActiveMobileMenuId(null);
                                                        setMenuAnchor(null);
                                                    }}
                                                    className={`flex items-center gap-2.5 rounded-xl hover:bg-slate-100 transition-all text-left group/btn cursor-pointer w-full ${isSingle
                                                        ? 'flex-col justify-center p-6 bg-white border-2 border-dashed border-slate-200 hover:border-sky-500 hover:bg-sky-50 flex-1'
                                                        : 'p-3 bg-white border border-slate-200 hover:border-sky-400'
                                                        }`}
                                                >
                                                    <div className={`rounded-full flex items-center justify-center shrink-0 border-2 border-slate-300 text-slate-400 group-hover/btn:border-sky-500 group-hover/btn:text-sky-500 transition-colors ${isSingle ? 'w-14 h-14 mb-2' : 'w-8 h-8'}`}>
                                                        <type.icon size={isSingle ? 28 : 16} />
                                                    </div>
                                                    <span className={`font-semibold text-slate-600 group-hover/btn:text-slate-900 ${isSingle ? 'text-sm text-center' : 'text-xs'}`}>{type.label}</span>
                                                </button>
                                            ));
                                        })()
                                    }
                                </div>

                                {/* Right Panel: Recent Assets */}
                                <div className="flex-1 bg-white flex flex-col min-w-0">
                                    <div className="p-4 border-b border-slate-100 flex justify-between items-center shrink-0">
                                        <h4 className="text-sm font-bold text-slate-700">Terakhir Diupload</h4>
                                        <div className="flex items-center gap-2">
                                            {loadingRecent && <Icons.Loader2 size={14} className="animate-spin text-slate-400" />}

                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setActiveMobileMenuId(null);
                                                    setMenuAnchor(null);
                                                }}
                                                className="w-7 h-7 rounded-full hover:bg-slate-100 flex items-center justify-center text-slate-400 hover:text-slate-600 transition-colors"
                                            >
                                                <Icons.X size={16} />
                                            </button>
                                        </div>
                                    </div>

                                    <div className="flex-1 overflow-y-auto p-4">
                                        {recentAssets.length > 0 ? (
                                            (() => {
                                                // 1. FILTER LOGIC
                                                const filteredAssets = recentAssets.filter(asset => {
                                                    let allowed = [];
                                                    if (field.type !== 'file_multi') {
                                                        allowed = [field.type];
                                                    } else {
                                                        let opts = field.options;
                                                        if (typeof opts === 'string') { try { opts = JSON.parse(opts); } catch { } }
                                                        if (opts?.allowed_types) allowed = opts.allowed_types;
                                                        else allowed = ['image', 'video', 'audio', 'pdf', 'docx', 'excel', 'txt'];
                                                    }

                                                    const type = (asset.type || '').toLowerCase();
                                                    const mainType = type.split('/')[0];
                                                    const ext = asset.name.split('.').pop().toLowerCase();

                                                    const typeMap = {
                                                        'pdf': ['application/pdf'],
                                                        'excel': ['application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'text/csv', 'csv'],
                                                        'docx': ['application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'],
                                                        'txt': ['text/plain'],
                                                        'image': ['image'],
                                                        'video': ['video'],
                                                        'audio': ['audio']
                                                    };

                                                    return allowed.some(a => {
                                                        if (a === 'file' || a === '*' || a === 'multi') return true;
                                                        if (a === mainType) return true;

                                                        const mapped = typeMap[a];
                                                        if (mapped) {
                                                            if (Array.isArray(mapped)) return mapped.some(m => type.includes(m));
                                                            return type.includes(mapped);
                                                        }
                                                        if (a === ext) return true;
                                                        return false;
                                                    });
                                                });

                                                // 2. EMPTY STATE
                                                if (filteredAssets.length === 0) {
                                                    // Determine label for friendly message
                                                    let typeLabel = "file";
                                                    if (field.type === 'image') typeLabel = "gambar";
                                                    else if (field.type === 'video') typeLabel = "video";
                                                    else if (field.type === 'audio') typeLabel = "audio";
                                                    else if (field.type === 'pdf') typeLabel = "dokumen PDF";

                                                    return (
                                                        <div className="flex flex-col items-center justify-center h-full text-slate-400 py-8">
                                                            <div className="w-12 h-12 rounded-full bg-slate-50 flex items-center justify-center mb-2">
                                                                <Icons.Inbox size={20} className="text-slate-300" />
                                                            </div>
                                                            <span className="text-[10px] font-medium">Tidak ada {typeLabel} yang diupload</span>
                                                        </div>
                                                    );
                                                }

                                                // 3. RENDER GRID
                                                return (
                                                    <div className="grid grid-cols-4 gap-1">
                                                        {filteredAssets.map((asset, i) => {
                                                            const ext = asset.name ? asset.name.split('.').pop().toLowerCase() : '';
                                                            const isImg = (asset.type && asset.type.startsWith('image/')) || ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'ico'].includes(ext);
                                                            const isVid = (asset.type && asset.type.startsWith('video/')) || ['mp4', 'webm', 'ogg', 'mov'].includes(ext);

                                                            // Determine Icon & Color for non-media
                                                            let FileIcon = Icons.FileText;
                                                            let colorClass = "text-slate-400";
                                                            let bgColorClass = "bg-slate-50";
                                                            let label = ext.toUpperCase();

                                                            if (ext === 'pdf') {
                                                                FileIcon = Icons.FileText;
                                                                colorClass = "text-red-500";
                                                                bgColorClass = "bg-red-50/50";
                                                            } else if (['xls', 'xlsx', 'csv'].includes(ext)) {
                                                                FileIcon = Icons.FileSpreadsheet;
                                                                colorClass = "text-emerald-500";
                                                                bgColorClass = "bg-emerald-50/50";
                                                                label = 'EXCEL';
                                                            } else if (['doc', 'docx'].includes(ext)) {
                                                                FileIcon = Icons.FileType2;
                                                                colorClass = "text-blue-500";
                                                                bgColorClass = "bg-blue-50/50";
                                                                label = 'WORD';
                                                            } else if (['ppt', 'pptx'].includes(ext)) {
                                                                FileIcon = Icons.File; // Placeholder
                                                                colorClass = "text-orange-500";
                                                                bgColorClass = "bg-orange-50/50";
                                                                label = 'PPT';
                                                            } else if (['txt'].includes(ext)) {
                                                                FileIcon = Icons.FileText;
                                                                colorClass = "text-slate-500";
                                                                bgColorClass = "bg-slate-50";
                                                            } else if (asset.type.startsWith('audio/')) {
                                                                FileIcon = Icons.Music;
                                                                colorClass = "text-purple-500";
                                                                bgColorClass = "bg-purple-50/50";
                                                                label = 'AUDIO';
                                                            }

                                                            return (
                                                                <button
                                                                    key={i}
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();

                                                                        // --- DEBUG START: RECENT ASSET CLICK ---
                                                                        console.group('%c 🕵️ DEBUG: Recent Asset Selected', 'background: #22c55e; color: #fff; padding: 2px 4px; border-radius: 4px; font-weight: bold;');
                                                                        console.log('%c Asset Info:', 'color: #3b82f6; font-weight: bold;', asset);
                                                                        console.log('%c Target Field:', 'color: #3b82f6; font-weight: bold;', field.key);
                                                                        console.log('%c Current Inputs:', 'color: #3b82f6; font-weight: bold;', inputs);
                                                                        console.groupEnd();
                                                                        // --- DEBUG END ---

                                                                        const newInputs = { ...inputs };
                                                                        const currentFiles = newInputs[field.key] || [];

                                                                        // 1. Check Max Count
                                                                        if (currentFiles.length >= (parseInt(field.default_value) || 5)) {
                                                                            showToast("Max file limit reached.", "error");
                                                                            return;
                                                                        }

                                                                        // 2. Check Valid Type
                                                                        {
                                                                            let allowed = [];

                                                                            // A. Direct Check: If this is a specific input (image, video), respect its type.
                                                                            if (field.type !== 'file_multi') {
                                                                                allowed = [field.type];
                                                                            }

                                                                            // 2b. Structural Fallback (Check Sibling Fields)
                                                                            // If no explicit types, infer from OTHER fields in this model
                                                                            if (allowed.length === 0) {
                                                                                const supportedTypes = ['image', 'video', 'audio', 'pdf', 'docx', 'excel', 'txt'];

                                                                                // Scan modelFields for any of these types
                                                                                modelFields.forEach(f => {
                                                                                    // Normalize type if needed (e.g. some might be 'file' which is generic, but usually specific types are key)
                                                                                    if (supportedTypes.includes(f.type) && !allowed.includes(f.type)) {
                                                                                        allowed.push(f.type);
                                                                                    }
                                                                                });

                                                                                // If still empty (e.g. only file_multi exists), allow all? 
                                                                                // Or keep it strict? User request implies "look for type". 
                                                                                // If only file_multi is there, usually it means it accepts anything or configured via options (step 2a).
                                                                            }


                                                                            // If we have constraints, validate
                                                                            if (allowed.length > 0) {
                                                                                const assetType = asset.type.toLowerCase(); // e.g. 'video/mp4'
                                                                                const assetMainType = assetType.split('/')[0]; // 'video'

                                                                                // Map specialized types
                                                                                let isMatch = false;
                                                                                if (allowed.includes('multi') || allowed.includes('*')) isMatch = true;
                                                                                else {
                                                                                    // Check exact match
                                                                                    const typeMap = {
                                                                                        'pdf': 'application/pdf',
                                                                                        'excel': ['application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'text/csv'],
                                                                                        'docx': ['application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'],
                                                                                        'txt': 'text/plain',
                                                                                        'image': 'image',
                                                                                        'video': 'video',
                                                                                        'audio': 'audio'
                                                                                    };

                                                                                    isMatch = allowed.some(a => {
                                                                                        if (a === assetMainType) return true;

                                                                                        const mapped = typeMap[a];
                                                                                        if (!mapped) return false;

                                                                                        if (Array.isArray(mapped)) {
                                                                                            return mapped.some(m => assetType.includes(m));
                                                                                        }
                                                                                        return assetType.includes(mapped);
                                                                                    });
                                                                                }

                                                                                if (!isMatch) {
                                                                                    setActiveMobileMenuId(null);
                                                                                    setMenuAnchor(null);
                                                                                    showToast("Tipe file ini tidak didukung oleh model ini.", "error");
                                                                                    console.warn(`[Val] Fail. Asset: ${assetType} vs Allowed:`, allowed);
                                                                                    return;
                                                                                }
                                                                            } else {
                                                                                // Fallback: If NO allowed types found at all
                                                                                setActiveMobileMenuId(null);
                                                                                setMenuAnchor(null);
                                                                                showToast("Tipe file ini tidak didukung oleh model ini.", "error");
                                                                                console.warn(`[Val] Fail. Allowed list is empty.`);
                                                                                return;
                                                                            }
                                                                        }
                                                                        // 3. Check Duplicate
                                                                        if (currentFiles.some(f => (typeof f === 'string' ? f === asset.url : f.name === asset.name))) {
                                                                            setActiveMobileMenuId(null);
                                                                            setMenuAnchor(null);
                                                                            return;
                                                                        }

                                                                        let newVal = asset.url;
                                                                        if (field.type === 'file_multi') {
                                                                            newVal = [...currentFiles, asset.url];
                                                                        }

                                                                        setInputs({
                                                                            ...newInputs,
                                                                            [field.key]: newVal
                                                                        });

                                                                        setActiveMobileMenuId(null);
                                                                        setMenuAnchor(null);
                                                                    }}
                                                                    className="relative aspect-square rounded-lg overflow-hidden border border-slate-100 bg-white hover:border-sky-400 hover:ring-1 hover:ring-sky-100 transition-all group/asset"
                                                                    title={asset.name}
                                                                >
                                                                    {isImg ? (
                                                                        <img src={asset.url} className={`w-full h-full ${['ico', 'svg'].includes(ext) ? 'object-contain p-2' : 'object-cover'}`} loading="lazy" />
                                                                    ) : isVid ? (
                                                                        <div className="w-full h-full relative">
                                                                            <video src={asset.url} className="w-full h-full object-cover opacity-80" />
                                                                            <div className="absolute top-1 left-1 bg-black/50 rounded-full p-0.5 pointer-events-none">
                                                                                <Icons.Video size={10} className="text-white" />
                                                                            </div>
                                                                        </div>
                                                                    ) : (
                                                                        <div className={`w-full h-full flex flex-col items-center justify-center gap-1 ${bgColorClass}`}>
                                                                            <FileIcon size={20} className={colorClass} />
                                                                            <span className="text-[7px] font-bold text-slate-500 uppercase truncate w-[90%] text-center">{label}</span>
                                                                            <span className="text-[6px] text-slate-400 truncate w-[90%] text-center leading-none">{asset.name}</span>
                                                                        </div>
                                                                    )}

                                                                    {/* Hover Overlay with Delete Action - Hover on Extra Large Desktop, Always on Mobile/Tablet/Laptop */}
                                                                    <div className="absolute inset-0 bg-black/40 flex flex-col items-end p-1 opacity-100 xl:opacity-0 xl:group-hover/asset:opacity-100 transition-opacity duration-200 pointer-events-none">
                                                                        <div
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                setAssetToDelete(asset);
                                                                            }}
                                                                            className="w-5 h-5 bg-white/90 hover:bg-white text-red-500 rounded flex items-center justify-center shadow-sm cursor-pointer hover:scale-110 transition-transform mb-1 pointer-events-auto"
                                                                            title="Hapus File"
                                                                        >
                                                                            <Icons.Trash2 size={10} />
                                                                        </div>
                                                                        {isImg && (
                                                                            <div
                                                                                onClick={(e) => {
                                                                                    e.stopPropagation();
                                                                                    // Save asset with target field key for auto-attach after edit
                                                                                    const fieldKey = activeMobileMenuId?.replace('menu-', '');
                                                                                    setImageEditorAsset({ ...asset, targetFieldKey: fieldKey });
                                                                                    setActiveMobileMenuId(null);
                                                                                    setMenuAnchor(null);
                                                                                }}
                                                                                className="w-5 h-5 bg-white/90 hover:bg-white text-sky-500 rounded flex items-center justify-center shadow-sm cursor-pointer hover:scale-110 transition-transform pointer-events-auto"
                                                                                title="Edit Gambar"
                                                                            >
                                                                                <Icons.Edit3 size={10} />
                                                                            </div>
                                                                        )}

                                                                    </div>
                                                                </button>
                                                            );
                                                        })}
                                                    </div>
                                                );
                                            })()
                                        ) : (
                                            <div className="h-full flex flex-col items-center justify-center text-slate-300 gap-2 min-h-[150px]">
                                                <Icons.Image size={24} strokeWidth={1.5} />
                                                <p className="text-[10px]">Belum ada file.</p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>,
                        document.body
                    );
                })()
            }

            {/* --- Backdrop for Global Menus --- */}
            {
                (activeMobileMenuId || mediaModal) && (
                    <div
                        className="fixed inset-0 z-[99990] bg-transparent"
                        onClick={() => {
                            if (activeMobileMenuId) {
                                setActiveMobileMenuId(null);
                                setMenuAnchor(null);
                            }
                        }}
                    />
                )
            }

            {/* === IMAGE EDITOR MODAL === */}
            <ImageEditorModal
                isOpen={!!imageEditorAsset}
                imageSrc={imageEditorAsset?.url || imageEditorAsset?.public_url}
                onClose={() => setImageEditorAsset(null)}
                onSave={async (blob) => {
                    if (!user?.id) return;
                    setIsSavingEditedImage(true);
                    try {
                        showToast("Menyimpan gambar...", "loading");
                        const fileExt = "png";
                        const fileName = `edited_${Date.now()}.${fileExt}`;
                        const filePath = `${user.id}/${fileName}`;

                        // 1. Upload to Supabase
                        const { data, error } = await supabase.storage
                            .from('asset-user') // Use 'asset-user' bucket
                            .upload(filePath, blob, {
                                contentType: 'image/png',
                                upsert: false
                            });

                        if (error) throw error;

                        const publicUrl = supabase.storage
                            .from('asset-user')
                            .getPublicUrl(filePath).data.publicUrl;

                        // 2. Pre-load the image to ensure no flickering/delay
                        // The user wants: "When loading finishes, the image is already there."
                        // So we wait for the browser to fetch it before closing the loading state.
                        await new Promise((resolve) => {
                            const img = new Image();
                            img.src = publicUrl;
                            img.onload = resolve;
                            img.onerror = resolve; // Proceed even if load fails (fallback)
                        });

                        // 3. Add to Recent Assets
                        const newAsset = {
                            name: fileName,
                            url: publicUrl,
                            public_url: publicUrl,
                            type: 'image/png',
                            source: 'upload',
                            size: blob.size,
                            date: Date.now()
                        };
                        setRecentAssets(prev => [newAsset, ...prev]);

                        // 4. Update Inputs (Replace/Attach)
                        const targetKey = imageEditorAsset?.targetFieldKey;
                        if (targetKey) {
                            const field = modelFields.find(f => f.key === targetKey);
                            if (field) {
                                if (field.type === 'file_multi') {
                                    // Handle Replace or Append
                                    setInputs(prev => {
                                        const current = prev[targetKey] || [];
                                        const currentArray = Array.isArray(current) ? current : (current ? [current] : []);
                                        const indexToUpdate = imageEditorAsset?.targetIndex;

                                        if (typeof indexToUpdate === 'number' && indexToUpdate >= 0 && indexToUpdate < currentArray.length) {
                                            // Replace existing
                                            const newArray = [...currentArray];
                                            newArray[indexToUpdate] = publicUrl;
                                            return { ...prev, [targetKey]: newArray };
                                        } else {
                                            // Append new (fallback)
                                            return { ...prev, [targetKey]: [...currentArray, publicUrl] };
                                        }
                                    });
                                } else {
                                    // Single input field (image, file, etc.)
                                    handleInputChange(targetKey, publicUrl);
                                }
                                showToast("Gambar berhasil diedit & dipasang!", "success");
                            } else {
                                showToast("Gambar berhasil diedit & disimpan pada terakhir upload!", "success");
                            }
                        } else {
                            showToast("Gambar berhasil diedit & disimpan pada terakhir upload!", "success");
                        }

                        setImageEditorAsset(null);

                    } catch (err) {
                        console.error(err);
                        showToast("Gagal menyimpan gambar.", "error");
                    } finally {
                        setIsSavingEditedImage(false);
                    }
                }}
                isSaving={isSavingEditedImage}
            />

            {/* === DELETE ASSET MODAL === */}
            <DeleteItemModal
                isOpen={!!assetToDelete}
                onClose={() => setAssetToDelete(null)}
                onConfirm={confirmDeleteAsset}
                title="Hapus File?"
                message="File ini akan dihapus permanen dari penyimpanan anda."
            />

            {/* --- Toast Notification (Compact) --- */}
            {
                toast.visible && (
                    <div className={`fixed top-4 left-1/2 -translate-x-1/2 z-[100020] px-4 py-2 rounded-full shadow-lg text-xs animate-in slide-in-from-top-2 fade-in duration-300 flex items-center gap-2 ${toast.type === 'error'
                        ? 'bg-rose-50 text-rose-600 border border-rose-200'
                        : toast.type === 'success'
                            ? 'bg-emerald-50 text-emerald-600 border border-emerald-200'
                            : 'bg-slate-800 text-white'
                        }`}>
                        {toast.type === 'error' && <Icons.AlertCircle size={14} />}
                        {toast.type === 'success' && <Icons.Check size={14} />}
                        {toast.type === 'loading' && <Icons.Loader2 size={14} className="animate-spin" />}
                        {toast.message}
                    </div>
                )
            }

        </>
    );
};

// NEW: Model Selector Modal (Pikaso Style)
const ModelSelectorModal = ({ models, selectedId, onSelect, onClose, capabilities, currentTotalCredits, inputs, isOpen }) => {
    const [search, setSearch] = useState("");
    const [providerFilter, setProviderFilter] = useState("All");
    const [hoveredModel, setHoveredModel] = useState(null);
    const [mousePos, setMousePos] = useState({ x: 0, y: 0 });

    // Reset loop if closed? No, keep it eager.

    // Extract unique providers for filter
    const uniqueProviders = ["All", ...new Set(models.map(m => m.ai_model_providers?.name).filter(Boolean))];

    const filteredModels = models.filter(m => {
        // Search
        const matchSearch = m.name.toLowerCase().includes(search.toLowerCase()) ||
            (m.ai_model_providers?.name || "").toLowerCase().includes(search.toLowerCase());
        // Filter
        const matchProvider = providerFilter === "All" || m.ai_model_providers?.name === providerFilter;

        return matchSearch && matchProvider;
    });

    return ReactDOM.createPortal(
        <div
            className={`fixed inset-0 z-[9999] flex items-center justify-center p-2 sm:p-4 bg-black/80 backdrop-blur-sm transition-all duration-200 ${isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
            onClick={onClose}
        >
            <div
                className="bg-white rounded-xl sm:rounded-2xl w-full max-w-7xl h-[90vh] sm:h-[85vh] flex flex-col shadow-2xl border border-slate-200 overflow-hidden font-sans"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="px-3 sm:px-6 py-2.5 sm:py-4 border-b border-slate-200 flex items-center justify-between bg-white shrink-0">
                    <h2 className="text-base sm:text-lg font-bold text-slate-800 tracking-tight">Models</h2>
                    <div className="flex items-center gap-1.5 sm:gap-3">
                        {/* Provider Filter */}
                        <div className="relative group">
                            <select
                                value={providerFilter}
                                onChange={e => setProviderFilter(e.target.value)}
                                className="bg-slate-50 text-xs sm:text-sm text-slate-700 border border-slate-200 rounded-lg pl-2 sm:pl-3 pr-6 sm:pr-8 py-1.5 sm:py-2 outline-none focus:border-blue-500 appearance-none cursor-pointer hover:bg-slate-100 transition-colors"
                            >
                                {uniqueProviders.map(p => <option key={p} value={p}>{p === 'All' ? 'All providers' : p}</option>)}
                            </select>
                            <Icons.ChevronDown size={12} className="absolute right-2 sm:right-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none group-hover:text-slate-600" />
                        </div>
                        {/* Search */}
                        <div className="relative">
                            <Icons.Search size={14} className="absolute left-2 sm:left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                            <input
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                                placeholder="Search models..."
                                className="bg-slate-50 text-xs sm:text-sm text-slate-700 border border-slate-200 rounded-lg pl-7 sm:pl-9 pr-2 sm:pr-3 py-1.5 sm:py-2 w-32 sm:w-64 outline-none focus:border-blue-500 placeholder:text-slate-400 focus:ring-1 focus:ring-blue-500 transition-all"
                            />
                        </div>
                        <button onClick={onClose} className="p-1.5 sm:p-2 hover:bg-slate-100 rounded-lg text-slate-400 hover:text-slate-600 transition-colors">
                            <Icons.X size={18} className="sm:hidden" />
                            <Icons.X size={20} className="hidden sm:block" />
                        </button>
                    </div>
                </div>

                {/* List - Vertical with horizontal scroll if content too wide */}
                <div className="flex-1 overflow-x-auto overflow-y-auto p-2 sm:p-4 space-y-1.5 sm:space-y-2 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:'none'] [scrollbar-width:'none']">
                    {filteredModels.map(model => {
                        const caps = capabilities[model.id] || {};
                        const isSelected = model.id === selectedId;

                        // Calculate actual total cost for ALL models
                        const calculateTotalCost = (basePrice) => {
                            let total = parseInt(basePrice) || 0;

                            // Add config option prices first
                            if (model.configuration_options) {
                                let configs = model.configuration_options;
                                if (!Array.isArray(configs)) {
                                    if (configs && Array.isArray(configs.categories)) configs = configs.categories;
                                    else configs = [];
                                }

                                configs.forEach(cat => {
                                    // Find selected or default option
                                    let selectedOption = null;

                                    if (cat.options && Array.isArray(cat.options)) {
                                        // Try user's input first (use category name as key)
                                        const inputValue = inputs?.[cat.name];
                                        if (inputValue) {
                                            selectedOption = cat.options.find(opt => opt.label === inputValue);
                                        }

                                        // If no input, use default option (is_default: true)
                                        if (!selectedOption) {
                                            selectedOption = cat.options.find(opt => opt.is_default === true);
                                        }

                                        // Fallback to first option
                                        if (!selectedOption && cat.options.length > 0) {
                                            selectedOption = cat.options[0];
                                        }

                                        // Add credit_cost (not price!)
                                        if (selectedOption && selectedOption.credit_cost) {
                                            total += parseInt(selectedOption.credit_cost) || 0;
                                        }
                                    }
                                });
                            }

                            // Apply quantity multiplier at the END (after adding option prices)
                            const qty = inputs?.['jumlah_gambar'] ? parseInt(inputs['jumlah_gambar']) : 1;
                            return total * qty;
                        };

                        const displayCost = calculateTotalCost(model.price_credits);

                        return (
                            <div
                                key={model.id}
                                onClick={() => onSelect(model.id)}
                                className={`group relative flex items-center gap-3 p-1 rounded-xl cursor-pointer transition-all border min-w-[950px] ${isSelected ? 'bg-sky-50 border-sky-300 ring-1 ring-sky-200 shadow-sm' : 'bg-white border-transparent hover:bg-slate-50 hover:border-slate-200'}`}
                            >
                                {/* Thumbnail / Video Preview - Smaller (w-14 h-9) */}
                                <div
                                    className={`w-14 h-9 rounded-md overflow-hidden relative shrink-0 shadow-sm cursor-zoom-in ${model.configuration_options?.cover_image ? 'bg-slate-100 border border-slate-200' : 'bg-white border border-slate-200'}`}
                                    onMouseEnter={(e) => {
                                        if (model.video_url || model.configuration_options?.cover_image) {
                                            setHoveredModel(model);
                                            // Fixed Position relative to Thumbnail (Right Side, Centered)
                                            const rect = e.currentTarget.getBoundingClientRect();
                                            setMousePos({ x: rect.right, y: rect.top + (rect.height / 2) });
                                        }
                                    }}
                                    onMouseLeave={() => setHoveredModel(null)}
                                >
                                    {/* Media Check: Image Priority over Video */}
                                    {model.configuration_options?.cover_image ? (
                                        <img
                                            src={model.configuration_options.cover_image.startsWith('http')
                                                ? model.configuration_options.cover_image
                                                : supabase.storage.from('assets').getPublicUrl(model.configuration_options.cover_image).data.publicUrl}
                                            className="w-full h-full object-cover"
                                            loading="lazy"
                                        />
                                    ) : model.video_url ? (
                                        <video
                                            src={model.video_url.startsWith('http') ? model.video_url : supabase.storage.from('assets').getPublicUrl(model.video_url).data.publicUrl}
                                            className="absolute inset-0 w-full h-full object-cover"
                                            muted loop autoPlay playsInline
                                        />
                                    ) : (
                                        <div className="w-full h-full flex items-center justify-center bg-slate-100">
                                            {model.ai_model_providers?.icon_url ? (
                                                <img src={model.ai_model_providers.icon_url.startsWith('http') ? model.ai_model_providers.icon_url : supabase.storage.from('assets').getPublicUrl(model.ai_model_providers.icon_url).data.publicUrl} className="w-8 h-8 rounded opacity-80" />
                                            ) : <Icons.Cpu className="text-slate-400" />}
                                        </div>
                                    )}
                                </div>

                                {/* Info - Single Row Layout - Start Aligned (Top) & Split (Left/Right) */}
                                <div className="flex-1 min-w-0 flex flex-row flex-nowrap items-start gap-3">
                                    {/* Left Group: Name & Status Badges */}
                                    <div className="flex items-center gap-2 min-w-[200px] flex-shrink-0">
                                        {model.ai_model_providers?.icon_url && (
                                            <img src={model.ai_model_providers.icon_url.startsWith('http') ? model.ai_model_providers.icon_url : supabase.storage.from('assets').getPublicUrl(model.ai_model_providers.icon_url).data.publicUrl} className="w-4 h-4 rounded-sm opacity-60 shrink-0" />
                                        )}
                                        <h3 className={`text-sm font-bold truncate ${isSelected ? 'text-sky-800' : 'text-slate-700'}`}>{model.name}</h3>

                                        {/* Status Badges */}
                                        {model.status === 'development' && <span className="text-[10px] bg-blue-500/10 text-blue-400 px-2 py-0.5 rounded-full font-bold border border-blue-500/20 tracking-wide leading-none shrink-0">DEV</span>}
                                        {model.is_new && <span className="text-[10px] bg-purple-500/10 text-purple-400 px-2 py-0.5 rounded-full font-bold border border-purple-500/20 tracking-wide leading-none shrink-0">NEW</span>}

                                        {/* Custom Label Badge */}
                                        {(() => {
                                            try {
                                                if (model.label && model.label.startsWith('{')) {
                                                    const lbl = JSON.parse(model.label);
                                                    if (lbl.text) {
                                                        const colors = {
                                                            blue: "bg-blue-100 text-blue-700 border-blue-200",
                                                            green: "bg-emerald-100 text-emerald-700 border-emerald-200",
                                                            purple: "bg-purple-100 text-purple-700 border-purple-200",
                                                            orange: "bg-orange-100 text-orange-700 border-orange-200",
                                                            rose: "bg-rose-100 text-rose-700 border-rose-200",
                                                            red: "bg-rose-100 text-rose-700 border-rose-200" // Alias untuk rose
                                                        };
                                                        const colorClass = colors[lbl.color] || colors.blue;
                                                        return (
                                                            <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold border tracking-wide flex items-center gap-1 leading-none shrink-0 ${colorClass}`}>
                                                                {lbl.icon && <DynamicIcon name={lbl.icon} size={10} />}
                                                                {lbl.text}
                                                            </span>
                                                        );
                                                    }
                                                }
                                                return null;
                                            } catch (e) { return null; }
                                        })()}
                                    </div>

                                    {/* Right Group: Credits, Caps, Configs - Push to far right with ml-auto */}
                                    <div className="flex items-center gap-2 text-xs text-slate-500 font-medium shrink-0 ml-auto flex-nowrap">
                                        {/* Credits (Dynamic Display) */}
                                        <div className="flex items-center gap-1.5 mr-2">
                                            <Icons.Zap size={12} className={isSelected ? "text-emerald-500 fill-current" : "text-emerald-500/70"} />
                                            {/* Show Total if selected, otherwise Base * Multiplier */}
                                            <span className={isSelected ? "text-emerald-600 font-bold" : "text-emerald-600/70"}>{displayCost} Kredit</span>
                                        </div>

                                        {/* Capability Badges */}
                                        {caps.hasStart || caps.hasEnd ? (
                                            <div className="flex items-center gap-1.5 bg-slate-50 text-slate-600 px-2.5 py-1 rounded-md border border-slate-200 shadow-sm whitespace-nowrap">
                                                <Icons.Image size={12} className="text-sky-500" />
                                                <span className="text-[10px] font-semibold">{caps.hasStart && caps.hasEnd ? "Start / End" : caps.hasStart ? "Start" : "End Img"}</span>
                                            </div>
                                        ) : null}

                                        {/* Config Options Badges */}
                                        {(() => {
                                            let configs = model.configuration_options;
                                            if (!Array.isArray(configs)) {
                                                if (configs && Array.isArray(configs.categories)) configs = configs.categories;
                                                else configs = [];
                                            }
                                            // Show ALL badges (removed slice limit)
                                            return configs.map((cat, idx) => {
                                                // Dynamic Icon Mapping - Strictly following "Smart Icon Trigger" list
                                                const name = cat.name.toLowerCase();
                                                let CatIcon = Icons.SlidersHorizontal; // Default

                                                // Layout/Rasio: ratio, dimensi, aspect, ukuran
                                                if (name.includes('ratio') || name.includes('dimen') || name.includes('aspect') || name.includes('ukura') || name.includes('size') || name.includes('width')) CatIcon = Icons.Maximize;
                                                // Waktu: durasi, waktu, time
                                                else if (name.includes('durasi') || name.includes('waktu') || name.includes('time')) CatIcon = Icons.Timer;
                                                // Kualitas: quality, kualitas, resolusi
                                                else if (name.includes('quality') || name.includes('kualitas') || name.includes('resolu') || name.includes('high')) CatIcon = Icons.Sparkles;
                                                // Gaya: style, gaya
                                                else if (name.includes('style') || name.includes('gaya')) CatIcon = Icons.Palette;
                                                // Suara: suara, voice, audio, tone
                                                else if (name.includes('suara') || name.includes('voice') || name.includes('audio') || name.includes('tone')) CatIcon = Icons.Mic;
                                                // Speed (Keep existing)
                                                else if (name.includes('speed') || name.includes('fps')) CatIcon = Icons.Activity;

                                                return (
                                                    <div
                                                        key={`cat-${idx}`}
                                                        title={`${cat.name}: ${cat.options?.map(o => o.label).join(', ')}`}
                                                        className="flex items-center gap-1.5 bg-slate-50 text-slate-600 px-2.5 py-1 rounded-md border border-slate-200 shadow-sm cursor-help hover:bg-slate-100 transition-colors whitespace-nowrap"
                                                    >
                                                        <CatIcon size={11} className="text-slate-400" />
                                                        <span className="text-[10px] font-semibold">{cat.name}</span>
                                                    </div>
                                                );
                                            });
                                        })()}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Hover Media Preview Overlay */}
                {hoveredModel && (hoveredModel.video_url || hoveredModel.configuration_options?.cover_image) && (
                    <div className="fixed z-[10000] pointer-events-none transition-all duration-200 ease-out" style={{
                        left: mousePos.x,
                        top: mousePos.y,
                        transform: 'translate(12px, -50%)', // Fixed offset from thumbnail right edge
                        width: hoveredModel.configuration_options?.cover_image ? '180px' : '240px',
                    }}>
                        {hoveredModel.configuration_options?.cover_image ? (
                            <div className="relative rounded-lg overflow-hidden shadow-2xl bg-white border border-slate-200 animate-in zoom-in-95 duration-150">
                                <img
                                    src={hoveredModel.configuration_options.cover_image.startsWith('http')
                                        ? hoveredModel.configuration_options.cover_image
                                        : supabase.storage.from('assets').getPublicUrl(hoveredModel.configuration_options.cover_image).data.publicUrl}
                                    className="w-full h-auto object-contain"
                                />
                            </div>
                        ) : (
                            <div className="relative rounded-lg overflow-hidden shadow-2xl aspect-video animate-in zoom-in-95 duration-150 bg-black border border-slate-700">
                                <video
                                    src={hoveredModel.video_url.startsWith('http') ? hoveredModel.video_url : supabase.storage.from('assets').getPublicUrl(hoveredModel.video_url).data.publicUrl}
                                    className="w-full h-full object-cover"
                                    autoPlay muted loop playsInline
                                />
                            </div>
                        )}
                    </div>
                )}



            </div>



        </div >,
        document.body
    );
};

export default GenericAiPage;

// --- Delete Confirmation Modal (Portal) ---
const DeleteConfirmationModal = ({ isOpen, onClose, onConfirm }) => {
    if (!isOpen) return null;

    return ReactDOM.createPortal(
        <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/60 backdrop-blur-sm px-4 animate-in fade-in duration-200" onClick={(e) => e.stopPropagation()}>
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden animate-in zoom-in-95 duration-200 border border-slate-100">
                <div className="p-8">
                    <h3 className="text-xl font-bold text-slate-800 mb-3 text-center">Hapus Semua Chat?</h3>
                    <p className="text-sm text-slate-600 mb-8 text-center leading-relaxed">
                        Anda akan menghapus <strong>seluruh riwayat percakapan</strong> di fitur ini.
                        <br />
                    </p>
                    <div className="flex gap-3">
                        <button
                            onClick={onClose}
                            className="flex-1 px-4 py-2.5 text-sm font-semibold text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-xl transition-all"
                        >
                            Batal
                        </button>
                        <button
                            onClick={onConfirm}
                            className="flex-1 px-4 py-2.5 text-sm font-bold text-white bg-red-600 hover:bg-red-700 rounded-xl shadow-md hover:shadow-lg transition-all"
                        >
                            Hapus
                        </button>
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
};

// --- Delete Item Modal (Granular) ---
const DeleteItemModal = ({ isOpen, onClose, onConfirm, title = "Hapus Item?", message = "Apakah anda yakin ingin menghapus hasil ini? Data riwayat/transaksi akan tetap ada, tetapi gambar/file akan dihapus." }) => {
    if (!isOpen) return null;

    return ReactDOM.createPortal(
        <div
            className="fixed inset-0 z-[100000] flex items-center justify-center bg-black/60 backdrop-blur-sm px-4 animate-in fade-in duration-200"
            onClick={onClose}
            style={{ pointerEvents: 'auto' }}
        >
            <div
                className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden animate-in zoom-in-95 duration-200 border border-slate-100 relative z-[100001]"
                onClick={(e) => e.stopPropagation()}
                style={{ pointerEvents: 'auto' }}
            >
                <div className="p-8">
                    <h3 className="text-xl font-bold text-slate-800 mb-3 text-center">{title}</h3>
                    <p className="text-sm text-slate-600 mb-8 text-center leading-relaxed">
                        {message}
                    </p>
                    <div className="flex gap-3">
                        <button
                            type="button"
                            onClick={(e) => {
                                e.stopPropagation();
                                onClose();
                            }}
                            className="flex-1 px-4 py-2.5 text-sm font-semibold text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-xl transition-all"
                        >
                            Batal
                        </button>
                        <button
                            type="button"
                            onClick={(e) => {
                                e.stopPropagation();
                                onConfirm();
                            }}
                            className="flex-1 px-4 py-2.5 text-sm font-bold text-white bg-red-600 hover:bg-red-700 rounded-xl shadow-md hover:shadow-lg transition-all"
                        >
                            Hapus
                        </button>
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
};

// --- Helper: Global Popover with Dynamic Positioning ---
const GlobalPopover = ({ triggerId, onClose, children }) => {
    const [style, setStyle] = useState({ top: 0, left: 0, opacity: 0 });

    // Check loop
    useEffect(() => {
        let rafId;
        const update = () => {
            const trigger = document.getElementById(triggerId);
            if (!trigger) {
                return;
            }

            const rect = trigger.getBoundingClientRect();

            // Positioning Logic
            const PADDING = 10;
            const MENU_WIDTH = 350;
            const MENU_HEIGHT = 220; // Estimated max height

            let top = rect.bottom + PADDING;
            let left = rect.left;

            // X Adjust
            if (left + MENU_WIDTH > window.innerWidth) {
                left = Math.max(PADDING, window.innerWidth - MENU_WIDTH - PADDING);
            }
            // Y Adjust
            if (top + MENU_HEIGHT > window.innerHeight) {
                top = Math.max(PADDING, rect.top - MENU_HEIGHT - PADDING);
            }

            setStyle({
                top,
                left,
                width: MENU_WIDTH,
                position: 'fixed',
                zIndex: 99999,
                opacity: 1
            });

            rafId = requestAnimationFrame(update);
        };

        rafId = requestAnimationFrame(update);
        return () => cancelAnimationFrame(rafId);
    }, [triggerId]);

    // Create Portal
    return ReactDOM.createPortal(
        <div style={style} className="fixed transition-opacity duration-100" onClick={(e) => e.stopPropagation()}>
            {children}
        </div>,
        document.body
    );
};
